/**
 * PayrollManagementScreen.js
 * JOBENG – Complete Payroll Management System
 * 
 * Features:
 * - Employee management (CRUD)
 * - Pay run processing
 * - Payslip generation & distribution
 * - Tax calculations & HMRC integration
 * - RTI submissions
 * - Pension auto-enrolment
 * - Statutory payments (SSP, SMP, etc.)
 * - Reports & analytics
 * - Audit trail
 * - Compliance checking
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Modal, TextInput,
  Alert, Dimensions, FlatList, RefreshControl, ActivityIndicator,
  BackHandler, Share, Animated, Platform, ScrollView,
  Image, KeyboardAvoidingView, Switch, Pressable,
    Linking, StatusBar,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { registerCallback } from '../../../utils/navigationCallbacks';
import { useBusiness } from '../../../context/BusinessContext';
import { useCompanyLogo } from '../../../hooks/useCompanyLogo';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system/legacy';
import * as MailComposer from 'expo-mail-composer';
import * as DocumentPicker from 'expo-document-picker';
import * as Calendar from 'expo-calendar';
import * as LocalAuthentication from 'expo-local-authentication';
import * as Notifications from 'expo-notifications';
import { empApi, ManageAPI, PayrollIntegrationAPI, apiCall } from '../../../services/employeeApi';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Picker } from '@react-native-picker/picker';
import * as XLSX from 'xlsx';
import * as Crypto from 'expo-crypto';
import * as Haptics from 'expo-haptics';

const { width, height } = Dimensions.get('window');

// Device optimization
const IS_PROMAX = width >= 430;
const IS_TABLET = width >= 768;
const W = width;
const H = height;
const HPAD = IS_PROMAX ? 18 : (IS_TABLET ? 24 : 14);
const CARD_R = IS_PROMAX ? 16 : (IS_TABLET ? 20 : 12);
const MODAL_R = IS_PROMAX ? 24 : (IS_TABLET ? 28 : 20);
const TOUCH_H = IS_PROMAX ? 48 : (IS_TABLET ? 54 : 44);
const ICON_BTN = IS_PROMAX ? 40 : (IS_TABLET ? 44 : 34);
const AVATAR_SM = IS_PROMAX ? 48 : (IS_TABLET ? 52 : 42);
const FAB_SIZE = IS_PROMAX ? 62 : (IS_TABLET ? 68 : 56);
const FAB_BOTTOM = IS_PROMAX ? 38 : (IS_TABLET ? 44 : 28);
const FS_XS = IS_PROMAX ? 11 : (IS_TABLET ? 12 : 10);
const FS_SM = IS_PROMAX ? 13 : (IS_TABLET ? 14 : 11);
const FS_BASE = IS_PROMAX ? 15 : (IS_TABLET ? 16 : 13);
const FS_MD = IS_PROMAX ? 16 : (IS_TABLET ? 17 : 14);
const FS_LG = IS_PROMAX ? 18 : (IS_TABLET ? 19 : 16);
const FS_XL = IS_PROMAX ? 20 : (IS_TABLET ? 22 : 17);

// Colors
const C = {
  primary: '#0e4e96',
  primaryLight: '#1a6dcc',
  primaryDark: '#0a3a6b',
  primaryGradient: ['#0e4e96', '#1a6dcc', '#2b8ce0'],
  
  accent: '#19abb5',
  accentLight: '#4bc5cf',
  accentDark: '#11838a',
  
  gold: '#fdb913',
  goldLight: '#fed44a',
  goldDark: '#d99b0a',
  
  success: '#10b981',
  successLight: '#4ade80',
  successDark: '#059669',
  
  warning: '#f59e0b',
  warningLight: '#fbbf24',
  warningDark: '#d97706',
  
  danger: '#dc2626',
  dangerLight: '#ef4444',
  dangerDark: '#b91c1c',
  
  purple: '#8b5cf6',
  purpleLight: '#a78bfa',
  purpleDark: '#7c3aed',
  
  info: '#3b82f6',
  infoLight: '#60a5fa',
  infoDark: '#2563eb',
  
  bg: '#f1f5f9',
  card: '#ffffff',
  border: '#e2e8f0',
  dark: '#0f172a',
  mid: '#475569',
  light: '#94a3b8',
};

// Payroll-specific API client (reuses shared empApi from employeeApi.js)
// empApi base: https://api.jobeng.ai/api/employees
// For payroll-internal routes we alias to the shared client
const api = empApi;

// Payroll-internal route constants
// Cross-screen operations use ManageAPI / PayrollIntegrationAPI from employeeApi.js
// api (=empApi) is used directly for payroll-owned routes below
const EP = {
  // Tax & statutory rates
  taxBandCurrent:  '/api/payroll/tax-bands/current',
  niBands:         '/api/payroll/ni-bands',
  statutoryRates:  '/api/payroll/statutory-rates',
  // Payroll calendar & audit
  payrollCalendar: '/api/payroll/calendar/events',
  payrollAudit:    '/api/payroll/audit',
  payrollCompliance:'/api/payroll/compliance/check',
};

// Helper Functions
const formatCurrency = (amount, currency = 'GBP') => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const formatDate = (date, format = 'short') => {
  if (!date) return '—';
  const d = new Date(date);
  switch (format) {
    case 'short': return d.toLocaleDateString('en-GB');
    case 'long': return d.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    case 'iso': return d.toISOString().split('T')[0];
    default: return d.toLocaleDateString('en-GB');
  }
};

const formatNumber = (num, decimals = 2) => {
  return num?.toFixed(decimals) || '0';
};

const calculateTax = (gross, taxCode = '1257L', taxBands = []) => {
  // UK tax calculation logic
  let taxable = gross;
  let tax = 0;
  
  // Apply tax bands
  for (const band of taxBands) {
    if (taxable <= 0) break;
    
    const bandAmount = Math.min(taxable, band.limit - band.start);
    tax += bandAmount * band.rate;
    taxable -= bandAmount;
  }
  
  return tax;
};

const calculateNI = (gross, niCategory = 'A', niBands = []) => {
  // National Insurance calculation
  let ni = 0;
  // Simplified calculation
  if (gross > 242) { // Primary threshold
    ni = (gross - 242) * 0.12;
    if (gross > 967) { // Upper earnings limit
      ni = (967 - 242) * 0.12 + (gross - 967) * 0.02;
    }
  }
  return Math.max(0, ni);
};

const calculatePension = (gross, employeeContribution = 0.05, employerContribution = 0.03) => {
  // Auto-enrolment pension calculation
  const qualifyingEarnings = Math.min(gross, 50000); // Upper limit
  const employeePension = qualifyingEarnings * employeeContribution;
  const employerPension = qualifyingEarnings * employerContribution;
  return { employeePension, employerPension };
};

const calculateStudentLoan = (gross, plan = 'plan2') => {
  // Student loan repayments
  const thresholds = {
    plan1: 22015,
    plan2: 27295,
    plan4: 27660,
    postgraduate: 21000,
  };
  
  const threshold = thresholds[plan] || thresholds.plan2;
  if (gross <= threshold) return 0;
  
  const excess = gross - threshold;
  return excess * 0.09; // 9% of excess
};

const makeId = (prefix = 'emp') => `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

const nowISO = () => new Date().toISOString();
const todayStr = () => new Date().toISOString().split('T')[0];

// Tax Bands Data
const TAX_BANDS_2024 = [
  { start: 0, limit: 12570, rate: 0, name: 'Personal Allowance' },
  { start: 12570, limit: 50270, rate: 0.2, name: 'Basic Rate' },
  { start: 50270, limit: 125140, rate: 0.4, name: 'Higher Rate' },
  { start: 125140, limit: Infinity, rate: 0.45, name: 'Additional Rate' },
];

const NI_BANDS_2024 = [
  { start: 0, limit: 242, rate: 0, name: 'Lower Earnings Limit' },
  { start: 242, limit: 967, rate: 0.12, name: 'Primary Threshold' },
  { start: 967, limit: Infinity, rate: 0.02, name: 'Upper Earnings Limit' },
];

const STATUTORY_RATES_2024 = {
  ssp: 116.75, // Statutory Sick Pay per week
  smp: 184.03, // Statutory Maternity Pay per week (first 6 weeks at 90% of earnings)
  spp: 184.03, // Statutory Paternity Pay per week
  shpp: 184.03, // Shared Parental Pay per week
  sap: 184.03, // Statutory Adoption Pay per week
};

// Sample Employees Data
const SAMPLE_EMPLOYEES = [
  {
    id: 'emp_001',
    employeeNumber: 'EMP001',
    title: 'Mr',
    firstName: 'James',
    lastName: 'Walker',
    preferredName: 'James',
    gender: 'Male',
    dateOfBirth: '1985-03-15',
    nationality: 'British',
    niNumber: 'AB123456C',
    taxCode: '1257L',
    taxRegion: 'UK',
    niCategory: 'A',
    studentLoan: 'plan2',
    postgraduateLoan: false,
    
    contact: {
      email: 'james.walker@jobeng.ai',
      phone: '+44 20 1234 5678',
      mobile: '+44 7700 123456',
      address: {
        line1: '123 High Street',
        line2: 'Flat 4',
        city: 'London',
        county: 'Greater London',
        postcode: 'SW1A 1AA',
        country: 'United Kingdom',
      },
    },
    
    employment: {
      startDate: '2020-01-15',
      endDate: null,
      probationEndDate: '2020-07-15',
      noticePeriod: 30, // days
      contractType: 'permanent', // permanent, fixed-term, temporary, zero-hours
      employmentType: 'full-time', // full-time, part-time
      workingHours: 37.5, // per week
      department: 'Engineering',
      jobTitle: 'Senior Engineer',
      jobGrade: 'E3',
      costCenter: 'CC-ENG-001',
      project: 'Alpha Initiative',
      location: 'London Office',
      holidayEntitlement: 28, // days per year
      holidayRemaining: 15,
      sickLeaveEntitlement: 10, // days per year
      sickLeaveTaken: 2,
    },
    
    payroll: {
      payFrequency: 'monthly', // weekly, bi-weekly, monthly
      payMethod: 'BACS', // BACS, cheque, cash
      paymentDate: 28, // day of month
      bankDetails: {
        accountName: 'James Walker',
        sortCode: '12-34-56',
        accountNumber: '12345678',
        bankName: 'Barclays',
      },
      salary: 55000, // annual
      hourlyRate: null,
      pension: {
        enrolled: true,
        scheme: 'NEST',
        employeeContribution: 0.05,
        employerContribution: 0.03,
        optOutDate: null,
        joiningDate: '2020-02-01',
      },
      deductions: [
        {
          id: 'ded_001',
          type: 'union',
          name: 'Union Fees',
          amount: 15,
          frequency: 'monthly',
          mandatory: false,
        },
        {
          id: 'ded_002',
          type: 'charity',
          name: 'Give as You Earn',
          amount: 20,
          frequency: 'monthly',
          mandatory: false,
        },
      ],
      benefits: [
        {
          id: 'ben_001',
          type: 'health',
          name: 'Private Health Insurance',
          value: 75,
          taxable: true,
        },
        {
          id: 'ben_002',
          type: 'life',
          name: 'Life Insurance (4x salary)',
          value: 0,
          taxable: false,
        },
      ],
      taxHistory: [],
      payHistory: [],
    },
    
    emergencyContacts: [
      {
        name: 'Sarah Walker',
        relationship: 'Spouse',
        phone: '+44 7700 234567',
        email: 'sarah.walker@email.com',
      },
    ],
    
    documents: [],
    
    status: 'active', // active, suspended, terminated, on-leave
    createdDate: '2020-01-15',
    modifiedDate: '2024-01-15',
    createdBy: 'HR System',
    modifiedBy: 'HR System',
  },
  {
    id: 'emp_002',
    employeeNumber: 'EMP002',
    title: 'Ms',
    firstName: 'Sarah',
    lastName: 'Johnson',
    preferredName: 'Sarah',
    gender: 'Female',
    dateOfBirth: '1990-07-22',
    nationality: 'British',
    niNumber: 'CD789012E',
    taxCode: '1257L',
    taxRegion: 'UK',
    niCategory: 'A',
    studentLoan: 'plan1',
    postgraduateLoan: true,
    
    contact: {
      email: 'sarah.johnson@jobeng.ai',
      phone: '+44 20 2345 6789',
      mobile: '+44 7700 345678',
      address: {
        line1: '456 Park Avenue',
        city: 'London',
        county: 'Greater London',
        postcode: 'SW1A 2BB',
        country: 'United Kingdom',
      },
    },
    
    employment: {
      startDate: '2021-03-01',
      endDate: null,
      probationEndDate: '2021-06-01',
      noticePeriod: 30,
      contractType: 'permanent',
      employmentType: 'full-time',
      workingHours: 37.5,
      department: 'Finance',
      jobTitle: 'Finance Manager',
      jobGrade: 'F2',
      costCenter: 'CC-FIN-001',
      project: null,
      location: 'London Office',
      holidayEntitlement: 28,
      holidayRemaining: 12,
      sickLeaveEntitlement: 10,
      sickLeaveTaken: 0,
    },
    
    payroll: {
      payFrequency: 'monthly',
      payMethod: 'BACS',
      paymentDate: 28,
      bankDetails: {
        accountName: 'Sarah Johnson',
        sortCode: '23-45-67',
        accountNumber: '23456789',
        bankName: 'HSBC',
      },
      salary: 65000,
      hourlyRate: null,
      pension: {
        enrolled: true,
        scheme: 'NEST',
        employeeContribution: 0.06,
        employerContribution: 0.04,
        optOutDate: null,
        joiningDate: '2021-04-01',
      },
      deductions: [],
      benefits: [],
      taxHistory: [],
      payHistory: [],
    },
    
    emergencyContacts: [],
    documents: [],
    status: 'active',
    createdDate: '2021-03-01',
    modifiedDate: '2024-02-01',
    createdBy: 'HR System',
    modifiedBy: 'HR System',
  },
  {
    id: 'emp_003',
    employeeNumber: 'EMP003',
    title: 'Dr',
    firstName: 'Michael',
    lastName: 'Chen',
    preferredName: 'Mike',
    gender: 'Male',
    dateOfBirth: '1978-11-03',
    nationality: 'British',
    niNumber: 'EF345678G',
    taxCode: 'K497',
    taxRegion: 'UK',
    niCategory: 'A',
    studentLoan: null,
    postgraduateLoan: false,
    
    contact: {
      email: 'michael.chen@jobeng.ai',
      phone: '+44 20 3456 7890',
      mobile: '+44 7700 456789',
      address: {
        line1: '789 Oak Road',
        city: 'London',
        county: 'Greater London',
        postcode: 'SW1A 3CC',
        country: 'United Kingdom',
      },
    },
    
    employment: {
      startDate: '2019-06-01',
      endDate: null,
      probationEndDate: '2019-09-01',
      noticePeriod: 60,
      contractType: 'permanent',
      employmentType: 'full-time',
      workingHours: 40,
      department: 'Executive',
      jobTitle: 'Technical Director',
      jobGrade: 'D1',
      costCenter: 'CC-EXEC-001',
      project: 'Innovation Lab',
      location: 'London Office',
      holidayEntitlement: 30,
      holidayRemaining: 22,
      sickLeaveEntitlement: 15,
      sickLeaveTaken: 0,
    },
    
    payroll: {
      payFrequency: 'monthly',
      payMethod: 'BACS',
      paymentDate: 28,
      bankDetails: {
        accountName: 'Michael Chen',
        sortCode: '34-56-78',
        accountNumber: '34567890',
        bankName: 'Lloyds',
      },
      salary: 95000,
      hourlyRate: null,
      pension: {
        enrolled: true,
        scheme: 'Aviva',
        employeeContribution: 0.08,
        employerContribution: 0.06,
        optOutDate: null,
        joiningDate: '2019-07-01',
      },
      deductions: [],
      benefits: [
        {
          id: 'ben_003',
          type: 'car',
          name: 'Company Car',
          value: 500,
          taxable: true,
        },
        {
          id: 'ben_004',
          type: 'health',
          name: 'Family Health Insurance',
          value: 150,
          taxable: true,
        },
      ],
      taxHistory: [],
      payHistory: [],
    },
    
    emergencyContacts: [
      {
        name: 'Lisa Chen',
        relationship: 'Spouse',
        phone: '+44 7700 567890',
        email: 'lisa.chen@email.com',
      },
    ],
    
    documents: [],
    status: 'active',
    createdDate: '2019-06-01',
    modifiedDate: '2024-01-20',
    createdBy: 'HR System',
    modifiedBy: 'HR System',
  },
  {
    id: 'emp_004',
    employeeNumber: 'EMP004',
    title: 'Mrs',
    firstName: 'Emma',
    lastName: 'Davies',
    preferredName: 'Emma',
    gender: 'Female',
    dateOfBirth: '1992-09-18',
    nationality: 'British',
    niNumber: 'GH456789I',
    taxCode: 'BR',
    taxRegion: 'UK',
    niCategory: 'A',
    studentLoan: 'plan2',
    postgraduateLoan: false,
    
    contact: {
      email: 'emma.davies@jobeng.ai',
      phone: '+44 20 4567 8901',
      mobile: '+44 7700 567890',
      address: {
        line1: '321 Elm Street',
        city: 'London',
        county: 'Greater London',
        postcode: 'SW1A 4DD',
        country: 'United Kingdom',
      },
    },
    
    employment: {
      startDate: '2022-01-10',
      endDate: null,
      probationEndDate: '2022-04-10',
      noticePeriod: 30,
      contractType: 'permanent',
      employmentType: 'part-time',
      workingHours: 22.5,
      department: 'HR',
      jobTitle: 'HR Advisor',
      jobGrade: 'H2',
      costCenter: 'CC-HR-001',
      project: null,
      location: 'London Office',
      holidayEntitlement: 28,
      holidayRemaining: 18,
      sickLeaveEntitlement: 10,
      sickLeaveTaken: 3,
    },
    
    payroll: {
      payFrequency: 'monthly',
      payMethod: 'BACS',
      paymentDate: 28,
      bankDetails: {
        accountName: 'Emma Davies',
        sortCode: '45-67-89',
        accountNumber: '45678901',
        bankName: 'NatWest',
      },
      salary: 35000, // pro-rata
      hourlyRate: 25,
      pension: {
        enrolled: true,
        scheme: 'NEST',
        employeeContribution: 0.05,
        employerContribution: 0.03,
        optOutDate: null,
        joiningDate: '2022-02-01',
      },
      deductions: [
        {
          id: 'ded_003',
          type: 'childcare',
          name: 'Childcare Vouchers',
          amount: 50,
          frequency: 'monthly',
          mandatory: false,
        },
      ],
      benefits: [],
      taxHistory: [],
      payHistory: [],
    },
    
    emergencyContacts: [],
    documents: [],
    status: 'active',
    createdDate: '2022-01-10',
    modifiedDate: '2024-02-10',
    createdBy: 'HR System',
    modifiedBy: 'HR System',
  },
  {
    id: 'emp_005',
    employeeNumber: 'EMP005',
    title: 'Mr',
    firstName: 'David',
    lastName: 'Williams',
    preferredName: 'Dave',
    gender: 'Male',
    dateOfBirth: '1988-05-30',
    nationality: 'Welsh',
    niNumber: 'IJ567890K',
    taxCode: '1257L',
    taxRegion: 'UK',
    niCategory: 'A',
    studentLoan: null,
    postgraduateLoan: false,
    
    contact: {
      email: 'david.williams@jobeng.ai',
      phone: '+44 20 5678 9012',
      mobile: '+44 7700 678901',
      address: {
        line1: '654 Pine Lane',
        city: 'London',
        county: 'Greater London',
        postcode: 'SW1A 5EE',
        country: 'United Kingdom',
      },
    },
    
    employment: {
      startDate: '2023-05-01',
      endDate: null,
      probationEndDate: '2023-08-01',
      noticePeriod: 30,
      contractType: 'fixed-term',
      employmentType: 'full-time',
      workingHours: 37.5,
      department: 'Projects',
      jobTitle: 'Project Manager',
      jobGrade: 'P3',
      costCenter: 'CC-PROJ-001',
      project: 'Alpha Initiative',
      location: 'London Office',
      holidayEntitlement: 28,
      holidayRemaining: 25,
      sickLeaveEntitlement: 10,
      sickLeaveTaken: 0,
    },
    
    payroll: {
      payFrequency: 'monthly',
      payMethod: 'BACS',
      paymentDate: 28,
      bankDetails: {
        accountName: 'David Williams',
        sortCode: '56-78-90',
        accountNumber: '56789012',
        bankName: 'Santander',
      },
      salary: 52000,
      hourlyRate: null,
      pension: {
        enrolled: true,
        scheme: 'NEST',
        employeeContribution: 0.05,
        employerContribution: 0.03,
        optOutDate: null,
        joiningDate: '2023-06-01',
      },
      deductions: [],
      benefits: [],
      taxHistory: [],
      payHistory: [],
    },
    
    emergencyContacts: [
      {
        name: 'Rachel Williams',
        relationship: 'Partner',
        phone: '+44 7700 789012',
        email: 'rachel.williams@email.com',
      },
    ],
    
    documents: [],
    status: 'active',
    createdDate: '2023-05-01',
    modifiedDate: '2024-02-15',
    createdBy: 'HR System',
    modifiedBy: 'HR System',
  },
];

// Sample Pay Runs
const SAMPLE_PAY_RUNS = [
  {
    id: 'pr_001',
    reference: 'PR-2024-12',
    period: 'December 2024',
    startDate: '2024-12-01',
    endDate: '2024-12-31',
    paymentDate: '2024-12-28',
    frequency: 'monthly',
    status: 'completed', // draft, processing, completed, approved, locked
    employeeCount: 45,
    totalGross: 287500,
    totalNet: 215625,
    totalTax: 57500,
    totalNI: 14375,
    totalPension: 8625,
    totalDeductions: 2875,
    createdDate: '2024-12-01',
    processedDate: '2024-12-20',
    approvedDate: '2024-12-21',
    approvedBy: 'Finance Director',
    lockedDate: '2024-12-22',
    lockedBy: 'System',
  },
  {
    id: 'pr_002',
    reference: 'PR-2025-01',
    period: 'January 2025',
    startDate: '2025-01-01',
    endDate: '2025-01-31',
    paymentDate: '2025-01-28',
    frequency: 'monthly',
    status: 'processing',
    employeeCount: 46,
    totalGross: 295000,
    totalNet: 221250,
    totalTax: 59000,
    totalNI: 14750,
    totalPension: 8850,
    totalDeductions: 2950,
    createdDate: '2025-01-01',
    processedDate: null,
    approvedDate: null,
    approvedBy: null,
    lockedDate: null,
    lockedBy: null,
  },
  {
    id: 'pr_003',
    reference: 'PR-2025-02',
    period: 'February 2025',
    startDate: '2025-02-01',
    endDate: '2025-02-28',
    paymentDate: '2025-02-28',
    frequency: 'monthly',
    status: 'draft',
    employeeCount: 46,
    totalGross: 0,
    totalNet: 0,
    totalTax: 0,
    totalNI: 0,
    totalPension: 0,
    totalDeductions: 0,
    createdDate: '2025-02-01',
    processedDate: null,
    approvedDate: null,
    approvedBy: null,
    lockedDate: null,
    lockedBy: null,
  },
];

// Sample Payslips
const SAMPLE_PAYSLIPS = [
  {
    id: 'ps_001',
    payRunId: 'pr_001',
    employeeId: 'emp_001',
    employeeName: 'James Walker',
    employeeNumber: 'EMP001',
    period: 'December 2024',
    issueDate: '2024-12-20',
    paymentDate: '2024-12-28',
    
    earnings: {
      basic: 4583.33,
      overtime: 0,
      bonus: 0,
      commission: 0,
      holiday: 0,
      sick: 0,
      maternity: 0,
      paternity: 0,
      total: 4583.33,
    },
    
    deductions: {
      tax: 916.67,
      ni: 458.33,
      pension: 229.17,
      studentLoan: 0,
      union: 15,
      charity: 20,
      childcare: 0,
      total: 1639.17,
    },
    
    netPay: 2944.16,
    
    yearToDate: {
      gross: 55000,
      tax: 11000,
      ni: 5500,
      pension: 2750,
      net: 35750,
    },
    
    paymentMethod: 'BACS',
    bankAccount: '****5678',
    sortCode: '**-**-56',
    
    status: 'paid',
    pdfUrl: null,
    emailed: true,
    viewed: true,
    downloaded: false,
    
    createdDate: '2024-12-20',
  },
];

// Employee Card Component
const EmployeeCard = React.memo(({ employee, onPress, onLongPress, onPayslip }) => {
  const statusColors = {
    active: C.success,
    suspended: C.warning,
    terminated: C.danger,
    'on-leave': C.info,
  };
  
  return (
    <TouchableOpacity
      style={styles.employeeCard}
      onPress={() => onPress(employee)}
      onLongPress={() => onLongPress(employee)}
      activeOpacity={0.7}
    >
      <View style={styles.employeeAvatar}>
        <Text style={styles.employeeAvatarText}>
          {(employee.firstName||'?')[0]}{(employee.lastName||'?')[0]}
        </Text>
      </View>
      
      <View style={styles.employeeInfo}>
        <View style={styles.employeeHeader}>
          <Text style={styles.employeeName}>
            {employee.firstName} {employee.lastName}
          </Text>
          <View style={[styles.employeeStatus, { backgroundColor: statusColors[employee.status] + '20' }]}>
            <View style={[styles.statusDot, { backgroundColor: statusColors[employee.status] }]} />
            <Text style={[styles.statusText, { color: statusColors[employee.status] }]}>
              {(employee.status||'active').toUpperCase()}
            </Text>
          </View>
        </View>
        
        <Text style={styles.employeeNumber}>{employee.employeeNumber}</Text>
        
        <View style={styles.employeeDetails}>
          <View style={styles.employeeDetail}>
            <MaterialCommunityIcons name="briefcase-outline" size={12} color={C.light} />
            <Text style={styles.employeeDetailText}>{employee.employment?.jobTitle || "—"}</Text>
          </View>
          
          <View style={styles.employeeDetail}>
            <MaterialCommunityIcons name="office-building-outline" size={12} color={C.light} />
            <Text style={styles.employeeDetailText}>{employee.employment?.department || "—"}</Text>
          </View>
          
          <View style={styles.employeeDetail}>
            <MaterialCommunityIcons name="currency-gbp" size={12} color={C.light} />
            <Text style={styles.employeeDetailText}>
              {formatCurrency(employee.payroll?.salary)}/yr
            </Text>
          </View>
        </View>
        
        <View style={styles.employeeFooter}>
          <View style={styles.employeeFooterItem}>
            <MaterialCommunityIcons name="calendar-start" size={10} color={C.light} />
            <Text style={styles.employeeFooterText}>
              Started {formatDate(employee.employment?.startDate)}
            </Text>
          </View>
          
          {employee.payroll?.pension?.enrolled && (
            <View style={styles.employeeFooterItem}>
              <MaterialCommunityIcons name="piggy-bank-outline" size={10} color={C.success} />
              <Text style={[styles.employeeFooterText, { color: C.success }]}>Pension</Text>
            </View>
          )}
        </View>
      </View>
      
      <View style={{ alignItems: 'center', gap: 4 }}>
        <TouchableOpacity
          onPress={(e) => { e.stopPropagation && e.stopPropagation(); onPayslip && onPayslip(employee); }}
          style={{ padding: 8, borderRadius: 8, backgroundColor: C.primary + '15' }}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <MaterialCommunityIcons name="file-pdf-box" size={20} color={C.primary} />
        </TouchableOpacity>
        <Ionicons name="chevron-forward" size={20} color={C.light} />
      </View>
    </TouchableOpacity>
  );
});

// PayRun Card Component
const PayRunCard = React.memo(({ payRun, onPress }) => {
  const statusColors = {
    draft: C.mid,
    processing: C.info,
    completed: C.success,
    approved: C.success,
    locked: C.dark,
  };
  
  return (
    <TouchableOpacity
      style={styles.payRunCard}
      onPress={() => onPress(payRun)}
      activeOpacity={0.7}
    >
      <View style={styles.payRunHeader}>
        <View>
          <Text style={styles.payRunPeriod}>{payRun.period}</Text>
          <Text style={styles.payRunRef}>{payRun.reference}</Text>
        </View>
        
        <View style={[styles.payRunStatus, { backgroundColor: statusColors[payRun.status] + '20' }]}>
          <Text style={[styles.payRunStatusText, { color: statusColors[payRun.status] }]}>
            {String(payRun.status ?? '').toUpperCase()}
          </Text>
        </View>
      </View>
      
      <View style={styles.payRunStats}>
        <View style={styles.payRunStat}>
          <Text style={styles.payRunStatLabel}>Employees</Text>
          <Text style={styles.payRunStatValue}>{payRun.employeeCount}</Text>
        </View>
        
        <View style={styles.payRunStat}>
          <Text style={styles.payRunStatLabel}>Gross Pay</Text>
          <Text style={styles.payRunStatValue}>{formatCurrency(payRun.totalGross)}</Text>
        </View>
        
        <View style={styles.payRunStat}>
          <Text style={styles.payRunStatLabel}>Net Pay</Text>
          <Text style={styles.payRunStatValue}>{formatCurrency(payRun.totalNet)}</Text>
        </View>
        
        <View style={styles.payRunStat}>
          <Text style={styles.payRunStatLabel}>Tax</Text>
          <Text style={styles.payRunStatValue}>{formatCurrency(payRun.totalTax)}</Text>
        </View>
      </View>
      
      <View style={styles.payRunFooter}>
        <View style={styles.payRunFooterItem}>
          <MaterialCommunityIcons name="calendar-range" size={12} color={C.light} />
          <Text style={styles.payRunFooterText}>
            {formatDate(payRun.startDate)} - {formatDate(payRun.endDate)}
          </Text>
        </View>
        
        <View style={styles.payRunFooterItem}>
          <MaterialCommunityIcons name="calendar-check" size={12} color={C.light} />
          <Text style={styles.payRunFooterText}>Paid {formatDate(payRun.paymentDate)}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
});

// Main Payroll Screen Component
export default function PayrollManagementScreen({ navigation, route }) {
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId } = route?.params || {};
  // ── Company Profile Integration ──────────────────────────────────────────
  const {
    companyProfile: bizCompanyProfile,
    subscription,
  } = useBusiness() || {};

  // Company logo — fetched/cached independently via useCompanyLogo hook
  const { logoUri, isLoading: logoLoading } = useCompanyLogo(bizCompanyProfile?.id) || {};

  const companyInfo = {
    name:               bizCompanyProfile?.name               || 'Your Company',
    email:              bizCompanyProfile?.email              || '',
    phone:              bizCompanyProfile?.phone              || '',
    website:            bizCompanyProfile?.website            || '',
    headquarters:       bizCompanyProfile?.headquarters       || '',
    industry:           bizCompanyProfile?.industry           || '',
    companySize:        bizCompanyProfile?.companySize        || '',
    vatNumber:          bizCompanyProfile?.vatNumber          || '',
    registrationNumber: bizCompanyProfile?.registrationNumber || '',
    companyCode:        bizCompanyProfile?.companyCode        || '',
    logo:               logoUri || bizCompanyProfile?.logo || null, // hook-resolved URI takes priority
    bankName:           bizCompanyProfile?.bankName           || '',
    bankAccount:        bizCompanyProfile?.bankAccount        || '',
    bankSortCode:       bizCompanyProfile?.bankSortCode       || '',
    bankIBAN:           bizCompanyProfile?.bankIBAN           || '',
    bankSWIFT:          bizCompanyProfile?.bankSWIFT          || '',
    defaultTaxRate:     bizCompanyProfile?.defaultTaxRate     || '20',
    paymentTerms:       bizCompanyProfile?.paymentTerms       || '30',
    invoicePrefix:      bizCompanyProfile?.invoicePrefix      || 'INV',
    invoiceFooter:      bizCompanyProfile?.invoiceFooter      || 'Thank you for your business.',
    invoiceNotes:       bizCompanyProfile?.invoiceNotes       || 'All charges are in GBP.',
  };

  // ── Auth & Password Functions ────────────────────────────────────────────
  const ADMIN_ROLES = ['admin', 'hr_manager', 'hr', 'owner', 'super_admin', 'director', 'ceo', 'cto', 'coo', 'manager'];

  const checkAdminStatus = async () => {
    try {
      const userData = await AsyncStorage.getItem('user_data');
      if (userData) {
        const user = JSON.parse(userData);
        const role = (user.user_role || user.role || user.account_type || '').toLowerCase();
        setIsAdminUser(ADMIN_ROLES.some(r => role.includes(r)) || user.is_admin === true || user.is_admin === 1);
      }
    } catch {}
    setAuthChecked(true);
  };

  // ── Screen Password (payroll_management) — DB: admin_passwords ──
  const SCREEN_MAX_ATTEMPTS = 5;
  const SCREEN_LOCKOUT_MINS = 15;
  const SCREEN_NAME = 'payroll_management';

  const checkScreenPassword = async () => {
    try {
      const bizId = routeCompanyId || bizCompanyProfile?.id || await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      if (!bizId) { setScreenPasswordVerified(true); setScreenPasswordChecked(true); return; }
      const headers = { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' };
      // 1. Try screen-specific password
      let rec = null;
      try {
        const r = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=${SCREEN_NAME}`, { headers });
        if (r.ok) { const j = await r.json(); const recs = (j?.data || []).filter(x => x.screen_name === SCREEN_NAME); if (recs.length) rec = recs[0]; }
      } catch {}
      // 2. Fallback to global password
      if (!rec) {
        try {
          const r2 = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=global`, { headers });
          if (r2.ok) { const j2 = await r2.json(); const recs2 = (j2?.data || []).filter(x => x.screen_name === 'global'); if (recs2.length) rec = recs2[0]; }
        } catch {}
      }
      // 3. Also try legacy (no screen_name)
      if (!rec) {
        try {
          const r3 = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}`, { headers });
          if (r3.ok) { const j3 = await r3.json(); if ((j3?.data || []).length) rec = j3.data[0]; }
        } catch {}
      }
      if (rec) {
        setScreenHasPassword(true);
        if (rec.locked_until && new Date(rec.locked_until) > new Date()) setScreenLockoutUntil(new Date(rec.locked_until).getTime());
      } else { setScreenHasPassword(false); setScreenPasswordVerified(true); }
    } catch { setScreenPasswordVerified(true); }
    setScreenPasswordChecked(true);
  };

  const verifyScreenPassword = async () => {
    setScreenPasswordError('');
    if (screenLockoutUntil && Date.now() < screenLockoutUntil) {
      setScreenPasswordError(`Locked. Try again in ${Math.ceil((screenLockoutUntil - Date.now()) / 60000)} min.`);
      return;
    }
    if (!screenPassword || screenPassword.length < 4) { setScreenPasswordError('Min 4 characters'); return; }
    try {
      const bizId = routeCompanyId || bizCompanyProfile?.id || await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
      // Find the right password record (screen-specific → global → legacy)
      let rec = null;
      for (const sn of [SCREEN_NAME, 'global', undefined]) {
        const url = sn ? `https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=${sn}` : `https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}`;
        try {
          const r = await fetch(url, { headers });
          if (r.ok) { const j = await r.json(); const recs = sn ? (j?.data || []).filter(x => x.screen_name === sn) : (j?.data || []); if (recs.length) { rec = recs[0]; break; } }
        } catch {}
      }
      if (!rec) { setScreenPasswordVerified(true); return; }
      if (rec.locked_until && new Date(rec.locked_until) > new Date()) {
        setScreenLockoutUntil(new Date(rec.locked_until).getTime());
        setScreenPasswordError('Account locked. Try again later.');
        return;
      }
      if (screenPassword === rec.password_hash) {
        setScreenPasswordVerified(true); setScreenPasswordAttempts(0); setScreenPassword('');
        try { await fetch(`https://api.jobeng.ai/api/data/admin_passwords/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: 0, locked_until: null }) }); } catch {}
      } else {
        const na = (rec.attempts || 0) + 1;
        setScreenPasswordAttempts(na);
        let lu = null;
        if (na >= SCREEN_MAX_ATTEMPTS) { lu = new Date(Date.now() + SCREEN_LOCKOUT_MINS * 60 * 1000).toISOString(); setScreenLockoutUntil(Date.now() + SCREEN_LOCKOUT_MINS * 60 * 1000); setScreenPasswordError(`Locked for ${SCREEN_LOCKOUT_MINS} min.`); }
        else { setScreenPasswordError(`Incorrect. ${SCREEN_MAX_ATTEMPTS - na} attempt${SCREEN_MAX_ATTEMPTS - na > 1 ? 's' : ''} left.`); }
        try { await fetch(`https://api.jobeng.ai/api/data/admin_passwords/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: na, locked_until: lu }) }); } catch {}
      }
    } catch { setScreenPasswordError('Verification failed.'); }
  };;

  // ── State ─────────────────────────────────────────────────────────────────
  // ── Auth + Password Gate ──
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [screenPasswordVerified, setScreenPasswordVerified] = useState(false);
  const [screenHasPassword, setScreenHasPassword] = useState(false);
  const [screenPassword, setScreenPassword] = useState('');
  const [screenPasswordError, setScreenPasswordError] = useState('');
  const [screenPasswordAttempts, setScreenPasswordAttempts] = useState(0);
  const [screenLockoutUntil, setScreenLockoutUntil] = useState(null);
  const [showScreenPassword, setShowScreenPassword] = useState(false);
  const [screenPasswordChecked, setScreenPasswordChecked] = useState(false);

  const [activeTab, setActiveTab] = useState('employees'); // employees, payruns, payslips, reports, analytics, settings
  
  const [employees, setEmployees] = useState([]);
  const [payRuns, setPayRuns] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [taxBands, setTaxBands] = useState(TAX_BANDS_2024);
  const [niBands, setNiBands] = useState(NI_BANDS_2024);
  const [statutoryRates, setStatutoryRates] = useState(STATUTORY_RATES_2024);
  
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('synced');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedPayRun, setSelectedPayRun] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  
  // Modals
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(false);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showPayRunDetail, setShowPayRunDetail] = useState(false);
  const [showPayRunForm, setShowPayRunForm] = useState(false);
  const [showPayslipDetail, setShowPayslipDetail] = useState(false);
  const [showPayslipForm, setShowPayslipForm] = useState(false);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showTaxCalculator, setShowTaxCalculator] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Forms
  const [employeeForm, setEmployeeForm] = useState({});
  const [payRunForm, setPayRunForm] = useState({});
  const [payslipForm, setPayslipForm] = useState({});
  const [reportForm, setReportForm] = useState({
    type: 'payroll-summary',
    period: 'monthly',
    startDate: '',
    endDate: '',
    format: 'pdf',
    departments: [],
    includeDetails: true,
  });
  
  // UI State
  const [isProcessing, setIsProcessing] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState({
    payeReference: '',
    accountsOfficeRef: '',
    payFrequency: 'Monthly',
    payMethod: 'BACS',
    paymentDay: '28',
    pensionScheme: 'NEST',
    minEmployerPension: '3',
    currency: 'GBP',
    rtiAutoSubmit: true,
    p45AutoGenerate: true,
    p60AutoGenerate: true,
    bacsFileGen: false,
    reminderPayRun: true,
    reminderHMRC: true,
    reminderPension: true,
    reminderBirthday: false,
    reminderContract: true,
  });
  const [progress, setProgress] = useState(0);
  const [empDetailTab, setEmpDetailTab] = useState('personal');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [selectedImportRows, setSelectedImportRows] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const searchInputRef = useRef(null);

  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    checkAdminStatus();
    checkScreenPassword();
    initialize();
    setupBackHandler();
    return () => {
      // Cleanup
    };
  }, []);


  // ─── Employee Management Integration ──────────────────────────────────────
  const syncFromEmployeeManagement = async () => {
    try {
      setSyncStatus('syncing');

      // ── Try ManageAPI first ────────────────────────────────────────────────
      let mgmtEmployees = [];
      try {
        const { data } = await ManageAPI.list();
        if (Array.isArray(data) && data.length > 0) mgmtEmployees = data;
      } catch (_) {}
      // Also write to AsyncStorage so the local fallback path below still works
      if (mgmtEmployees.length > 0) {
        const mapped = { employees: mgmtEmployees, lastUpdated: new Date().toISOString() };
        await AsyncStorage.setItem('employeeManagementData', JSON.stringify(mapped));
      }
      
      // Pull from employeeManagementData
      const mgmtRaw = await AsyncStorage.getItem('employeeManagementData');
      if (mgmtRaw) {
        const mgmtData = JSON.parse(mgmtRaw);
        const mgmtEmployees = mgmtData.employees || [];
        
        // Get current payroll employees
        const currentPayrollEmps = [...employees];
        let added = 0, updated = 0;
        
        for (const mgmtEmp of mgmtEmployees) {
          if (mgmtEmp.status !== 'Active' && mgmtEmp.status !== 'Probation') continue;
          
          // Check if already in payroll
          const existingIdx = currentPayrollEmps.findIndex(p =>
            p.id === mgmtEmp.id ||
            (p.contact?.email && p.contact.email === mgmtEmp.email) ||
            p.employeeNumber === mgmtEmp.employeeId
          );
          
          if (existingIdx === -1) {
            // New employee - add to payroll
            const nameParts = mgmtEmp.name.split(' ');
            const newPayrollEmp = {
              id: mgmtEmp.id,
              employeeNumber: mgmtEmp.employeeId || `EMP${String(currentPayrollEmps.length + 1).padStart(4, '0')}`,
              title: 'Mr/Ms',
              firstName: nameParts[0] || mgmtEmp.name,
              lastName: nameParts.slice(1).join(' ') || '',
              preferredName: nameParts[0] || mgmtEmp.name,
              gender: '',
              dateOfBirth: mgmtEmp.dateOfBirth || '',
              nationality: 'British',
              niNumber: '',
              taxCode: '1257L',
              taxRegion: 'UK',
              niCategory: 'A',
              studentLoan: 'none',
              postgraduateLoan: false,
              contact: {
                email: mgmtEmp.email || '',
                phone: mgmtEmp.phone || '',
                mobile: mgmtEmp.phone || '',
                address: {
                  line1: mgmtEmp.address || '',
                  city: '',
                  postcode: '',
                  country: 'United Kingdom',
                },
              },
              employment: {
                startDate: mgmtEmp.startDate || new Date().toISOString().split('T')[0],
                endDate: null,
                contractType: mgmtEmp.employeeCode === 'CONTRACT' ? 'fixed-term' : 'permanent',
                employmentType: mgmtEmp.employeeCode === 'PART_TIME' ? 'part-time' : 'full-time',
                workingHours: mgmtEmp.employeeCode === 'PART_TIME' ? 20 : 37.5,
                department: mgmtEmp.department || '',
                jobTitle: mgmtEmp.position || '',
                location: mgmtEmp.location || 'Office',
                holidayEntitlement: 28,
                holidayRemaining: 28,
              },
              payroll: {
                payFrequency: 'monthly',
                payMethod: 'BACS',
                paymentDate: 28,
                bankDetails: {
                  accountName: mgmtEmp.name,
                  sortCode: '',
                  accountNumber: '',
                  bankName: '',
                },
                salary: parseInt(mgmtEmp.salary) || 0,
                hourlyRate: null,
                pension: {
                  enrolled: true,
                  scheme: 'NEST',
                  employeeContribution: 0.05,
                  employerContribution: 0.03,
                  joiningDate: mgmtEmp.startDate || new Date().toISOString().split('T')[0],
                },
                deductions: [],
                benefits: [],
              },
              status: 'active',
              avatar: mgmtEmp.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(mgmtEmp.name)}&background=0e4e96&color=fff&size=128`,
              _fromManagement: true,
              _mgmtId: mgmtEmp.id,
            };
            currentPayrollEmps.push(newPayrollEmp);
            added++;
          } else {
            // Update salary and position from management
            const current = currentPayrollEmps[existingIdx];
            const newSalary = parseInt(mgmtEmp.salary) || current.payroll?.salary || 0;
            if (newSalary !== current.payroll?.salary || mgmtEmp.position !== current.employment?.jobTitle) {
              currentPayrollEmps[existingIdx] = {
                ...current,
                employment: {
                  ...current.employment,
                  jobTitle: mgmtEmp.position || current.employment?.jobTitle,
                  department: mgmtEmp.department || current.employment?.department,
                },
                payroll: {
                  ...current.payroll,
                  salary: newSalary,
                },
                _mgmtSynced: true,
                _mgmtSyncedAt: new Date().toISOString(),
              };
              updated++;
            }
          }
        }
        
        setEmployees(currentPayrollEmps);
        await AsyncStorage.setItem('payroll_employees', JSON.stringify(currentPayrollEmps));
        setSyncStatus('synced');
        
        if (added > 0 || updated > 0) {
          Alert.alert(
            'Sync Complete',
            `Added ${added} new employees, updated ${updated} existing employees from management system.`
          );
        } else {
          Alert.alert('Already in Sync', 'All management employees are already in payroll.');
        }
      } else {
        setSyncStatus('synced');
        Alert.alert('No Data', 'No employee management data found. Add employees in the Employee Management screen first.');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setSyncStatus('error');
      Alert.alert('Sync Error', 'Failed to sync from employee management: ' + err.message);
    }
  };

  const writePayrollRecordForEmployee = async (employeeId, payslipData) => {
    try {
      const recordsRaw = await AsyncStorage.getItem('payroll_records_per_employee');
      const records = recordsRaw ? JSON.parse(recordsRaw) : {};
      
      if (!records[employeeId]) records[employeeId] = { payslips: [], ytd: {} };
      
      // Add payslip
      records[employeeId].payslips = [payslipData, ...(records[employeeId].payslips || [])];
      
      // Update YTD
      const year = new Date().getFullYear().toString();
      if (!records[employeeId].ytd[year]) {
        records[employeeId].ytd[year] = { gross: 0, tax: 0, ni: 0, pension: 0, net: 0 };
      }
      records[employeeId].ytd[year].gross += payslipData.gross || 0;
      records[employeeId].ytd[year].tax += payslipData.tax || payslipData.incomeTax || 0;
      records[employeeId].ytd[year].ni += payslipData.ni || payslipData.nationalInsurance || 0;
      records[employeeId].ytd[year].pension += payslipData.employeePension || 0;
      records[employeeId].ytd[year].net += payslipData.net || payslipData.netPay || 0;
      
      await AsyncStorage.setItem('payroll_records_per_employee', JSON.stringify(records));
    } catch (err) {
      console.error('Error writing payroll record:', err);
    }
  };

  const navigateToEmployeeDetails = (emp) => {
    if (!navigation) return;
    navigation.navigate('EmployeeDetails', {
      employeeId: emp.id || emp._mgmtId,
      employee: {
        id: emp.id,
        employeeId: emp.employeeNumber,
        name: emp.firstName + ' ' + emp.lastName,
        email: emp.contact?.email || '',
        phone: emp.contact?.phone || '',
        position: emp.employment?.jobTitle || '',
        department: emp.employment?.department || '',
        baseSalary: emp.payroll?.salary || 0,
        salary: emp.payroll?.salary || 0,
        totalPackage: emp.payroll?.salary || 0,
        startDate: emp.employment?.startDate || '',
        avatar: emp.avatar || '',
        skills: emp.skills || [],
      },
    });
  };

  const initialize = async () => {
    setIsLoading(true);
    try {
      // Auto-sync from employee management on startup
      try {
        const syncRaw = await AsyncStorage.getItem('payroll_employee_sync');
        const syncData = syncRaw ? JSON.parse(syncRaw) : {};
        const syncedCount = Object.keys(syncData).length;
        if (syncedCount > 0) {
          // Merge synced data into payroll employees
          const cachedPayrollEmpsRaw = await AsyncStorage.getItem('payroll_employees');
          const cachedPayrollEmps = cachedPayrollEmpsRaw ? JSON.parse(cachedPayrollEmpsRaw) : [];
          for (const [key, syncEmp] of Object.entries(syncData)) {
            const idx = cachedPayrollEmps.findIndex(p => p.id === syncEmp.employeeId || (p.contact?.email === syncEmp.email));
            if (idx !== -1) {
              cachedPayrollEmps[idx].payroll = {
                ...cachedPayrollEmps[idx].payroll,
                salary: syncEmp.salary || cachedPayrollEmps[idx].payroll?.salary,
              };
              cachedPayrollEmps[idx]._syncedAt = syncEmp.updatedAt;
            }
          }
          await AsyncStorage.setItem('payroll_employees', JSON.stringify(cachedPayrollEmps));
        }
      } catch (syncErr) {
        console.log('Sync merge skipped:', syncErr.message);
      }

      // ── Fetch all employees from the SAME source as HR Dashboard & Employee Management
      //    /api/data/employees = 6 employees (the full list)
      //    /api/data/payroll_employees = 3 (only payroll-enrolled, used for salary overlay)
      try {
        const cid  = await AsyncStorage.getItem('company_id');
        const tok  = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
        const hdrs = {
          'Content-Type': 'application/json',
          'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        };
        const base = 'https://api.jobeng.ai';
        const qry  = cid ? `?company_id=${parseInt(cid)||0}` : '';

        const parseRows = async (r) => {
          if (r.status !== 'fulfilled' || !r.value.ok) return null;
          const json = await r.value.json().catch(() => null);
          if (!json) return null;
          return Array.isArray(json) ? json : (json?.data ?? null);
        };

        // Fetch all 5 tables in parallel
        const [empR, payEmpR, slipR, runR] = await Promise.allSettled([
          fetch(`${base}/api/data/employees${qry}`,         { headers: hdrs }), // all 6 employees
          fetch(`${base}/api/payroll/employees`, { headers: hdrs }), // payroll-specific fields
          fetch(`${base}/api/data/payslips${qry}`,          { headers: hdrs }),
          fetch(`${base}/api/payroll/runs`, { headers: hdrs }),
        ]);

        const [allEmps, payEmps, apiSlips, apiRuns] = await Promise.all([
          parseRows(empR), parseRows(payEmpR), parseRows(slipR), parseRows(runR),
        ]);

        // Build a salary/payroll-data lookup from payroll_employees table
        const payrollLookup = {};
        if (payEmps?.length > 0) {
          payEmps.forEach(pe => {
            const key = pe.id || pe.employee_id || pe.employeeId;
            if (key) payrollLookup[String(key)] = pe;
          });
        }

        // Normalise all employees into payroll schema — use allEmps (6) as the base
        const sourceEmps = allEmps?.length > 0 ? allEmps : (payEmps || []);
        if (sourceEmps.length > 0) {
          const norm = sourceEmps.map(e => {
            const pe = payrollLookup[String(e.id)] || payrollLookup[String(e.employee_id)] || {};
            const rawName = e.name || pe.name || '';
            const firstName = e.first_name || e.firstName || rawName.split(' ')[0] || '';
            const lastName  = e.last_name  || e.lastName  || rawName.split(' ').slice(1).join(' ') || '';
            const jobTitle  = e.job_title  || e.position  || e.jobTitle || pe.position || '';
            const department= e.department || pe.department || '';
            const startDate = e.start_date || e.startDate || e.hire_date || pe.start_date || '';
            const salary    = parseFloat(pe.base_salary || pe.salary || e.base_salary || e.salary || 0);
            const status    = (e.status || pe.status || 'active').toLowerCase();
            return {
              id:             String(e.id || pe.id || `pe_${Date.now()}_${Math.random()}`),
              firstName,
              lastName,
              employeeNumber: e.employee_number || e.employee_id || pe.employee_id || String(e.id || ''),
              status,
              department,
              jobTitle,
              // employment sub-object expected by EmployeeCard and detail modal
              employment: {
                jobTitle,
                department,
                startDate,
                employmentType: e.employment_type || pe.employment_type || 'Full-Time',
                grade:          pe.grade || '',
              },
              contact: {
                email:   e.email   || pe.email   || '',
                phone:   e.phone   || pe.phone   || '',
                address: e.address || pe.address || '',
              },
              payroll: {
                salary,
                payFrequency:   pe.pay_frequency || 'monthly',
                payMethod:      pe.payment_method || pe.pay_method || 'BACS',
                paymentDate:    28,
                bankDetails: {
                  accountNumber: pe.bank_account || e.bank_account || '',
                  sortCode:      pe.sort_code    || '',
                },
                pension: {
                  enrolled:             true,
                  employeeContribution: parseFloat(pe.pension_rate || 5),
                  employerContribution: 3,
                  scheme:               pe.pension_scheme || 'Workplace Pension',
                },
                ytd: pe.ytd || { gross: 0, tax: 0, ni: 0, net: 0, pension: 0 },
                allowances:    parseFloat(pe.allowances || 0),
                bonus:         parseFloat(pe.bonus || e.bonus || 0),
                overtime:      parseFloat(pe.overtime_hours || 0),
                commission:    parseFloat(pe.commission || 0),
                deductions: {
                  insurance: parseFloat(pe.insurance_deduction || 0),
                  other:     parseFloat(pe.other_deductions   || 0),
                },
              },
              taxCode:    pe.tax_code   || e.tax_code   || '1257L',
              niNumber:   pe.ni_number  || e.ni_number  || '',
              niCategory: pe.ni_category || 'A',
              startDate,
              avatar: (() => {
                const p = e.profile_image || pe.avatar || pe.profile_image || null;
                return p ? (p.startsWith('http') ? p : `https://api.jobeng.ai${p}`) : null;
              })(),
            };
          });
          setEmployees(norm);
          await AsyncStorage.setItem('payroll_employees', JSON.stringify(norm));
          // Also write to shared cache so HR and Employee Management screens stay in sync
          const cid4 = await AsyncStorage.getItem('company_id').catch(()=>null);
          await AsyncStorage.setItem('hr_employees', JSON.stringify(norm));
          if (cid4) await AsyncStorage.setItem(`hr_employees_${cid4}`, JSON.stringify(norm));
          await AsyncStorage.setItem('mgmt_emp_cache', JSON.stringify(norm));
          console.log(`[Payroll] Loaded ${norm.length} employees (from /api/data/employees)`);
        }

        if (apiSlips?.length > 0) {
          setPayslips(apiSlips);
          await AsyncStorage.setItem('payroll_payslips', JSON.stringify(apiSlips));
          console.log(`[Payroll] Loaded ${apiSlips.length} payslips from API`);
        }

        if (apiRuns?.length > 0) {
          const norm = apiRuns.map(r => ({
            id:           r.id || `run_${Date.now()}`,
            period:       r.period || r.pay_period || '',
            payDate:      r.pay_date || r.payDate || '',
            status:       r.status || 'draft',
            totalGross:   parseFloat(r.total_gross  || r.totalGross  || 0),
            totalNet:     parseFloat(r.total_net    || r.totalNet    || 0),
            totalTax:     parseFloat(r.total_tax    || r.totalTax    || 0),
            totalNI:      parseFloat(r.total_ni     || r.totalNI     || 0),
            totalPension: parseFloat(r.total_pension|| r.totalPension|| 0),
            employeeCount:parseInt(r.employee_count || r.employeeCount || 0),
            notes:        r.notes || '',
          }));
          setPayRuns(norm);
          await AsyncStorage.setItem('payroll_payruns', JSON.stringify(norm));
          console.log(`[Payroll] Loaded ${norm.length} pay runs from API`);
        }
      } catch (apiErr) {
        console.warn('[Payroll] API fetch failed, using cache:', apiErr.message);
      }

      // ── Also load from shared employee cache written by EmployeeManagement ─────
      try {
        const cid3 = await AsyncStorage.getItem('company_id');
        const sharedCache = await AsyncStorage.getItem('mgmt_emp_cache')
                         || (cid3 ? await AsyncStorage.getItem(`hr_employees_${cid3}`) : null)
                         || await AsyncStorage.getItem('hr_employees');
        if (sharedCache) {
          const shared = JSON.parse(sharedCache);
          if (Array.isArray(shared) && shared.length > 0) {
            // Merge shared employees into payroll, keeping existing payroll data
            setEmployees(prev => {
              if (prev.length >= shared.length) return prev; // already have more
              const map = new Map(prev.map(e => [String(e.id), e]));
              shared.forEach(e => {
                const key = String(e.id || e.employeeId || '');
                if (!key) return;
                if (!map.has(key)) {
                  const _fn = e.firstName || e.first_name || (e.name||'').split(' ')[0] || '';
                  const _ln = e.lastName  || e.last_name  || (e.name||'').split(' ').slice(1).join(' ') || '';
                  const _jt = e.position  || e.jobTitle   || e.job_title || '';
                  const _dp = e.department || '';
                  map.set(key, {
                    id: key,
                    firstName: _fn, lastName: _ln,
                    employeeNumber: e.employeeId || e.employee_number || key,
                    status: (e.status || 'active').toLowerCase(),
                    department: _dp, jobTitle: _jt,
                    employment: { jobTitle: _jt, department: _dp, startDate: e.startDate || e.start_date || '', employmentType: 'Full-Time', grade: '' },
                    contact: { email: e.email || '', phone: e.phone || '', address: e.address || '' },
                    payroll: { salary: parseInt(e.salary)||0, payFrequency:'monthly', payMethod:'BACS', paymentDate:28, pension:{ enrolled:true, employeeContribution:5, employerContribution:3 } },
                    taxCode: '1257L', niNumber: '', niCategory: 'A', startDate: e.startDate || '',
                  });
                }
              });
              const merged = Array.from(map.values());
              if (merged.length > prev.length) {
                AsyncStorage.setItem('payroll_employees', JSON.stringify(merged)).catch(() => {});
                console.log(`[Payroll] Merged ${merged.length} employees from shared cache`);
              }
              return merged;
            });
          }
        }
      } catch (_) {}

      // ── Load from cache (fallback if API returned nothing) ─────────────
      const cachedEmployees = await AsyncStorage.getItem('payroll_employees');
      if (cachedEmployees) {
        setEmployees(prev => prev.length > 0 ? prev : JSON.parse(cachedEmployees));
      } else {
        setEmployees(SAMPLE_EMPLOYEES);
        await AsyncStorage.setItem('payroll_employees', JSON.stringify(SAMPLE_EMPLOYEES));
      }
      
      const cachedPayRuns = await AsyncStorage.getItem('payroll_payruns');
      if (cachedPayRuns) {
        setPayRuns(prev => prev.length > 0 ? prev : JSON.parse(cachedPayRuns));
      } else {
        setPayRuns(SAMPLE_PAY_RUNS);
        await AsyncStorage.setItem('payroll_payruns', JSON.stringify(SAMPLE_PAY_RUNS));
      }

      const cachedPayslips = await AsyncStorage.getItem('payroll_payslips');
      if (cachedPayslips) {
        setPayslips(prev => prev.length > 0 ? prev : JSON.parse(cachedPayslips));
      } else {
        setPayslips(SAMPLE_PAYSLIPS);
        await AsyncStorage.setItem('payroll_payslips', JSON.stringify(SAMPLE_PAYSLIPS));
      }

      // ── Fetch tax/NI bands from confirmed working endpoints ─────────────
      try {
        const cid = await AsyncStorage.getItem('company_id');
        const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
        const hdrs = {
          'Content-Type': 'application/json',
          'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        };
        const [taxR, niR] = await Promise.allSettled([
          fetch('https://api.jobeng.ai/api/payroll/tax-bands/current', { headers: hdrs }),
          fetch('https://api.jobeng.ai/api/payroll/ni-bands', { headers: hdrs }),
        ]);
        if (taxR.status === 'fulfilled' && taxR.value.ok) {
          const json = await taxR.value.json();
          const rows = Array.isArray(json) ? json : (json?.data ?? []);
          if (rows.length > 0) {
            setTaxBands(rows);
            await AsyncStorage.setItem('payroll_tax_bands', JSON.stringify(rows));
          }
        }
        if (niR.status === 'fulfilled' && niR.value.ok) {
          const json = await niR.value.json();
          const rows = Array.isArray(json) ? json : (json?.data ?? []);
          if (rows.length > 0) {
            setNiBands(rows);
            await AsyncStorage.setItem('payroll_ni_bands', JSON.stringify(rows));
          }
        }
      } catch (error) {
        console.log('Using default tax bands — API unavailable');
        // Try cache
        try {
          const cachedTax = await AsyncStorage.getItem('payroll_tax_bands');
          const cachedNI  = await AsyncStorage.getItem('payroll_ni_bands');
          if (cachedTax) setTaxBands(JSON.parse(cachedTax));
          if (cachedNI)  setNiBands(JSON.parse(cachedNI));
        } catch (_) {}
      }
      
      // Load payroll-specific settings (PAYE ref, Accounts Office ref, etc.)
      const payrollSettingsJson = await AsyncStorage.getItem('payroll_settings');
      if (payrollSettingsJson) {
        const ps = JSON.parse(payrollSettingsJson);
        setPayrollSettings(ps);
      }

      // ── Merge additional employees from ManageAPI (company_id-scoped) ──────
      try {
        const cid2 = await AsyncStorage.getItem('company_id');
        const empResult = await ManageAPI.list(cid2 ? { company_id: cid2 } : {});
        const apiEmps = Array.isArray(empResult) ? empResult : (empResult?.data ?? []);
        if (Array.isArray(apiEmps) && apiEmps.length > 0) {
          const normalized = apiEmps.map(e => {
            const fn = e.firstName || e.first_name || (e.name||'').split(' ')[0] || '';
            const ln = e.lastName  || e.last_name  || (e.name||'').split(' ').slice(1).join(' ') || '';
            const jt = e.jobTitle  || e.position   || e.job_title || '';
            const dp = e.department || '';
            const sd = e.startDate || e.start_date || e.hire_date || '';
            return {
              id:            String(e.id || e.employeeId || `e_${Date.now()}`),
              firstName: fn, lastName: ln,
              employeeNumber: e.employee_number || e.employeeId || String(e.id || ''),
              status:        (e.status || 'active').toLowerCase(),
              department: dp, jobTitle: jt,
              employment: { jobTitle: jt, department: dp, startDate: sd, employmentType: 'Full-Time', grade: '' },
              contact: {
                email:   e.email   || e.contact?.email   || '',
                phone:   e.phone   || e.contact?.phone   || '',
                address: e.address || e.contact?.address || '',
              },
              payroll: {
                salary:       parseFloat(e.salary || e.baseSalary || e.base_salary || e.payroll?.salary || 0),
                payFrequency: e.payroll?.payFrequency || 'monthly',
                payMethod:    e.payroll?.payMethod    || 'BACS',
                paymentDate:  e.payroll?.paymentDate  || 28,
                pension: {
                  enrolled:             e.payroll?.pension?.enrolled ?? true,
                  employeeContribution: e.payroll?.pension?.employeeContribution ?? 5,
                  employerContribution: e.payroll?.pension?.employerContribution ?? 3,
                },
                ytd: e.payroll?.ytd || { gross: 0, tax: 0, ni: 0, net: 0, pension: 0 },
              },
              taxCode:    e.taxCode    || e.tax_code    || '1257L',
              niNumber:   e.niNumber   || e.ni_number   || '',
              niCategory: e.niCategory || 'A',
              startDate:  sd,
              avatar: (() => {
                const p = e.profile_image || e.avatar || null;
                return p ? (p.startsWith('http') ? p : `https://api.jobeng.ai${p}`) : null;
              })(),
            };
          });
          setEmployees(prev => {
            const map = new Map(prev.map(e => [String(e.id), e]));
            normalized.forEach(e => map.set(String(e.id), { ...map.get(String(e.id)), ...e }));
            const merged = Array.from(map.values());
            AsyncStorage.setItem('payroll_employees', JSON.stringify(merged)).catch(() => {});
            return merged;
          });
        }
      } catch (empApiErr) {
        console.warn('[PayrollAPI] employee list fetch failed:', empApiErr.message);
      }

      // ── Load payroll analytics ───────────────────────────────────────────
      try {
        const [costRes, monthlyRes] = await Promise.allSettled([
          PayrollIntegrationAPI.getPayrollCost(),
          PayrollIntegrationAPI.getMonthly(),
        ]);
        if (costRes.status === 'fulfilled') {
          const cost = costRes.value.data ?? {};
          await AsyncStorage.setItem('payroll_cost_cache', JSON.stringify(cost));
        }
        if (monthlyRes.status === 'fulfilled') {
          const monthly = monthlyRes.value.data ?? [];
          await AsyncStorage.setItem('payroll_monthly_cache', JSON.stringify(monthly));
        }
      } catch (_) {}

      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true,
      }).start();
      
    } catch (error) {
      console.error('Init error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setupBackHandler = () => {
    const handler = BackHandler.addEventListener('hardwareBackPress', handleBack);
    return () => handler.remove();
  };

  const handleBack = useCallback(() => {
    if (showEmployeeDetail || showEmployeeForm || showPayRunDetail || 
        showPayRunForm || showPayslipDetail || showPayslipForm || 
        showReportGenerator || showAnalytics || showTaxCalculator || showSettings) {
      closeAllModals();
      return true;
    }
    navigation?.goBack();
    return true;
  }, [
    showEmployeeDetail, showEmployeeForm, showPayRunDetail, showPayRunForm,
    showPayslipDetail, showPayslipForm, showReportGenerator, showAnalytics,
    showTaxCalculator, showSettings, navigation
  ]);

  const closeAllModals = () => {
    setShowEmployeeDetail(false);
    setShowEmployeeForm(false);
    setShowPayRunDetail(false);
    setShowPayRunForm(false);
    setShowPayslipDetail(false);
    setShowPayslipForm(false);
    setShowReportGenerator(false);
    setShowAnalytics(false);
    setShowTaxCalculator(false);
    setShowSettings(false);
  };

  // ── Data Processing ─────────────────────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    const safe = (employees || []).filter(e => e && typeof e === 'object');
    if (!searchQuery) return safe;
    const query = searchQuery.toLowerCase();
    return safe.filter(emp => {
      try {
        return (
          (emp.firstName || '').toLowerCase().includes(query) ||
          (emp.lastName || '').toLowerCase().includes(query) ||
          (emp.employeeNumber || '').toLowerCase().includes(query) ||
          (emp.employment?.department || '').toLowerCase().includes(query) ||
          (emp.employment?.jobTitle || '').toLowerCase().includes(query) ||
          (emp.niNumber || '').toLowerCase().includes(query) ||
          (emp.status || '').toLowerCase().includes(query)
        );
      } catch { return false; }
    });
  }, [employees, searchQuery]);

  const analytics = useMemo(() => {
    // Fully guard every property access - employees may come from storage/import with partial data
    const safeEmployees = (employees || []).filter(e => e && typeof e === 'object');
    const activeEmployees = safeEmployees.filter(e => e.status === 'active');

    const getSalary = (e) => {
      try { return Number(e?.payroll?.salary) || 0; } catch { return 0; }
    };
    const getPayroll = (e) => e?.payroll || {};
    const getEmployment = (e) => e?.employment || {};
    const getPension = (e) => e?.payroll?.pension || {};

    const totalAnnualSalary = activeEmployees.reduce((sum, e) => sum + getSalary(e), 0);
    const monthlyPayroll = totalAnnualSalary / 12;

    const pensionEnrolled = activeEmployees.filter(e => getPension(e).enrolled).length;
    const withStudentLoan = activeEmployees.filter(e => e.studentLoan).length;

    const departmentBreakdown = {};
    activeEmployees.forEach(e => {
      const dept = getEmployment(e).department || 'Unknown';
      if (!departmentBreakdown[dept]) {
        departmentBreakdown[dept] = { count: 0, salary: 0 };
      }
      departmentBreakdown[dept].count++;
      departmentBreakdown[dept].salary += getSalary(e);
    });

    const safePayRuns = (payRuns || []).filter(pr => pr && typeof pr === 'object');
    const completedRuns = safePayRuns.filter(pr => pr.status === 'completed');

    return {
      totalEmployees: safeEmployees.length,
      activeEmployees: activeEmployees.length,
      onLeave: safeEmployees.filter(e => e.status === 'on-leave').length,
      terminated: safeEmployees.filter(e => e.status === 'terminated').length,
      suspended: safeEmployees.filter(e => e.status === 'suspended').length,

      totalAnnualSalary,
      totalMonthlySalary: monthlyPayroll,
      averageSalary: activeEmployees.length ? totalAnnualSalary / activeEmployees.length : 0,

      pensionEnrolled,
      pensionPercentage: activeEmployees.length ? (pensionEnrolled / activeEmployees.length) * 100 : 0,
      withStudentLoan,

      departmentBreakdown,

      upcomingPayRun: safePayRuns.find(pr => pr.status === 'processing') || safePayRuns[0] || null,
      completedPayRuns: completedRuns.length,
      totalPaidYTD: completedRuns.reduce((sum, pr) => sum + (Number(pr.totalNet) || 0), 0),
      taxCollectedYTD: completedRuns.reduce((sum, pr) => sum + (Number(pr.totalTax) || 0), 0),
      niCollectedYTD: completedRuns.reduce((sum, pr) => sum + (Number(pr.totalNI) || 0), 0),
      pensionCollectedYTD: completedRuns.reduce((sum, pr) => sum + (Number(pr.totalPension) || 0), 0),
    };
  }, [employees, payRuns]);

  // ── Employee Actions ────────────────────────────────────────────────────
  const createEmployee = async () => {
    try {
      const newEmployee = {
        ...employeeForm,
        id: makeId('emp'),
        employeeNumber: `EMP${String(employees.length + 1).padStart(3, '0')}`,
        createdDate: nowISO(),
        modifiedDate: nowISO(),
        status: 'active',
      };
      
      const updated = [newEmployee, ...employees];
      setEmployees(updated);
      await AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));
      
      // API call
      try {
        const res = await api.post('/api/payroll/employees', newEmployee);
        const saved = res.data?.data ?? res.data;
        if (saved?.id) {
          setEmployees(prev => prev.map(e => e.id === newEmployee.id ? { ...e, id: saved.id } : e));
        }
        // Sync to employee management
        PayrollIntegrationAPI.syncEmployee(saved?.id ?? newEmployee.id).catch(() => {});
      } catch (postErr) {
        console.warn('[PayrollAPI] POST employee failed (saved locally):', postErr.message);
      }
      
      setShowEmployeeForm(false);
      setEmployeeForm({});
      
      Alert.alert('Success', 'Employee created successfully');
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      Alert.alert('Error', 'Failed to create employee');
    }
  };

  const updateEmployee = async () => {
    try {
      const updated = employees.map(emp =>
        emp.id === selectedEmployee.id
          ? { ...emp, ...employeeForm, modifiedDate: nowISO() }
          : emp
      );
      
      setEmployees(updated);
      await AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));
      
      // API call
      try {
        await api.put(`/api/payroll/employees/${selectedEmployee.id}`, employeeForm);
        // Sync salary/role changes to employee management
        if (employeeForm.payroll?.salary || employeeForm.jobTitle) {
          PayrollIntegrationAPI.syncEmployee(selectedEmployee.id).catch(() => {});
        }
      } catch (putErr) {
        console.warn('[PayrollAPI] PUT employee failed (saved locally):', putErr.message);
      }
      
      setShowEmployeeForm(false);
      setSelectedEmployee(null);
      setEmployeeForm({});
      
      Alert.alert('Success', 'Employee updated successfully');
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      Alert.alert('Error', 'Failed to update employee');
    }
  };

  const deleteEmployee = (employee) => {
    Alert.alert(
      'Delete Employee',
      `Are you sure you want to delete ${employee.firstName} ${employee.lastName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updated = employees.filter(emp => emp.id !== employee.id);
              setEmployees(updated);
              await AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));
              
              // API call
              try {
                await api.delete(`/api/payroll/employees/${employee.id}`);
              } catch (delErr) {
                console.warn('[PayrollAPI] DELETE employee failed:', delErr.message);
              }
              
              setShowEmployeeDetail(false);
              Alert.alert('Success', 'Employee deleted');
              Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
            } catch (error) {
              Alert.alert('Error', 'Failed to delete employee');
            }
          },
        },
      ]
    );
  };

  // ── Pay Run Actions ─────────────────────────────────────────────────────
  const createPayRun = async () => {
    try {
      const newPayRun = {
        ...payRunForm,
        id: makeId('pr'),
        reference: `PR-${new Date().getFullYear()}-${String(payRuns.length + 1).padStart(2, '0')}`,
        status: 'draft',
        employeeCount: employees.filter(e => e.status === 'active').length,
        totalGross: 0,
        totalNet: 0,
        totalTax: 0,
        totalNI: 0,
        totalPension: 0,
        createdDate: nowISO(),
      };
      
      const updated = [newPayRun, ...payRuns];
      setPayRuns(updated);
      await AsyncStorage.setItem('payroll_payruns', JSON.stringify(updated));
      
      // API call
      try {
        const prRes = await api.post('/api/payroll/pay-runs', newPayRun);
        const savedPR = prRes.data?.data ?? prRes.data;
        if (savedPR?.id && savedPR.id !== newPayRun.id) {
          setPayRuns(prev => prev.map(r => r.id === newPayRun.id ? { ...r, id: savedPR.id } : r));
        }
      } catch (prErr) {
        console.warn('[PayrollAPI] POST pay run failed (saved locally):', prErr.message);
      }
      
      setShowPayRunForm(false);
      setPayRunForm({});
      
      Alert.alert('Success', 'Pay run created');
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      Alert.alert('Error', 'Failed to create pay run');
    }
  };

  const processPayRun = async (payRun) => {
    Alert.alert(
      'Process Pay Run',
      `Process pay run for ${payRun.period}? This will calculate pay for all active employees.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Process',
          onPress: async () => {
            try {
              setIsProcessing(true);
              setProgress(0);
              
              // Update status
              const updated = payRuns.map(pr =>
                pr.id === payRun.id
                  ? { ...pr, status: 'processing' }
                  : pr
              );
              setPayRuns(updated);
              
              // Simulate processing
              for (let i = 0; i <= 100; i += 10) {
                setProgress(i);
                await new Promise(resolve => setTimeout(resolve, 500));
              }
              
              // Calculate totals
              const activeEmployees = employees.filter(e => e.status === 'active');
              let totalGross = 0, totalTax = 0, totalNI = 0, totalPension = 0;
              
              activeEmployees.forEach(emp => {
                const payroll = emp?.payroll || {};
                const monthlySalary = (Number(payroll.salary) || 0) / 12;
                totalGross += monthlySalary;

                const tax = calculateTax(monthlySalary, emp.taxCode || '1257L', taxBands);
                totalTax += tax;

                const ni = calculateNI(monthlySalary, emp.niCategory || 'A', niBands);
                totalNI += ni;

                if (payroll.pension?.enrolled) {
                  const { employeePension } = calculatePension(
                    monthlySalary,
                    payroll.pension.employeeContribution || 0.05,
                    payroll.pension.employerContribution || 0.03
                  );
                  totalPension += employeePension;
                }
              });
              
              const totalNet = totalGross - totalTax - totalNI - totalPension;
              
              // Update with calculated values
              const finalUpdated = payRuns.map(pr =>
                pr.id === payRun.id
                  ? {
                      ...pr,
                      status: 'completed',
                      processedDate: nowISO(),
                      totalGross,
                      totalNet,
                      totalTax,
                      totalNI,
                      totalPension,
                    }
                  : pr
              );
              setPayRuns(finalUpdated);
              
              // Generate payslips
              await generatePayslips(payRun.id, activeEmployees);
              
              // API call
              api.post(`/api/payroll/pay-runs/${payRun.id}/process`).catch(() => {});
              
              setIsProcessing(false);
              setProgress(0);
              setShowPayRunDetail(false);
              
              Alert.alert('Success', 'Pay run processed successfully');
              Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            } catch (error) {
              setIsProcessing(false);
              Alert.alert('Error', 'Failed to process pay run');
            }
          },
        },
      ]
    );
  };

  const approvePayRun = async (payRun) => {
    Alert.alert(
      'Approve Pay Run',
      `Approve pay run for ${payRun.period}? This will lock the calculations and prepare for payment.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Approve',
          onPress: async () => {
            try {
              const updated = payRuns.map(pr =>
                pr.id === payRun.id
                  ? {
                      ...pr,
                      status: 'approved',
                      approvedDate: nowISO(),
                      approvedBy: 'Current User',
                    }
                  : pr
              );
              setPayRuns(updated);
              await AsyncStorage.setItem('payroll_payruns', JSON.stringify(updated));
              
              // API call
              api.post(`/api/payroll/pay-runs/${payRun.id}/approve`).catch(() => {});
              
              setShowPayRunDetail(false);
              Alert.alert('Success', 'Pay run approved');
              Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            } catch (error) {
              Alert.alert('Error', 'Failed to approve pay run');
            }
          },
        },
      ]
    );
  };

  const lockPayRun = async (payRun) => {
    Alert.alert(
      'Lock Pay Run',
      `Lock pay run for ${payRun.period}? This will prevent any further changes.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Lock',
          onPress: async () => {
            try {
              const updated = payRuns.map(pr =>
                pr.id === payRun.id
                  ? {
                      ...pr,
                      status: 'locked',
                      lockedDate: nowISO(),
                      lockedBy: 'Current User',
                    }
                  : pr
              );
              setPayRuns(updated);
              await AsyncStorage.setItem('payroll_payruns', JSON.stringify(updated));
              
              // API call
              api.post(`/api/payroll/pay-runs/${payRun.id}/lock`).catch(() => {});
              
              setShowPayRunDetail(false);
              Alert.alert('Success', 'Pay run locked');
              Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            } catch (error) {
              Alert.alert('Error', 'Failed to lock pay run');
            }
          },
        },
      ]
    );
  };

  // ── Payslip Actions ─────────────────────────────────────────────────────
  const generatePayslips = async (payRunId, employees) => {
    const newPayslips = employees.map(emp => {
      const payroll = emp?.payroll || {};
      const monthlySalary = (Number(payroll.salary) || 0) / 12;
      const tax = calculateTax(monthlySalary, emp.taxCode || '1257L', taxBands);
      const ni = calculateNI(monthlySalary, emp.niCategory || 'A', niBands);

      let pension = 0;
      if (payroll.pension?.enrolled) {
        const { employeePension } = calculatePension(
          monthlySalary,
          payroll.pension.employeeContribution || 0.05,
          payroll.pension.employerContribution || 0.03
        );
        pension = employeePension;
      }
      
      const netPay = monthlySalary - tax - ni - pension;
      
      return {
        id: makeId('ps'),
        payRunId,
        employeeId: emp.id,
        employeeName: `${emp.firstName} ${emp.lastName}`,
        employeeNumber: emp.employeeNumber,
        period: payRuns.find(pr => pr.id === payRunId)?.period,
        issueDate: nowISO(),
        paymentDate: payRuns.find(pr => pr.id === payRunId)?.paymentDate,
        
        earnings: {
          basic: monthlySalary,
          overtime: 0,
          bonus: 0,
          commission: 0,
          holiday: 0,
          sick: 0,
          total: monthlySalary,
        },
        
        deductions: {
          tax,
          ni,
          pension,
          studentLoan: 0,
          total: tax + ni + pension,
        },
        
        netPay,
        
        yearToDate: {
          gross: monthlySalary * 12,
          tax: tax * 12,
          ni: ni * 12,
          pension: pension * 12,
          net: netPay * 12,
        },
        
        paymentMethod: emp?.payroll?.payMethod || "BACS",
        status: 'generated',
        createdDate: nowISO(),
      };
    });
    
    const updated = [...newPayslips, ...payslips];
    setPayslips(updated);
    await AsyncStorage.setItem('payroll_payslips', JSON.stringify(updated));
    
    // API call
    try {
      await api.post('/api/payroll/payslips/generate', { payRunId, payslips: newPayslips });
      // Write per-employee records for Dashboard + Details screens
      newPayslips.forEach(ps => {
        writePayrollRecordForEmployee(ps.employeeId, ps);
      });
    } catch (psErr) {
      console.warn('[PayrollAPI] POST payslips failed (saved locally):', psErr.message);
    }
  };

  const emailPayslip = async (payslip) => {
    try {
      // ── 1. Resolve employee ──────────────────────────────────────────────
      const employee = employees.find(
        e => e.id === payslip.employeeId ||
             e.employeeNumber === payslip.employeeNumber
      );

      const recipientEmail =
        payslip.employeeEmail ||
        employee?.contact?.email ||
        employee?.email ||
        null;

      const recipientName =
        payslip.employeeName ||
        (employee ? `${employee.firstName} ${employee.lastName}`.trim() : 'Employee');

      if (!recipientEmail) {
        Alert.alert(
          'No Email Address',
          `No email address found for ${recipientName}. Please add an email to the employee record before sending.`,
          [{ text: 'OK' }]
        );
        return;
      }

      // ── 2. Generate PDF ──────────────────────────────────────────────────
      const html = generatePayslipHTML(payslip, employee || { firstName: recipientName });
      let pdfUri = null;
      try {
        const printResult = await Print.printToFileAsync({ html });
        pdfUri = printResult?.uri || null;
      } catch (pdfErr) {
        console.warn('PDF generation failed, will send without attachment:', pdfErr);
      }

      // ── 3. Check if MailComposer is available ────────────────────────────
      const mailAvailable = await MailComposer.isAvailableAsync();

      if (mailAvailable) {
        const composeOptions = {
          subject: `Your Payslip – ${payslip.period || 'this pay period'}`,
          recipients: [recipientEmail],
          body:
            `Dear ${recipientName},

` +
            `Please find attached your payslip for ${payslip.period || 'the current period'}.

` +
            `Pay Summary:
` +
            `  Gross Pay:    £${(payslip.gross || payslip.grossPay || 0).toFixed(2)}
` +
            `  Income Tax:   £${(payslip.tax || payslip.incomeTax || 0).toFixed(2)}
` +
            `  National Ins: £${(payslip.ni || payslip.nationalInsurance || 0).toFixed(2)}
` +
            `  Pension:      £${(payslip.employeePension || payslip.pension || 0).toFixed(2)}
` +
            `  Net Pay:      £${(payslip.net || payslip.netPay || 0).toFixed(2)}

` +
            `If you have any queries, please contact the payroll team.

` +
            `Regards,
${companyInfo.name || 'Payroll Team'}`,
        };
        if (pdfUri) composeOptions.attachments = [pdfUri];

        const result = await MailComposer.composeAsync(composeOptions);

        if (result.status === 'sent' || result.status === 'saved') {
          // Mark as emailed
          const updated = payslips.map(ps =>
            ps.id === payslip.id ? { ...ps, emailed: true, emailedAt: new Date().toISOString() } : ps
          );
          setPayslips(updated);
          await AsyncStorage.setItem('payroll_payslips', JSON.stringify(updated));
          api.post(`/api/payroll/payslips/${payslip.id}/email`).catch(() => {});
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          Alert.alert('Sent', `Payslip sent to ${recipientEmail}`);
        } else if (result.status === 'cancelled') {
          // User cancelled – no error needed
        } else {
          Alert.alert('Not Sent', 'The email was not sent. Please try again.');
        }
      } else {
        // ── 4. Fallback: share PDF (or show address to copy) ────────────────
        if (pdfUri) {
          await Sharing.shareAsync(pdfUri, {
            mimeType: 'application/pdf',
            dialogTitle: `Payslip for ${recipientName}`,
            UTI: 'com.adobe.pdf',
          });
          // Still mark as emailed since user shared it
          const updated = payslips.map(ps =>
            ps.id === payslip.id ? { ...ps, emailed: true, emailedAt: new Date().toISOString() } : ps
          );
          setPayslips(updated);
          await AsyncStorage.setItem('payroll_payslips', JSON.stringify(updated));
        } else {
          Alert.alert(
            'Email Unavailable',
            `No mail app is configured on this device.

Employee email: ${recipientEmail}

Please send the payslip manually.`,
            [{ text: 'OK' }]
          );
        }
      }
    } catch (error) {
      console.error('emailPayslip error:', error);
      Alert.alert(
        'Email Failed',
        error?.message
          ? `Could not send payslip: ${error.message}`
          : 'An unexpected error occurred while sending the payslip. Please try again.'
      );
    }
  };

  // Generate payslip directly for an employee (uses latest pay run data or estimated)
  const generatePayslipForEmployee = async (employee) => {
    try {
      const empPayslips = payslips.filter(ps =>
        ps.employeeId === employee.id || ps.employeeNumber === employee.employeeNumber
      );
      if (empPayslips.length > 0) {
        // Use most recent payslip
        await downloadPayslip(empPayslips[0]);
        return;
      }
      // No payslip yet — generate estimated from salary
      const salary = employee.payroll?.salary || 0;
      const monthly = salary / 12;
      const tax = monthly * 0.2;
      const ni  = monthly > 1048 ? (monthly - 1048) * 0.12 : 0;
      const pension = monthly * 0.05;
      const net = monthly - tax - ni - pension;
      const estimated = {
        id: `est_${employee.id}`,
        period: new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        employeeId: employee.id,
        employeeName: `${employee.firstName} ${employee.lastName}`,
        employeeNumber: employee.employeeNumber,
        paymentMethod: employee.payroll?.payMethod || 'BACS',
        paymentDate: new Date().toISOString(),
        earnings: { basic: monthly, overtime: 0, bonus: 0, commission: 0, holiday: 0, sick: 0, total: monthly },
        deductions: { tax, ni, pension, studentLoan: 0, other: 0, total: tax + ni + pension },
        netPay: net,
        yearToDate: { gross: monthly, tax, ni, pension, net },
      };
      const html = generatePayslipHTML(estimated, employee);
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      await Sharing.shareAsync(uri, {
        mimeType: 'application/pdf',
        dialogTitle: `Payslip – ${employee.firstName} ${employee.lastName}`,
        UTI: 'com.adobe.pdf',
      });
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (err) {
      console.error('generatePayslipForEmployee error:', err);
      Alert.alert('Error', 'Could not generate payslip: ' + err.message);
    }
  };

  const downloadPayslip = async (payslip) => {
    try {
      const employee = employees.find(e => e.id === payslip.employeeId);
      
      // Normalise flat API payslip to expected nested structure
      const ps = payslip.earnings ? payslip : {
        ...payslip,
        earnings: {
          basic:    parseFloat(payslip.gross || payslip.base_salary || payslip.salary || 0),
          overtime: parseFloat(payslip.overtime || payslip.overtime_hours || 0),
          bonus:    parseFloat(payslip.bonus || 0),
          commission: 0, holiday: 0, sick: 0,
          total:    parseFloat(payslip.gross || payslip.total_gross || 0),
        },
        deductions: {
          tax:        parseFloat(payslip.tax || payslip.income_tax || payslip.total_tax || 0),
          ni:         parseFloat(payslip.ni  || payslip.national_insurance || payslip.total_ni || 0),
          pension:    parseFloat(payslip.pension || payslip.total_pension || payslip.employee_pension || 0),
          studentLoan: 0, other: 0,
          total:      parseFloat((payslip.tax || 0) + (payslip.ni || 0) + (payslip.pension || 0)),
        },
        netPay:     parseFloat(payslip.net || payslip.net_pay || payslip.total_net || 0),
        yearToDate: payslip.yearToDate || payslip.ytd || {
          gross: parseFloat(payslip.gross || 0),
          tax: parseFloat(payslip.tax || 0),
          ni: parseFloat(payslip.ni || 0),
          pension: parseFloat(payslip.pension || 0),
          net: parseFloat(payslip.net || 0),
        },
        period: payslip.period || payslip.pay_period || new Date(payslip.pay_date || payslip.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        paymentMethod: payslip.paymentMethod || payslip.payment_method || 'BACS',
        paymentDate: payslip.paymentDate || payslip.pay_date || new Date().toISOString(),
      };
      
      // Generate PDF
      const html = generatePayslipHTML(ps, employee);
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      
      // Share
      await Sharing.shareAsync(uri, {
        mimeType: 'application/pdf',
        dialogTitle: 'Save Payslip',
        UTI: 'com.adobe.pdf',
      });
      
      // Update status
      const updated = payslips.map(ps =>
        ps.id === payslip.id ? { ...ps, downloaded: true } : ps
      );
      setPayslips(updated);
      await AsyncStorage.setItem('payroll_payslips', JSON.stringify(updated));
      
      // API call
      api.post(`/api/payroll/payslips/${payslip.id}/download`, { format: 'pdf' }).catch(() => {});
      
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      Alert.alert('Error', 'Failed to download payslip');
    }
  };

  const generatePayslipHTML = (payslip, employee) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Payslip ${payslip.period}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { text-align: center; margin-bottom: 30px; }
          .company-name { font-size: 24px; font-weight: bold; color: #0e4e96; }
          .payslip-title { font-size: 20px; margin: 10px 0; }
          .period { color: #666; }
          .employee-details { margin: 20px 0; padding: 20px; background: #f5f5f5; border-radius: 5px; }
          .employee-name { font-size: 18px; font-weight: bold; }
          .employee-number { color: #666; }
          .section { margin: 20px 0; }
          .section-title { font-weight: bold; border-bottom: 2px solid #0e4e96; padding-bottom: 5px; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
          .total-row { font-weight: bold; background: #f5f5f5; }
          .net-pay { font-size: 18px; font-weight: bold; color: #10b981; text-align: right; margin-top: 20px; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          ${companyInfo.logo ? '<img src="' + companyInfo.logo + '" style="max-height:60px;max-width:180px;object-fit:contain;margin-bottom:8px;display:block;margin-left:auto;margin-right:auto;" alt="Company Logo" />' : ''}
          <div class="company-name">${companyInfo.name}</div>
          ${companyInfo.headquarters ? '<div class="company-address">' + companyInfo.headquarters + '</div>' : ''}
          ${companyInfo.email ? '<div class="company-contact">' + companyInfo.email + ' | ' + (companyInfo.phone || '') + '</div>' : ''}
          ${companyInfo.registrationNumber ? '<div class="company-reg">Reg: ' + companyInfo.registrationNumber + (companyInfo.vatNumber ? ' | VAT: ' + companyInfo.vatNumber : '') + '</div>' : ''}
          <div class="payslip-title">Payslip</div>
          <div class="period">${payslip.period}</div>
        </div>
        
        <div class="employee-details">
          <div class="employee-name">${employee.firstName} ${employee.lastName}</div>
          <div class="employee-number">Employee Number: ${employee.employeeNumber}</div>
          <div>NI Number: ${employee.niNumber}</div>
          <div>Tax Code: ${employee.taxCode}</div>
          <div>Department: ${employee.employment?.department}</div>
          <div>Job Title: ${employee.employment?.jobTitle}</div>
        </div>
        
        <div class="section">
          <div class="section-title">Earnings</div>
          <table>
            <tr>
              <th>Description</th>
              <th style="text-align: right;">Amount</th>
            </tr>
            <tr>
              <td>Basic Pay</td>
              <td style="text-align: right;">£${payslip.earnings.basic.toFixed(2)}</td>
            </tr>
            ${payslip.earnings.overtime > 0 ? `
            <tr>
              <td>Overtime</td>
              <td style="text-align: right;">£${payslip.earnings.overtime.toFixed(2)}</td>
            </tr>
            ` : ''}
            ${payslip.earnings.bonus > 0 ? `
            <tr>
              <td>Bonus</td>
              <td style="text-align: right;">£${payslip.earnings.bonus.toFixed(2)}</td>
            </tr>
            ` : ''}
            <tr class="total-row">
              <td>Total Earnings</td>
              <td style="text-align: right;">£${payslip.earnings.total.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Deductions</div>
          <table>
            <tr>
              <th>Description</th>
              <th style="text-align: right;">Amount</th>
            </tr>
            <tr>
              <td>Income Tax</td>
              <td style="text-align: right;">£${payslip.deductions.tax.toFixed(2)}</td>
            </tr>
            <tr>
              <td>National Insurance</td>
              <td style="text-align: right;">£${payslip.deductions.ni.toFixed(2)}</td>
            </tr>
            <tr>
              <td>Pension Contribution</td>
              <td style="text-align: right;">£${payslip.deductions.pension.toFixed(2)}</td>
            </tr>
            ${payslip.deductions.studentLoan > 0 ? `
            <tr>
              <td>Student Loan</td>
              <td style="text-align: right;">£${payslip.deductions.studentLoan.toFixed(2)}</td>
            </tr>
            ` : ''}
            <tr class="total-row">
              <td>Total Deductions</td>
              <td style="text-align: right;">£${payslip.deductions.total.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        
        <div class="net-pay">
          Net Pay: £${payslip.netPay.toFixed(2)}
        </div>
        
        <div class="section">
          <div class="section-title">Year to Date</div>
          <table>
            <tr>
              <td>Gross Pay YTD</td>
              <td style="text-align: right;">£${payslip.yearToDate.gross.toFixed(2)}</td>
            </tr>
            <tr>
              <td>Tax YTD</td>
              <td style="text-align: right;">£${payslip.yearToDate.tax.toFixed(2)}</td>
            </tr>
            <tr>
              <td>NI YTD</td>
              <td style="text-align: right;">£${payslip.yearToDate.ni.toFixed(2)}</td>
            </tr>
            <tr>
              <td>Pension YTD</td>
              <td style="text-align: right;">£${payslip.yearToDate.pension.toFixed(2)}</td>
            </tr>
            <tr>
              <td>Net Pay YTD</td>
              <td style="text-align: right;">£${payslip.yearToDate.net.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Payment Details</div>
          <table>
            <tr>
              <td>Payment Method</td>
              <td>${payslip.paymentMethod}</td>
            </tr>
            <tr>
              <td>Payment Date</td>
              <td>${new Date(payslip.paymentDate).toLocaleDateString('en-GB')}</td>
            </tr>
          </table>
        </div>
        
        <div class="payment-bank" style="margin:20px 0;padding:15px;background:#f0f7ff;border-radius:5px;">
          <div style="font-weight:bold;margin-bottom:8px;">Bank Payment Details</div>
          ${companyInfo.bankName ? '<div>Bank: ' + companyInfo.bankName + '</div>' : ''}
          ${companyInfo.bankSortCode ? '<div>Sort Code: ' + companyInfo.bankSortCode + '</div>' : ''}
          <div>Payment Method: ${payslip.paymentMethod || 'BACS'}</div>
        </div>
        <div class="footer">
          <p>This is a computer generated payslip and does not require a signature.</p>
          ${payrollSettings.payeReference ? '<p>PAYE Reference: ' + payrollSettings.payeReference + '</p>' : ''}
          <p>Generated on ${new Date().toLocaleDateString('en-GB')}</p>
        </div>
      </body>
      </html>
    `;
  };

  // ── Report Actions ──────────────────────────────────────────────────────
  const generateReport = async () => {
    try {
      setIsProcessing(true);
      setProgress(0);

      if (employees.length === 0 && payRuns.length === 0 && payslips.length === 0) {
        setIsProcessing(false);
        Alert.alert('No Data', 'Add employees and run a pay run before generating reports.');
        return;
      }

      let data = [];
      
      switch (reportForm.type) {
        case 'payroll-summary':
          data = payRuns.map(pr => ({
            Period: pr.period,
            Employees: pr.employeeCount,
            Gross: pr.totalGross,
            Tax: pr.totalTax,
            NI: pr.totalNI,
            Pension: pr.totalPension,
            Net: pr.totalNet,
          }));
          break;
          
        case 'employee-summary':
          data = employees.map(emp => ({
            'Employee No': emp.employeeNumber,
            Name: `${emp.firstName} ${emp.lastName}`,
            Department: emp.employment?.department || '',
            'Job Title': emp.employment?.jobTitle || '',
            Salary: emp.payroll?.salary || 0,
            'Start Date': emp.employment?.startDate || '',
            Status: emp.status,
          }));
          break;
          
        case 'tax-summary':
          data = employees.map(emp => ({
            'Employee No': emp.employeeNumber,
            Name: `${emp.firstName} ${emp.lastName}`,
            'Tax Code': emp.taxCode,
            'Tax YTD': payslips.filter(ps => ps.employeeNumber === emp.employeeNumber).reduce((s, ps) => s + (ps.yearToDate?.tax || 0), 0),
            'NI YTD': payslips.filter(ps => ps.employeeNumber === emp.employeeNumber).reduce((s, ps) => s + (ps.yearToDate?.ni || 0), 0),
          }));
          break;
      }
      
      // Generate based on format
      if (reportForm.format === 'pdf') {
        const html = generateReportHTML(reportForm.type, data);
        const { uri } = await Print.printToFileAsync({ html });
        
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: 'Export Report',
        });
      } else if (reportForm.format === 'excel') {
        // Generate Excel
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        
        const wbout = xlsxToBase64(wb);
        const xlsxFilename = 'payroll_report_' + reportForm.type + '_' + todayStr() + '.xlsx';
        const xlsxUri = FileSystem.documentDirectory + xlsxFilename;
        await FileSystem.writeAsStringAsync(xlsxUri, wbout, {
          encoding: 'base64',
        });
        await Sharing.shareAsync(xlsxUri, {
          mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          dialogTitle: 'Export Report',
          UTI: 'org.openxmlformats.spreadsheetml.sheet',
        });
      } else if (reportForm.format === 'csv' && Array.isArray(data) && data.length > 0) {
        const headers = Object.keys(data[0]);
        const csvRows = [
          headers.join(','),
          ...data.map(row =>
            headers.map(h => {
              const val = String(row[h] ?? '').replace(/"/g, '""');
              return val.includes(',') || val.includes('"') || val.includes('\n') ? '"' + val + '"' : val;
            }).join(',')
          ),
        ];
        const csvFilename = 'payroll_report_' + reportForm.type + '_' + todayStr() + '.csv';
        const csvUri = FileSystem.documentDirectory + csvFilename;
        await FileSystem.writeAsStringAsync(csvUri, csvRows.join('\n'), {
          encoding: 'utf8',
        });
        await Sharing.shareAsync(csvUri, {
          mimeType: 'text/csv',
          dialogTitle: 'Export Report',
          UTI: 'public.comma-separated-values-text',
        });
      }
      
      // API call
      try {
        const rptRes = await api.post('/api/payroll/reports/generate', reportForm);
        const report = rptRes.data?.data ?? rptRes.data;
        if (report?.downloadUrl) {
          Alert.alert('Report Ready', 'Your report has been generated.', [
            { text: 'Download', onPress: () => Sharing.shareAsync(report.downloadUrl) },
            { text: 'OK' },
          ]);
        }
      } catch (rptErr) {
        console.warn('[PayrollAPI] POST report failed:', rptErr.message);
      }
      
      setIsProcessing(false);
      setProgress(0);
      setShowReportGenerator(false);
      
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      setIsProcessing(false);
      Alert.alert('Error', 'Failed to generate report');
    }
  };

  const generateReportHTML = (type, data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${companyInfo.name} - Payroll Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #1a1a1a; }
          .report-header { border-bottom: 3px solid #0e4e96; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-start; }
          .company-block .company-name { font-size: 22px; font-weight: bold; color: #0e4e96; }
          .company-block .company-sub { font-size: 13px; color: #666; margin-top: 4px; }
          .report-meta { text-align: right; font-size: 13px; color: #666; }
          .report-meta .report-type { font-size: 18px; font-weight: bold; color: #0e4e96; margin-bottom: 6px; }
          h1 { color: #0e4e96; margin: 0 0 4px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 13px; }
          th { background: #0e4e96; color: white; padding: 10px 8px; text-align: left; }
          td { padding: 8px; border-bottom: 1px solid #e5e7eb; }
          tr:nth-child(even) td { background: #f9fafb; }
          .total-row td { font-weight: bold; background: #f0f7ff; border-top: 2px solid #0e4e96; }
          .report-footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #999; text-align: center; }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="company-block">
            ${companyInfo.logo ? '<img src="' + companyInfo.logo + '" style="max-height:48px;max-width:150px;object-fit:contain;margin-bottom:8px;" alt="Logo" /><br/>' : ''}
            <div class="company-name">${companyInfo.name}</div>
            <div class="company-sub">${companyInfo.headquarters || ''}</div>
            <div class="company-sub">${companyInfo.email || ''}${companyInfo.phone ? ' | ' + companyInfo.phone : ''}</div>
            ${companyInfo.registrationNumber ? '<div class="company-sub">Reg No: ' + companyInfo.registrationNumber + '</div>' : ''}
            ${companyInfo.vatNumber ? '<div class="company-sub">VAT: ' + companyInfo.vatNumber + '</div>' : ''}
          </div>
          <div class="report-meta">
            <div class="report-type">${type.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
            <div>Generated: ${new Date().toLocaleDateString('en-GB')}</div>
            <div>Tax Year: 2024/25</div>
            ${payrollSettings.payeReference ? '<div>PAYE Ref: ' + payrollSettings.payeReference + '</div>' : ''}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              ${Object.keys(data[0] || {}).map(key => '<th>' + key + '</th>').join('')}
            </tr>
          </thead>
          <tbody>
            ${data.map(row =>
              '<tr>' + Object.values(row).map(val => '<td>' + (val ?? '') + '</td>').join('') + '</tr>'
            ).join('')}
          </tbody>
        </table>

        <div class="report-footer">
          <p>${companyInfo.name} | JobEng Payroll v2.4.1 | HMRC Recognised Software | 2024/25 Tax Year</p>
        </div>
      </body>
      </html>
    `;
  };


  // ── Document Upload ──────────────────────────────────────────────────────
  const uploadEmployeeDocument = async (emp) => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled) return;
      const asset = result.assets?.[0] || result;
      if (!asset?.uri) return;

      const docEntry = {
        id:         'doc_' + Date.now(),
        name:       asset.name || 'document',
        uri:        asset.uri,
        mimeType:   asset.mimeType || 'application/octet-stream',
        size:       asset.size   || 0,
        uploadedAt: new Date().toISOString(),
      };

      const updated = employees.map(e =>
        e.id === emp.id
          ? { ...e, documents: [...(e.documents || []), docEntry] }
          : e
      );
      setEmployees(updated);
      await AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));

      // Update selectedEmployee so the modal re-renders immediately
      setSelectedEmployee(prev => prev?.id === emp.id
        ? { ...prev, documents: [...(prev.documents || []), docEntry] }
        : prev
      );

      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      Alert.alert('Uploaded', `"${docEntry.name}" has been attached to ${emp.personal?.firstName || 'this employee'}'s record.`);
    } catch (err) {
      Alert.alert('Upload Failed', err?.message || 'Could not upload document. Please try again.');
    }
  };

  const deleteEmployeeDocument = async (emp, docId) => {
    Alert.alert('Remove Document', 'Remove this document from the employee record?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: async () => {
        const updated = employees.map(e =>
          e.id === emp.id
            ? { ...e, documents: (e.documents || []).filter(d => d.id !== docId) }
            : e
        );
        setEmployees(updated);
        await AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));
        setSelectedEmployee(prev => prev?.id === emp.id
          ? { ...prev, documents: (prev.documents || []).filter(d => d.id !== docId) }
          : prev
        );
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      }},
    ]);
  };

  const openEmployeeDocument = async (doc) => {
    try {
      await Sharing.shareAsync(doc.uri, {
        mimeType: doc.mimeType,
        dialogTitle: doc.name,
        UTI: doc.mimeType,
      });
    } catch {
      Alert.alert('Cannot Open', 'This file cannot be opened on your device.');
    }
  };

  const formatDocSize = (bytes) => {
    if (!bytes || bytes === 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const docIcon = (mimeType = '') => {
    if (mimeType.includes('pdf'))   return 'file-pdf-box';
    if (mimeType.includes('image')) return 'file-image';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'file-word';
    if (mimeType.includes('sheet') || mimeType.includes('excel'))   return 'file-excel';
    return 'file-document-outline';
  };

  // ── Import / Export Functions ──────────────────────────────────────────────
  const pickImportFile = async () => {
    try {
      setImportError('');
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'text/csv',
          'text/comma-separated-values',
          '*/*',
        ],
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      const asset = result.assets?.[0] || result;
      const uri = asset.uri;
      const name = asset.name || 'import_file';

      setImportLoading(true);
      setImportFileName(name);

      let rows = [];

      const ext = name.split('.').pop()?.toLowerCase();

      if (ext === 'csv') {
        // Parse CSV
        const csvText = await FileSystem.readAsStringAsync(uri, { encoding: 'utf8' });
        const lines = csvText.trim().split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        rows = lines.slice(1).filter(l => l.trim()).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const obj = {};
          headers.forEach((h, i) => { obj[h] = values[i] || ''; });
          return obj;
        });
      } else {
        // Parse XLSX/XLS
        const base64 = await FileSystem.readAsStringAsync(uri, { encoding: 'base64' });
        const workbook = XLSX.read(base64, { type: 'base64' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      }

      if (rows.length === 0) {
        setImportError('No data rows found in file. Ensure file has a header row and data rows.');
        setImportLoading(false);
        return;
      }

      // Normalize rows to employee preview format
      const normalized = rows.map((row, idx) => {
        // Try multiple common column name variants
        const get = (...keys) => {
          for (const k of keys) {
            const found = Object.keys(row).find(rk => rk.toLowerCase().replace(/[^a-z]/g, '') === k.toLowerCase().replace(/[^a-z]/g, ''));
            if (found && row[found]) return String(row[found]).trim();
          }
          return '';
        };
        return {
          _rowIndex: idx,
          _valid: true,
          firstName: get('firstname', 'first name', 'fname', 'forename', 'given name'),
          lastName: get('lastname', 'last name', 'lname', 'surname', 'family name'),
          email: get('email', 'email address', 'workemail'),
          department: get('department', 'dept'),
          jobTitle: get('jobtitle', 'job title', 'title', 'position', 'role'),
          salary: get('salary', 'annual salary', 'wage', 'pay'),
          niNumber: get('ninumber', 'ni number', 'national insurance', 'ni'),
          taxCode: get('taxcode', 'tax code'),
          startDate: get('startdate', 'start date', 'employment date', 'joined'),
          payFrequency: get('payfrequency', 'pay frequency', 'frequency') || 'monthly',
          contractType: get('contracttype', 'contract type', 'contract') || 'permanent',
        };
      }).filter(r => r.firstName || r.lastName || r.email);

      if (normalized.length === 0) {
        setImportError('Could not map any rows. Check column headers match: First Name, Last Name, Email, Department, Job Title, Salary, NI Number, Tax Code, Start Date.');
        setImportLoading(false);
        return;
      }

      setImportData(normalized);
      setSelectedImportRows(normalized.map(r => r._rowIndex));
      setImportLoading(false);
      setShowImportModal(true);
    } catch (err) {
      setImportLoading(false);
      setImportError('Failed to read file: ' + (err.message || 'Unknown error'));
      Alert.alert('Import Error', 'Could not read the selected file. Please ensure it is a valid CSV or Excel file.');
    }
  };

  const confirmImport = async () => {
    try {
      const toImport = importData.filter(r => selectedImportRows.includes(r._rowIndex));
      if (toImport.length === 0) {
        Alert.alert('No Rows Selected', 'Please select at least one row to import.');
        return;
      }

      const newEmployees = toImport.map((row, i) => {
        const nextNum = employees.length + i + 1;
        return {
          id: makeId('emp'),
          employeeNumber: `EMP${String(nextNum).padStart(3, '0')}`,
          title: 'Mr',
          firstName: row.firstName || 'Unknown',
          lastName: row.lastName || 'Employee',
          preferredName: row.firstName || 'Unknown',
          gender: 'Unknown',
          dateOfBirth: '',
          nationality: 'British',
          niNumber: row.niNumber || '',
          taxCode: row.taxCode || '1257L',
          taxRegion: 'UK',
          niCategory: 'A',
          studentLoan: null,
          postgraduateLoan: false,
          contact: {
            email: row.email || '',
            phone: '',
            mobile: '',
            address: { line1: '', line2: '', city: '', county: '', postcode: '', country: 'United Kingdom' },
          },
          employment: {
            startDate: row.startDate || todayStr(),
            endDate: null,
            probationEndDate: '',
            noticePeriod: 30,
            contractType: row.contractType || 'permanent',
            employmentType: 'full-time',
            workingHours: 37.5,
            department: row.department || 'General',
            jobTitle: row.jobTitle || 'Employee',
            jobGrade: '',
            costCenter: '',
            project: null,
            location: 'Main Office',
            holidayEntitlement: 28,
            holidayRemaining: 28,
            sickLeaveEntitlement: 10,
            sickLeaveTaken: 0,
          },
          payroll: {
            payFrequency: row.payFrequency || 'monthly',
            payMethod: 'BACS',
            paymentDate: 28,
            bankDetails: { accountName: `${row.firstName} ${row.lastName}`, sortCode: '', accountNumber: '', bankName: '' },
            salary: parseFloat(String(row.salary).replace(/[^0-9.]/g, '')) || 0,
            hourlyRate: null,
            pension: { enrolled: true, scheme: 'NEST', employeeContribution: 0.05, employerContribution: 0.03, optOutDate: null, joiningDate: todayStr() },
            deductions: [],
            benefits: [],
            taxHistory: [],
            payHistory: [],
          },
          emergencyContacts: [],
          documents: [],
          status: 'active',
          createdDate: nowISO(),
          modifiedDate: nowISO(),
          createdBy: 'Import',
          modifiedBy: 'Import',
        };
      });

      const updated = [...employees, ...newEmployees];
      setEmployees(updated);
      await AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));
      try {
        const impRes = await api.post('/api/payroll/employees/import', { employees: newEmployees });
        const result = impRes.data?.data ?? impRes.data;
        const added   = result?.added   ?? newEmployees.length;
        const skipped = result?.skipped ?? 0;
        const failed  = result?.failed  ?? 0;
        Alert.alert(
          'Import Complete',
          `${added} imported, ${skipped} skipped, ${failed} failed.`
        );
        // Sync all to payroll
        PayrollIntegrationAPI.syncAll().catch(() => {});
      } catch (impErr) {
        console.warn('[PayrollAPI] POST import failed (saved locally):', impErr.message);
      }

      setShowImportModal(false);
      setImportData([]);
      setSelectedImportRows([]);
      setImportFileName('');

      Alert.alert('Import Successful', `${newEmployees.length} employee${newEmployees.length !== 1 ? 's' : ''} imported successfully.`);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (err) {
      Alert.alert('Import Failed', 'Could not save imported employees: ' + (err.message || ''));
    }
  };

  const exportEmployees = async (format = 'xlsx') => {
    try {
      setExportLoading(true);

      const safeEmps = (employees || []).filter(e => e && typeof e === 'object');
      if (safeEmps.length === 0 && format !== 'xlsx') {
        setExportLoading(false);
        Alert.alert('Nothing to Export', 'No employee data found to export.');
        return;
      }
      const data = safeEmps.map(e => ({
        'Employee No': e.employeeNumber,
        'First Name': e.firstName,
        'Last Name': e.lastName,
        'Email': e.contact?.email || '',
        'Phone': e.contact?.phone || '',
        'Department': e.employment?.department || '',
        'Job Title': e.employment?.jobTitle || '',
        'Job Grade': e.employment?.jobGrade || '',
        'Contract Type': e.employment?.contractType || '',
        'Employment Type': e.employment?.employmentType || '',
        'Working Hours': e.employment?.workingHours || '',
        'Start Date': e.employment?.startDate || '',
        'Annual Salary': e.payroll?.salary || 0,
        'Pay Frequency': e.payroll?.payFrequency || '',
        'Pay Method': e.payroll?.payMethod || '',
        'NI Number': e.niNumber || '',
        'Tax Code': e.taxCode || '',
        'NI Category': e.niCategory || '',
        'Student Loan': e.studentLoan || '',
        'Pension Enrolled': e.payroll?.pension?.enrolled ? 'Yes' : 'No',
        'Pension Scheme': e.payroll?.pension?.scheme || '',
        'Employee Pension %': Math.round((e.payroll?.pension?.employeeContribution || 0) * 100),
        'Employer Pension %': Math.round((e.payroll?.pension?.employerContribution || 0) * 100),
        'Location': e.employment?.location || '',
        'Cost Centre': e.employment?.costCenter || '',
        'Status': e.status || '',
        'Date of Birth': e.dateOfBirth || '',
        'Nationality': e.nationality || '',
      }));

      let uri;
      let mimeType;
      let filename;

      if (format === 'csv') {
        // Build CSV string
        if (data.length === 0) {
          setExportLoading(false);
          Alert.alert('Nothing to Export', 'No data to export.');
          return;
        }
        const headers = Object.keys(data[0]);
        const csvRows = [
          headers.join(','),
          ...data.map(row =>
            headers.map(h => {
              const val = String(row[h] ?? '').replace(/"/g, '""');
              return val.includes(',') || val.includes('"') || val.includes('\n') ? '"' + val + '"' : val;
            }).join(',')
          ),
        ];
        const csvContent = csvRows.join('\n');
        filename = 'employees_export.csv';
        uri = FileSystem.documentDirectory + filename;
        await FileSystem.writeAsStringAsync(uri, csvContent, { encoding: 'utf8' });
        mimeType = 'text/csv';
      } else {
        // XLSX
        const wb = XLSX.utils.book_new();
        // Main sheet
        const ws = XLSX.utils.json_to_sheet(data);
        // Column widths
        if (data.length > 0) ws['!cols'] = Object.keys(data[0]).map(() => ({ wch: 18 }));
        XLSX.utils.book_append_sheet(wb, ws, 'Employees');

        // Pay Runs sheet
        if (payRuns.length > 0) {
          const prData = payRuns.map(pr => ({
            'Reference': pr.reference,
            'Period': pr.period,
            'Start Date': pr.startDate,
            'End Date': pr.endDate,
            'Payment Date': pr.paymentDate,
            'Frequency': pr.frequency,
            'Status': pr.status,
            'Employees': pr.employeeCount,
            'Total Gross': pr.totalGross,
            'Total Tax': pr.totalTax,
            'Total NI': pr.totalNI,
            'Total Pension': pr.totalPension,
            'Total Net': pr.totalNet,
          }));
          const wsPayRuns = XLSX.utils.json_to_sheet(prData);
          XLSX.utils.book_append_sheet(wb, wsPayRuns, 'Pay Runs');
        }

        // Payslips sheet
        if (payslips.length > 0) {
          const psData = payslips.map(ps => ({
            'Employee': ps.employeeName,
            'Employee No': ps.employeeNumber,
            'Period': ps.period,
            'Issue Date': ps.issueDate,
            'Payment Date': ps.paymentDate,
            'Basic Pay': ps.earnings?.basic || 0,
            'Total Earnings': ps.earnings?.total || 0,
            'Tax': ps.deductions?.tax || 0,
            'NI': ps.deductions?.ni || 0,
            'Pension': ps.deductions?.pension || 0,
            'Total Deductions': ps.deductions?.total || 0,
            'Net Pay': ps.netPay || 0,
            'Status': ps.status,
          }));
          const wsPayslips = XLSX.utils.json_to_sheet(psData);
          XLSX.utils.book_append_sheet(wb, wsPayslips, 'Payslips');
        }

        const wbout = xlsxToBase64(wb);
        filename = 'payroll_export_' + todayStr() + '.xlsx';
        uri = FileSystem.documentDirectory + filename;
        await FileSystem.writeAsStringAsync(uri, wbout, { encoding: 'base64' });
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }

      setExportLoading(false);

      await Sharing.shareAsync(uri, {
        mimeType,
        dialogTitle: 'Export Payroll Data',
        UTI: format === 'csv' ? 'public.comma-separated-values-text' : 'org.openxmlformats.spreadsheetml.sheet',
      });

      api.post('/api/payroll/employees/export', { format, count: data.length }).catch(() => {});
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (err) {
      setExportLoading(false);
      Alert.alert('Export Failed', 'Could not export data: ' + (err.message || 'Unknown error. Ensure storage permissions are granted.'));
    }
  };

  // ── XLSX → base64 — battle-tested for Hermes/React Native ──────────────
  const xlsxToBase64 = (wb) => {
    // Step 1: get raw bytes as Uint8Array (most reliable XLSX output in RN)
    const uint8 = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });

    // Step 2: encode Uint8Array → base64 without Buffer or btoa
    // Processes 3 bytes → 4 base64 chars, handles padding correctly
    const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    const len = uint8.length;
    let b64 = '';
    let i = 0;

    // Process full 3-byte groups
    while (i + 2 < len) {
      const a = uint8[i++];
      const b = uint8[i++];
      const c = uint8[i++];
      b64 += CHARS[a >> 2]
           + CHARS[((a & 0x03) << 4) | (b >> 4)]
           + CHARS[((b & 0x0F) << 2) | (c >> 6)]
           + CHARS[c & 0x3F];
    }

    // Handle remaining 1 or 2 bytes
    if (i < len) {
      const a = uint8[i++];
      const b = i < len ? uint8[i] : 0;
      b64 += CHARS[a >> 2]
           + CHARS[((a & 0x03) << 4) | (b >> 4)]
           + (i < len ? CHARS[(b & 0x0F) << 2] : '=')
           + '=';
    }

    return b64;
  };

  // ── Tax Calculator ──────────────────────────────────────────────────────
  const [taxCalculator, setTaxCalculator] = useState({
    annualSalary: 50000,
    taxCode: '1257L',
    pensionContribution: 5,
    studentLoanPlan: 'plan2',
    results: null,
  });

  const calculateTaxResults = () => {
    const { annualSalary, taxCode, pensionContribution, studentLoanPlan } = taxCalculator;
    
    const monthlySalary = annualSalary / 12;
    const pensionAmount = annualSalary * (pensionContribution / 100);
    const taxableSalary = annualSalary - pensionAmount;
    
    // Calculate tax
    let tax = 0;
    let remaining = taxableSalary;
    
    for (const band of taxBands) {
      if (remaining <= 0) break;
      const bandAmount = Math.min(remaining, band.limit - band.start);
      tax += bandAmount * band.rate;
      remaining -= bandAmount;
    }
    
    // Calculate NI
    const ni = calculateNI(monthlySalary, 'A', niBands) * 12;
    
    // Calculate student loan
    const studentLoan = calculateStudentLoan(annualSalary, studentLoanPlan);
    
    // Take home
    const netAnnual = annualSalary - tax - ni - pensionAmount - studentLoan;
    const netMonthly = netAnnual / 12;
    
    setTaxCalculator(prev => ({
      ...prev,
      results: {
        annualSalary,
        monthlySalary,
        tax,
        taxRate: (tax / annualSalary) * 100,
        ni,
        niRate: (ni / annualSalary) * 100,
        pensionAmount,
        pensionRate: pensionContribution,
        studentLoan,
        netAnnual,
        netMonthly,
        effectiveRate: ((annualSalary - netAnnual) / annualSalary) * 100,
      },
    }));
  };

  // ── Render Methods ──────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={C.primaryGradient} style={styles.header}>
      <View style={styles.headerRow}>
        <TouchableOpacity style={styles.headerBtn} onPress={() => navigation?.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>{companyInfo.name !== 'Your Company' ? companyInfo.name : 'Payroll Management'}</Text>
          <View style={styles.headerSubRow}>
            <Text style={styles.headerSub}>{analytics.activeEmployees} active employees{companyInfo.name !== 'Your Company' ? ' · Payroll' : ''}</Text>
            {syncStatus !== 'synced' && (
              <View style={[styles.syncBadge, { backgroundColor: syncStatus === 'syncing' ? C.info + '20' : syncStatus === 'offline' ? C.warning + '20' : C.danger + '20' }]}>
                <MaterialCommunityIcons name={syncStatus === 'syncing' ? 'sync' : syncStatus === 'offline' ? 'wifi-off' : 'alert'} size={12} color={syncStatus === 'syncing' ? C.info : syncStatus === 'offline' ? C.warning : C.danger} />
                <Text style={[styles.syncBadgeText, { color: syncStatus === 'syncing' ? C.info : syncStatus === 'offline' ? C.warning : C.danger }]}>
                  <Text>{syncStatus === 'syncing' ? 'Syncing' : syncStatus === 'offline' ? 'Offline' : 'Error'}</Text>
                </Text>
              </View>
            )}
          </View>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerBtn} onPress={() => setShowSearch(!showSearch)}>
            <Ionicons name={showSearch ? 'close' : 'search'} size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerBtn} onPress={() => setShowTaxCalculator(true)}>
            <MaterialCommunityIcons name="calculator-variant" size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.navigate('CompanyProfile')} accessibilityLabel="Company Profile">
            <MaterialCommunityIcons name="office-building-outline" size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerBtn} onPress={() => setShowSettings(true)}>
            <Ionicons name="settings-outline" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.statsBar}>
        <TouchableOpacity style={styles.statItem} onPress={() => setActiveTab('employees')}>
          <MaterialCommunityIcons name="account-group" size={16} color={C.successLight} />
          <Text style={styles.statValue}>{analytics.activeEmployees}</Text>
          <Text style={styles.statLabel}>Active</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.statItem} onPress={() => setActiveTab('payruns')}>
          <MaterialCommunityIcons name="cash-multiple" size={16} color={C.infoLight} />
          <Text style={styles.statValue}>{payRuns.length}</Text>
          <Text style={styles.statLabel}>Pay Runs</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.statItem} onPress={() => setActiveTab('analytics')}>
          <MaterialCommunityIcons name="currency-gbp" size={16} color={C.goldLight} />
          <Text style={styles.statValue}>{formatCurrency(analytics.totalMonthlySalary)}</Text>
          <Text style={styles.statLabel}>Monthly</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.statItem} onPress={() => setActiveTab('payslips')}>
          <MaterialCommunityIcons name="file-document-multiple" size={16} color={C.purpleLight} />
          <Text style={styles.statValue}>{payslips.length}</Text>
          <Text style={styles.statLabel}>Payslips</Text>
        </TouchableOpacity>
      </View>
      {showSearch && (
        <View style={styles.searchBar}>
          <Ionicons name="search" size={18} color={C.light} />
          <TextInput
            ref={searchInputRef}
            style={styles.searchInput}
            placeholder="Search employees, pay runs..."
            placeholderTextColor={C.light}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoFocus
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={18} color={C.light} />
            </TouchableOpacity>
          ) : null}
        </View>
      )}
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabsScroll}>
        {[
          { id: 'employees', label: 'Employees', icon: 'account-group', count: employees.length },
          { id: 'payruns', label: 'Pay Runs', icon: 'cash-multiple', count: payRuns.length },
          { id: 'payslips', label: 'Payslips', icon: 'file-document', count: payslips.length },
          { id: 'reports', label: 'Reports', icon: 'file-chart', count: null },
          { id: 'analytics', label: 'Analytics', icon: 'chart-bar', count: null },
          { id: 'pension', label: 'Pension', icon: 'piggy-bank', count: null },
          { id: 'compliance', label: 'Compliance', icon: 'shield-check', count: null },
          { id: 'audit', label: 'Audit', icon: 'history', count: null },
        ].map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.tabActive]}
            onPress={() => { setActiveTab(tab.id); Haptics.selectionAsync(); }}
          >
            <MaterialCommunityIcons name={tab.icon} size={16} color={activeTab === tab.id ? C.primary : C.mid} />
            <Text style={[styles.tabText, activeTab === tab.id && styles.tabTextActive]}>{tab.label}</Text>
            {tab.count !== null && tab.count > 0 && (
              <View style={[styles.tabBadge, activeTab === tab.id && styles.tabBadgeActive]}>
                <Text style={[styles.tabBadgeText, activeTab === tab.id && { color: C.primary }]}>{tab.count}</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderEmployeesTab = () => (
    <>
      <View style={styles.actionBar}>
        <TouchableOpacity style={styles.createBtn} onPress={() => { AsyncStorage.getItem('company_id').catch(() => null).then(cid => {
                  const cbKey = `payroll_save_${Date.now()}`;
                  registerCallback(cbKey, (newEmp) => {
                    const payrollEmp = {
                      ...newEmp,
                      payroll: {
                        salary: newEmp.salary || 0,
                        payFrequency: 'monthly',
                        payMethod: 'BACS',
                        paymentDate: 28,
                        pension: { enrolled: true, employeeContribution: 5, employerContribution: 3 },
                      },
                      taxCode: newEmp.taxCode || '1257L',
                      niCategory: 'A',
                    };
                    setEmployees(prev => [payrollEmp, ...prev]);
                  });
                  navigation.navigate('AddEmployeeScreen', {
                    companyId: cid,
                    existingCount: employees.length,
                    callbackKey: cbKey,
                  });
                }); }}>
          <LinearGradient colors={C.primaryGradient} style={styles.createBtnGrad}>
            <Ionicons name="add" size={18} color="#fff" />
            <Text style={styles.createBtnText}>New Employee</Text>
          </LinearGradient>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.importBtn, importLoading && { opacity: 0.5 }]}
          onPress={pickImportFile}
          disabled={importLoading}
        >
          {importLoading
            ? <ActivityIndicator size="small" color={C.primary} />
            : <MaterialCommunityIcons name="file-import" size={18} color={C.primary} />
          }
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.exportBtn, exportLoading && { opacity: 0.5 }]}
          disabled={exportLoading}
          onPress={() => Alert.alert(
            'Export Employees',
            'Choose export format',
            [
              { text: 'Excel (.xlsx)', onPress: () => exportEmployees('xlsx') },
              { text: 'CSV (.csv)', onPress: () => exportEmployees('csv') },
              { text: 'Cancel', style: 'cancel' },
            ]
          )}
        >
          {exportLoading
            ? <ActivityIndicator size="small" color={C.primary} />
            : <MaterialCommunityIcons name="file-export" size={18} color={C.primary} />
          }
        </TouchableOpacity>
      </View>
      <View style={styles.resultsBar}>
        <Text style={styles.resultsCount}>{filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''}</Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {['active', 'on-leave', 'terminated'].map(s => (
            <TouchableOpacity key={s} onPress={() => setSearchQuery(s)} style={[styles.filterChip, searchQuery === s && styles.filterChipActive]}>
              <Text style={[styles.filterChipText, searchQuery === s && { color: C.primary }]}>{s}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      <FlatList
        data={filteredEmployees}
        keyExtractor={item => item.id}
        renderItem={({ item }) => (
          <EmployeeCard
            employee={item}
            onPress={(emp) => { setSelectedEmployee(emp); setShowEmployeeDetail(true); }}
            onLongPress={(emp) => { setSelectedEmployee(emp); setEmployeeForm(emp); setShowEmployeeForm(true); }}
            onPayslip={(emp) => generatePayslipForEmployee(emp)}
          />
        )}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await new Promise(r => setTimeout(r, 1000)); setRefreshing(false); }} colors={[C.primary]} />}
        ListEmptyComponent={() => (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="account-off" size={48} color={C.light} />
            <Text style={styles.emptyText}>No employees found</Text>
            <TouchableOpacity onPress={() => { AsyncStorage.getItem('company_id').catch(() => null).then(cid => {
                  const cbKey = `payroll_save_${Date.now()}`;
                  registerCallback(cbKey, (newEmp) => {
                    const payrollEmp = {
                      ...newEmp,
                      payroll: {
                        salary: newEmp.salary || 0,
                        payFrequency: 'monthly',
                        payMethod: 'BACS',
                        paymentDate: 28,
                        pension: { enrolled: true, employeeContribution: 5, employerContribution: 3 },
                      },
                      taxCode: newEmp.taxCode || '1257L',
                      niCategory: 'A',
                    };
                    setEmployees(prev => [payrollEmp, ...prev]);
                  });
                  navigation.navigate('AddEmployeeScreen', {
                    companyId: cid,
                    existingCount: employees.length,
                    callbackKey: cbKey,
                  });
                }); }} style={styles.emptyBtn}>
              <Text style={styles.emptyBtnText}>Add Employee</Text>
            </TouchableOpacity>
          </View>
        )}
      />
    </>
  );

  const renderPayRunsTab = () => (
    <>
      <View style={styles.actionBar}>
        <TouchableOpacity style={styles.createBtn} onPress={() => {
          setPayRunForm({ startDate: todayStr(), endDate: todayStr(), paymentDate: todayStr(), frequency: 'monthly' });
          setShowPayRunForm(true);
        }}>
          <LinearGradient colors={C.primaryGradient} style={styles.createBtnGrad}>
            <Ionicons name="add" size={18} color="#fff" />
            <Text style={styles.createBtnText}>New Pay Run</Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
      <View style={styles.resultsBar}>
        <Text style={styles.resultsCount}>{payRuns.length} pay run{payRuns.length !== 1 ? 's' : ''}</Text>
      </View>
      <FlatList
        data={payRuns}
        keyExtractor={item => item.id}
        renderItem={({ item }) => (
          <PayRunCard payRun={item} onPress={(pr) => { setSelectedPayRun(pr); setShowPayRunDetail(true); }} />
        )}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={() => (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="cash-remove" size={48} color={C.light} />
            <Text style={styles.emptyText}>No pay runs yet</Text>
          </View>
        )}
      />
    </>
  );

  const renderPayslipsTab = () => (
    <>
      <View style={styles.actionBar}>
        <TouchableOpacity style={styles.createBtn} onPress={async () => {
          if (payslips.length === 0) {
            Alert.alert('No Payslips', 'Process a pay run first to generate payslips.');
            return;
          }
          Alert.alert(
            'Email All Payslips',
            `Send ${payslips.length} payslip${payslips.length !== 1 ? 's' : ''} to employees?`,
            [
              { text: 'Cancel', style: 'cancel' },
              {
                text: 'Send All',
                onPress: async () => {
                  setIsProcessing(true);
                  let sent = 0;
                  for (const ps of payslips) {
                    try { await emailPayslip(ps); sent++; } catch {}
                    setProgress(Math.round((sent / payslips.length) * 100));
                  }
                  setIsProcessing(false);
                  setProgress(0);
                  Alert.alert('Done', sent + ' payslip' + (sent !== 1 ? 's' : '') + ' emailed.');
                  Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
                },
              },
            ]
          );
        }}>
          <LinearGradient colors={C.primaryGradient} style={styles.createBtnGrad}>
            <MaterialCommunityIcons name="email-multiple-outline" size={18} color="#fff" />
            <Text style={styles.createBtnText}>Email All</Text>
          </LinearGradient>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.exportBtn, exportLoading && { opacity: 0.5 }]}
          disabled={exportLoading}
          onPress={() => exportEmployees('xlsx')}
        >
          {exportLoading
            ? <ActivityIndicator size="small" color={C.primary} />
            : <MaterialCommunityIcons name="file-export" size={18} color={C.primary} />
          }
        </TouchableOpacity>
      </View>
      <View style={styles.resultsBar}>
        <Text style={styles.resultsCount}>{payslips.length} payslip{payslips.length !== 1 ? 's' : ''}</Text>
      </View>
      <FlatList
        data={payslips}
        keyExtractor={item => item.id}
        renderItem={({ item }) => {
          const emp = employees.find(e => e.id === item.employeeId);
          return (
            <TouchableOpacity style={styles.payslipCard} onPress={() => { setSelectedPayslip(item); setShowPayslipDetail(true); }} activeOpacity={0.7}>
              <View style={styles.payslipAvatar}>
                <Text style={styles.payslipAvatarText}>{item.employeeName?.split(' ').map(n => n[0]).join('') || '?'}</Text>
              </View>
              <View style={styles.payslipInfo}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Text style={styles.payslipName}>{item.employeeName}</Text>
                  <Text style={styles.payslipAmount}>{formatCurrency(item.netPay)}</Text>
                </View>
                <Text style={styles.payslipPeriod}>{item.period} • {item.employeeNumber}</Text>
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
                  <View style={[styles.payslipBadge, { backgroundColor: item.emailed ? C.success + '20' : C.light + '20' }]}>
                    <MaterialCommunityIcons name="email-check" size={10} color={item.emailed ? C.success : C.light} />
                    <Text style={[styles.payslipBadgeText, { color: item.emailed ? C.success : C.light }]}>Emailed</Text>
                  </View>
                  <View style={[styles.payslipBadge, { backgroundColor: item.downloaded ? C.info + '20' : C.light + '20' }]}>
                    <MaterialCommunityIcons name="download" size={10} color={item.downloaded ? C.info : C.light} />
                    <Text style={[styles.payslipBadgeText, { color: item.downloaded ? C.info : C.light }]}>Downloaded</Text>
                  </View>
                  <View style={[styles.payslipBadge, { backgroundColor: C.success + '20' }]}>
                    <Text style={[styles.payslipBadgeText, { color: C.success }]}>{item.status?.toUpperCase()}</Text>
                  </View>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={18} color={C.light} />
            </TouchableOpacity>
          );
        }}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={() => (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="file-document-outline" size={48} color={C.light} />
            <Text style={styles.emptyText}>No payslips yet</Text>
            <Text style={styles.emptySubText}>{'Go to Pay Runs tab → open a draft pay run → tap Process to generate payslips automatically'}</Text>
            <TouchableOpacity onPress={() => setActiveTab('payruns')} style={styles.emptyBtn}>
              <Text style={styles.emptyBtnText}>Go to Pay Runs</Text>
            </TouchableOpacity>
          </View>
        )}
      />
    </>
  );

  const renderReportsTab = () => {
    const reportTypes = [
      { id: 'payroll-summary', label: 'Payroll Summary', icon: 'file-chart', desc: 'Monthly payroll overview with totals', color: C.primary },
      { id: 'employee-summary', label: 'Employee Report', icon: 'account-details', desc: 'Full employee listing with salary data', color: C.info },
      { id: 'tax-summary', label: 'Tax Report', icon: 'bank', desc: 'Tax & NI deductions summary for HMRC', color: C.warning },
      { id: 'pension-report', label: 'Pension Report', icon: 'piggy-bank', desc: 'Auto-enrolment contributions report', color: C.success },
      { id: 'p60', label: 'P60 Reports', icon: 'file-certificate', desc: 'Annual end-of-year certificates', color: C.purple },
      { id: 'p11d', label: 'P11D Reports', icon: 'briefcase-variant', desc: 'Benefits in kind reporting', color: C.accent },
      { id: 'payrun-detail', label: 'Pay Run Detail', icon: 'cash-multiple', desc: 'Detailed breakdown per pay run', color: C.gold },
      { id: 'cost-centre', label: 'Cost Centre Report', icon: 'office-building', desc: 'Payroll costs by department/cost centre', color: C.dangerLight },
    ];
    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: HPAD, paddingBottom: 100 }}>
        <Text style={styles.sectionLabel}>Select Report Type</Text>
        <View style={styles.reportGrid}>
          {reportTypes.map(rt => (
            <TouchableOpacity key={rt.id} style={styles.reportCard} onPress={() => {
              setReportForm(prev => ({ ...prev, type: rt.id }));
              setShowReportGenerator(true);
            }} activeOpacity={0.7}>
              <View style={[styles.reportIcon, { backgroundColor: rt.color + '18' }]}>
                <MaterialCommunityIcons name={rt.icon} size={28} color={rt.color} />
              </View>
              <Text style={styles.reportCardTitle}>{rt.label}</Text>
              <Text style={styles.reportCardDesc}>{rt.desc}</Text>
              <View style={[styles.reportChip, { backgroundColor: rt.color + '15' }]}>
                <Text style={[styles.reportChipText, { color: rt.color }]}>Generate</Text>
                <Ionicons name="arrow-forward" size={12} color={rt.color} />
              </View>
            </TouchableOpacity>
          ))}
        </View>
        <Text style={styles.sectionLabel}>Recent Reports</Text>
        <View style={styles.recentReports}>
          {[
            { name: 'Payroll Summary - Dec 2024', date: '2024-12-20', type: 'PDF', size: '1.2 MB' },
            { name: 'Employee Report Q4 2024', date: '2024-12-15', type: 'Excel', size: '456 KB' },
            { name: 'P60 Reports 2023-24', date: '2024-05-31', type: 'PDF Bundle', size: '3.8 MB' },
          ].map((r, i) => (
            <TouchableOpacity key={i} style={styles.recentReportItem} onPress={() => Alert.alert('Open', `Opening ${r.name}`)}>
              <MaterialCommunityIcons name="file-pdf-box" size={32} color={C.danger} />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={styles.recentReportName}>{r.name}</Text>
                <Text style={styles.recentReportMeta}>{r.type} • {r.size} • {formatDate(r.date)}</Text>
              </View>
              <TouchableOpacity onPress={() => Alert.alert('Share', 'Sharing report...')}>
                <MaterialCommunityIcons name="share-variant" size={20} color={C.mid} />
              </TouchableOpacity>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    );
  };

  const renderAnalyticsTab = () => {
    const deptData = Object.entries(analytics.departmentBreakdown || {});
    const maxSalary = Math.max(...deptData.map(([, d]) => d.salary), 1);
    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: HPAD, paddingBottom: 100 }}>
        {/* KPI Cards */}
        <Text style={styles.sectionLabel}>Key Metrics</Text>
        <View style={styles.kpiGrid}>
          {[
            { label: 'Annual Payroll', value: formatCurrency(analytics.totalAnnualSalary), icon: 'currency-gbp', color: C.primary, sub: 'Total annual cost' },
            { label: 'Monthly Cost', value: formatCurrency(analytics.totalMonthlySalary), icon: 'calendar-month', color: C.info, sub: 'This month' },
            { label: 'Avg Salary', value: formatCurrency(analytics.averageSalary), icon: 'chart-bell-curve', color: C.success, sub: 'Per employee' },
            { label: 'Tax YTD', value: formatCurrency(analytics.taxCollectedYTD), icon: 'bank', color: C.warning, sub: 'PAYE collected' },
            { label: 'NI YTD', value: formatCurrency(analytics.niCollectedYTD), icon: 'shield-account', color: C.accent, sub: 'Employee NI' },
            { label: 'Pension YTD', value: formatCurrency(analytics.pensionCollectedYTD), icon: 'piggy-bank', color: C.purple, sub: 'Contributions' },
          ].map((kpi, i) => (
            <View key={i} style={[styles.kpiCard, { borderLeftColor: kpi.color, borderLeftWidth: 3 }]}>
              <View style={[styles.kpiIcon, { backgroundColor: kpi.color + '18' }]}>
                <MaterialCommunityIcons name={kpi.icon} size={20} color={kpi.color} />
              </View>
              <Text style={styles.kpiValue}>{kpi.value}</Text>
              <Text style={styles.kpiLabel}>{kpi.label}</Text>
              <Text style={styles.kpiSub}>{kpi.sub}</Text>
            </View>
          ))}
        </View>

        {/* Headcount */}
        <Text style={styles.sectionLabel}>Headcount Breakdown</Text>
        <View style={styles.analyticsCard}>
          {[
            { label: 'Active', count: analytics.activeEmployees, color: C.success, total: analytics.totalEmployees },
            { label: 'On Leave', count: analytics.onLeave, color: C.info, total: analytics.totalEmployees },
            { label: 'Terminated', count: analytics.terminated, color: C.danger, total: analytics.totalEmployees },
          ].map((h, i) => (
            <View key={i} style={styles.headcountRow}>
              <View style={[styles.headcountDot, { backgroundColor: h.color }]} />
              <Text style={styles.headcountLabel}>{h.label}</Text>
              <View style={styles.headcountBar}>
                <View style={[styles.headcountFill, { width: `${h.total > 0 ? (h.count / h.total) * 100 : 0}%`, backgroundColor: h.color }]} />
              </View>
              <Text style={styles.headcountCount}>{h.count}</Text>
            </View>
          ))}
        </View>

        {/* Department Breakdown */}
        <Text style={styles.sectionLabel}>Department Costs</Text>
        <View style={styles.analyticsCard}>
          {deptData.length === 0 ? (
            <Text style={styles.emptyText}>No department data</Text>
          ) : deptData.map(([dept, data], i) => (
            <View key={i} style={styles.deptRow}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={styles.deptName}>{dept}</Text>
                  <Text style={styles.deptSalary}>{formatCurrency(data.salary)}</Text>
                </View>
                <View style={styles.deptBarBg}>
                  <View style={[styles.deptBarFill, { width: `${(data.salary / maxSalary) * 100}%`, backgroundColor: [C.primary, C.info, C.success, C.warning, C.purple, C.accent][i % 6] }]} />
                </View>
                <Text style={styles.deptCount}>{data.count} employee{data.count !== 1 ? 's' : ''}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Payroll Trend */}
        <Text style={styles.sectionLabel}>Monthly Payroll Trend</Text>
        <View style={styles.analyticsCard}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 8, height: 120, paddingTop: 8 }}>
            {payRuns.filter(pr => pr.status === 'completed').slice(-6).map((pr, i) => {
              const maxGross = Math.max(...payRuns.map(p => p.totalGross), 1);
              const barH = Math.max((pr.totalGross / maxGross) * 100, 4);
              return (
                <View key={i} style={{ flex: 1, alignItems: 'center' }}>
                  <Text style={{ fontSize: 8, color: C.light, marginBottom: 2 }}>{formatCurrency(pr.totalGross, 'GBP').replace('£', '').split('.')[0]}k</Text>
                  <View style={{ flex: 1, justifyContent: 'flex-end', width: '100%' }}>
                    <View style={{ height: `${barH}%`, backgroundColor: C.primary + 'cc', borderRadius: 4 }} />
                  </View>
                  <Text style={{ fontSize: 8, color: C.light, marginTop: 4 }}>{pr.period.split(' ')[0].substring(0, 3)}</Text>
                </View>
              );
            })}
          </View>
          {payRuns.filter(pr => pr.status === 'completed').length === 0 && (
            <Text style={{ color: C.light, textAlign: 'center', paddingVertical: 20 }}>Process pay runs to see trends</Text>
          )}
        </View>

        {/* Pension Stats */}
        <Text style={styles.sectionLabel}>Pension Overview</Text>
        <View style={styles.analyticsCard}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-around' }}>
            <View style={{ alignItems: 'center' }}>
              <Text style={[styles.kpiValue, { color: C.success }]}>{analytics.pensionEnrolled}</Text>
              <Text style={styles.kpiLabel}>Enrolled</Text>
            </View>
            <View style={{ alignItems: 'center' }}>
              <Text style={[styles.kpiValue, { color: C.warning }]}>{analytics.activeEmployees - analytics.pensionEnrolled}</Text>
              <Text style={styles.kpiLabel}>Not Enrolled</Text>
            </View>
            <View style={{ alignItems: 'center' }}>
              <Text style={[styles.kpiValue, { color: C.primary }]}>{analytics.activeEmployees > 0 ? Math.round(analytics.pensionPercentage) : 0}%</Text>
              <Text style={styles.kpiLabel}>Enrollment Rate</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    );
  };

  const renderPensionTab = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: HPAD, paddingBottom: 100 }}>
      <Text style={styles.sectionLabel}>Pension Summary</Text>
      <View style={styles.analyticsCard}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-around', paddingVertical: 8 }}>
          <View style={{ alignItems: 'center' }}>
            <Text style={[styles.kpiValue, { color: C.success }]}>{analytics.pensionEnrolled}</Text>
            <Text style={styles.kpiLabel}>Auto-Enrolled</Text>
          </View>
          <View style={{ alignItems: 'center' }}>
            <Text style={[styles.kpiValue, { color: C.primary }]}>{formatCurrency(analytics.pensionCollectedYTD)}</Text>
            <Text style={styles.kpiLabel}>Contributions YTD</Text>
          </View>
        </View>
      </View>
      <Text style={styles.sectionLabel}>Employee Pension Status</Text>
      {employees.filter(e => e.status === 'active').map(emp => (
        <View key={emp.id} style={styles.pensionRow}>
          <View style={styles.pensionAvatar}>
            <Text style={styles.pensionAvatarText}>{emp.firstName[0]}{emp.lastName[0]}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.pensionName}>{emp.firstName} {emp.lastName}</Text>
            <Text style={styles.pensionScheme}>{emp.payroll?.pension?.enrolled ? (emp.payroll?.pension?.scheme || 'NEST') : 'Not enrolled'}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            {emp.payroll?.pension?.enrolled ? (
              <>
                <Text style={styles.pensionRate}>{Math.round((emp.payroll?.pension?.employeeContribution || 0) * 100)}% / {Math.round((emp.payroll?.pension?.employerContribution || 0) * 100)}%</Text>
                <Text style={[styles.pensionStatus, { color: C.success }]}>Enrolled</Text>
              </>
            ) : (
              <Text style={[styles.pensionStatus, { color: C.warning }]}>Opt-Out</Text>
            )}
          </View>
          <TouchableOpacity style={{ marginLeft: 12 }} onPress={() => {
            const updated = employees.map(e => e.id === emp.id ? { ...e, payroll: { ...(e.payroll || {}), pension: { ...(e.payroll?.pension || {}), enrolled: !e.payroll?.pension?.enrolled } } } : e);
            setEmployees(updated);
            AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));
            Haptics.selectionAsync();
          }}>
            <MaterialCommunityIcons name={emp.payroll?.pension?.enrolled ? 'toggle-switch' : 'toggle-switch-off'} size={28} color={emp.payroll?.pension?.enrolled ? C.success : C.light} />
          </TouchableOpacity>
        </View>
      ))}
    </ScrollView>
  );

  const renderComplianceTab = () => {
    const issues = [];

    // ── Company-level compliance (from CompanyProfile + payrollSettings) ──
    if (!companyInfo.registrationNumber)    issues.push({ type: 'warning', msg: 'Company registration number not set — update Company Profile', emp: null });
    if (!companyInfo.vatNumber)             issues.push({ type: 'warning', msg: 'VAT number not set — update Company Profile', emp: null });
    if (!payrollSettings.payeReference)     issues.push({ type: 'error',   msg: 'PAYE Reference not set — required for RTI submissions. Add in Payroll Settings.', emp: null });
    if (!payrollSettings.accountsOfficeRef) issues.push({ type: 'warning', msg: 'Accounts Office Reference not set — add in Payroll Settings', emp: null });
    if (!companyInfo.bankAccount)           issues.push({ type: 'warning', msg: 'Company bank account not set — BACS payments will fail. Update Company Profile.', emp: null });

    employees.forEach(emp => {
      if (!emp.niNumber) issues.push({ type: 'warning', msg: `${emp.firstName} ${emp.lastName}: Missing NI Number`, emp: emp.id });
      if (!emp.taxCode) issues.push({ type: 'error', msg: `${emp.firstName} ${emp.lastName}: Missing Tax Code`, emp: emp.id });
      if (!emp.payroll?.bankDetails?.accountNumber) issues.push({ type: 'error', msg: `${emp.firstName} ${emp.lastName}: Missing bank details`, emp: emp.id });
      if (emp.status === 'active' && (emp.payroll?.salary || 0) < 12570 && !emp.payroll?.pension?.enrolled) {
        issues.push({ type: 'info', msg: `${emp.firstName} ${emp.lastName}: Below NI threshold - pension review needed`, emp: emp.id });
      }
    });
    const rtiDue = payRuns.filter(pr => pr.status === 'completed' && !pr.rtiSubmitted);
    if (rtiDue.length > 0) issues.push({ type: 'error', msg: `${rtiDue.length} pay run(s) have RTI FPS not submitted to HMRC` });
    const pensionNonCompliant = employees.filter(e => e.status === 'active' && !e.payroll?.pension?.enrolled && ((Number(e.payroll?.salary) || 0) / 12) >= 520).length;
    if (pensionNonCompliant > 0) issues.push({ type: 'warning', msg: `${pensionNonCompliant} employee(s) eligible for auto-enrolment but not enrolled` });

    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: HPAD, paddingBottom: 100 }}>
        <View style={[styles.complianceStatus, { backgroundColor: issues.filter(i => i.type === 'error').length > 0 ? C.danger + '15' : issues.length > 0 ? C.warning + '15' : C.success + '15' }]}>
          <MaterialCommunityIcons name={issues.filter(i => i.type === 'error').length > 0 ? 'shield-alert' : issues.length > 0 ? 'shield-half-full' : 'shield-check'} size={40} color={issues.filter(i => i.type === 'error').length > 0 ? C.danger : issues.length > 0 ? C.warning : C.success} />
          <Text style={[styles.complianceTitle, { color: issues.filter(i => i.type === 'error').length > 0 ? C.danger : issues.length > 0 ? C.warning : C.success }]}>
            <Text>{issues.filter(i => i.type === 'error').length > 0 ? 'Action Required' : issues.length > 0 ? 'Review Recommended' : 'Fully Compliant'}</Text>
          </Text>
          <Text style={styles.complianceSub}>{issues.length} issue{issues.length !== 1 ? 's' : ''} found</Text>
        </View>
        <Text style={styles.sectionLabel}>Compliance Issues</Text>
        {issues.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="check-circle" size={48} color={C.success} />
            <Text style={styles.emptyText}>All compliance checks passed</Text>
          </View>
        ) : issues.map((issue, i) => (
          <View key={i} style={[styles.issueCard, { borderLeftColor: issue.type === 'error' ? C.danger : issue.type === 'warning' ? C.warning : C.info }]}>
            <MaterialCommunityIcons name={issue.type === 'error' ? 'alert-circle' : issue.type === 'warning' ? 'alert' : 'information'} size={20} color={issue.type === 'error' ? C.danger : issue.type === 'warning' ? C.warning : C.info} />
            <Text style={styles.issueText}>{issue.msg}</Text>
          </View>
        ))}
        <Text style={styles.sectionLabel}>RTI Status</Text>
        {payRuns.map(pr => (
          <View key={pr.id} style={styles.rtiRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.rtiPeriod}>{pr.period}</Text>
              <Text style={styles.rtiRef}>{pr.reference}</Text>
            </View>
            <View style={[styles.rtiBadge, { backgroundColor: pr.status === 'completed' ? C.success + '20' : C.light + '20' }]}>
              <Text style={[styles.rtiBadgeText, { color: pr.status === 'completed' ? C.success : C.light }]}>
                <Text>{pr.status === 'completed' ? 'FPS Submitted' : 'Pending'}</Text>
              </Text>
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  const renderAuditTab = () => {
    const auditLog = [
      { action: 'Employee Created', user: 'HR System', target: 'James Walker (EMP001)', date: '2020-01-15', type: 'create' },
      { action: 'Pay Run Processed', user: 'Finance Director', target: 'PR-2024-12', date: '2024-12-20', type: 'process' },
      { action: 'Payslip Emailed', user: 'System', target: 'James Walker - Dec 2024', date: '2024-12-20', type: 'email' },
      { action: 'Pay Run Approved', user: 'Finance Director', target: 'PR-2024-12', date: '2024-12-21', type: 'approve' },
      { action: 'Salary Updated', user: 'HR Manager', target: 'Sarah Johnson: £60,000 → £65,000', date: '2024-02-01', type: 'update' },
      { action: 'Tax Code Changed', user: 'HMRC Notification', target: 'Michael Chen: 1257L → K497', date: '2024-01-20', type: 'update' },
      { action: 'Pension Enrolled', user: 'System', target: 'David Williams', date: '2023-06-01', type: 'pension' },
    ];
    const iconMap = { create: 'account-plus', process: 'cog-play', email: 'email-outline', approve: 'check-circle', update: 'pencil', pension: 'piggy-bank' };
    const colorMap = { create: C.success, process: C.info, email: C.accent, approve: C.primary, update: C.warning, pension: C.purple };
    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: HPAD, paddingBottom: 100 }}>
        <Text style={styles.sectionLabel}>Audit Trail</Text>
        {auditLog.map((log, i) => (
          <View key={i} style={styles.auditRow}>
            <View style={[styles.auditIcon, { backgroundColor: colorMap[log.type] + '18' }]}>
              <MaterialCommunityIcons name={iconMap[log.type]} size={18} color={colorMap[log.type]} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.auditAction}>{log.action}</Text>
              <Text style={styles.auditTarget}>{log.target}</Text>
              <Text style={styles.auditMeta}>{log.user} • {formatDate(log.date)}</Text>
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };


  // ── Modal: Employee Detail ───────────────────────────────────────────────
  const renderEmployeeDetailModal = () => {
    if (!selectedEmployee) return null;
    const emp = selectedEmployee;

    return (
      <Modal visible={showEmployeeDetail} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowEmployeeDetail(false)} style={styles.modalCloseBtn}>
              <Ionicons name="close" size={22} color={C.dark} />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>{emp.firstName} {emp.lastName}</Text>
            <TouchableOpacity onPress={() => { setShowEmployeeDetail(false); setEmployeeForm(emp); setShowEmployeeForm(true); }} style={styles.modalEditBtn}>
              <MaterialCommunityIcons name="pencil" size={18} color={C.primary} />
            </TouchableOpacity>
          </View>
          {/* Employee header card */}
          <LinearGradient colors={C.primaryGradient} style={styles.empDetailHero}>
            <View style={styles.empDetailAvatar}>
              <Text style={styles.empDetailAvatarText}>{emp.firstName[0]}{emp.lastName[0]}</Text>
            </View>
            <Text style={styles.empDetailName}>{emp.title} {emp.firstName} {emp.lastName}</Text>
            <Text style={styles.empDetailRole}>{emp.employment?.jobTitle} • {emp.employment?.department}</Text>
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
              <View style={[styles.empDetailBadge, { backgroundColor: emp.status === 'active' ? C.success + '30' : C.warning + '30' }]}>
                <Text style={{ color: emp.status === 'active' ? C.successLight : C.warningLight, fontSize: 11, fontWeight: '600' }}>{String(emp.status ?? '').toUpperCase()}</Text>
              </View>
              <View style={[styles.empDetailBadge, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
                <Text style={{ color: '#fff', fontSize: 11 }}>{emp.employeeNumber}</Text>
              </View>
            </View>
          </LinearGradient>
          {/* Sub-tabs */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ backgroundColor: '#fff', maxHeight: 50 }} contentContainerStyle={{ paddingHorizontal: HPAD }}>
            {['personal', 'employment', 'payroll', 'deductions', 'benefits', 'documents'].map(t => (
              <TouchableOpacity key={t} onPress={() => setEmpDetailTab(t)} style={[styles.subTab, empDetailTab === t && styles.subTabActive]}>
                <Text style={[styles.subTabText, empDetailTab === t && styles.subTabTextActive]}>{t.charAt(0).toUpperCase() + t.slice(1)}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: HPAD, paddingBottom: 40 }}>
            {empDetailTab === 'personal' && (
              <>
                <View style={styles.detailGroup}>
                  <Text style={styles.detailGroupTitle}>Personal Information</Text>
                  {[
                    ['Full Name', `${emp.title} ${emp.firstName} ${emp.lastName}`],
                    ['Preferred Name', emp.preferredName],
                    ['Date of Birth', formatDate(emp.dateOfBirth)],
                    ['Gender', emp.gender],
                    ['Nationality', emp.nationality],
                    ['NI Number', emp.niNumber],
                    ['Tax Code', emp.taxCode],
                    ['NI Category', emp.niCategory],
                    ['Student Loan', emp.studentLoan || 'None'],
                  ].map(([label, value]) => (
                    <View key={label} style={styles.detailRow}>
                      <Text style={styles.detailLabel}>{label}</Text>
                      <Text style={styles.detailValue}>{value || '—'}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.detailGroup}>
                  <Text style={styles.detailGroupTitle}>Contact Details</Text>
                  {[
                    ['Email', emp.contact?.email],
                    ['Phone', emp.contact?.phone],
                    ['Mobile', emp.contact?.mobile],
                    ['Address', `${emp.contact?.address.line1}${emp.contact?.address.line2 ? ', ' + emp.contact?.address.line2 : ''}, ${emp.contact?.address.city}, ${emp.contact?.address.postcode}`],
                  ].map(([label, value]) => (
                    <View key={label} style={styles.detailRow}>
                      <Text style={styles.detailLabel}>{label}</Text>
                      <Text style={styles.detailValue}>{value || '—'}</Text>
                    </View>
                  ))}
                </View>
                {emp.emergencyContacts?.length > 0 && (
                  <View style={styles.detailGroup}>
                    <Text style={styles.detailGroupTitle}>Emergency Contacts</Text>
                    {(emp.emergencyContacts||[]).map((ec, i) => (
                      <View key={i} style={styles.emergencyCard}>
                        <Text style={styles.emergencyName}>{ec.name} ({ec.relationship})</Text>
                        <Text style={styles.emergencyPhone}>{ec.phone}</Text>
                        <Text style={styles.emergencyEmail}>{ec.email}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </>
            )}
            {empDetailTab === 'employment' && (
              <View style={styles.detailGroup}>
                <Text style={styles.detailGroupTitle}>Employment Details</Text>
                {[
                  ['Start Date', formatDate(emp.employment?.startDate)],
                  ['Contract Type', emp.employment?.contractType],
                  ['Employment Type', emp.employment?.employmentType],
                  ['Working Hours', `${emp.employment?.workingHours} hrs/week`],
                  ['Department', emp.employment?.department],
                  ['Job Title', emp.employment?.jobTitle],
                  ['Job Grade', emp.employment?.jobGrade],
                  ['Cost Centre', emp.employment?.costCenter],
                  ['Location', emp.employment?.location],
                  ['Notice Period', `${emp.employment?.noticePeriod} days`],
                  ['Holiday Entitlement', `${emp.employment?.holidayEntitlement} days`],
                  ['Holiday Remaining', `${emp.employment?.holidayRemaining} days`],
                  ['Sick Leave Taken', `${emp.employment?.sickLeaveTaken} days`],
                ].map(([label, value]) => (
                  <View key={label} style={styles.detailRow}>
                    <Text style={styles.detailLabel}>{label}</Text>
                    <Text style={styles.detailValue}>{value || '—'}</Text>
                  </View>
                ))}
              </View>
            )}
            {empDetailTab === 'payroll' && (
              <>
                <View style={styles.detailGroup}>
                  <Text style={styles.detailGroupTitle}>Pay Settings</Text>
                  {[
                    ['Pay Frequency', emp.payroll?.payFrequency || '—'],
                    ['Payment Method', emp.payroll?.payMethod || '—'],
                    ['Annual Salary', formatCurrency(emp.payroll?.salary)],
                    ['Monthly Salary', formatCurrency((emp.payroll?.salary || 0) / 12)],
                    ['Hourly Rate', emp.payroll?.hourlyRate ? formatCurrency(emp.payroll?.hourlyRate) : '—'],
                  ].map(([label, value]) => (
                    <View key={label} style={styles.detailRow}>
                      <Text style={styles.detailLabel}>{label}</Text>
                      <Text style={styles.detailValue}>{value || '—'}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.detailGroup}>
                  <Text style={styles.detailGroupTitle}>Bank Details</Text>
                  {[
                    ['Account Name', emp.payroll?.bankDetails?.accountName],
                    ['Bank', emp.payroll?.bankDetails?.bankName],
                    ['Sort Code', emp.payroll?.bankDetails?.sortCode],
                    ['Account Number', emp.payroll?.bankDetails?.accountNumber ? '****' + emp.payroll?.bankDetails?.accountNumber?.slice(-4) : '—'],
                  ].map(([label, value]) => (
                    <View key={label} style={styles.detailRow}>
                      <Text style={styles.detailLabel}>{label}</Text>
                      <Text style={styles.detailValue}>{value || '—'}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.detailGroup}>
                  <Text style={styles.detailGroupTitle}>Pension</Text>
                  {[
                    ['Enrolled', emp.payroll?.pension?.enrolled ? 'Yes' : 'No'],
                    ['Scheme', emp.payroll?.pension?.scheme || '—'],
                    ['Employee Contribution', `${Math.round((emp.payroll?.pension?.employeeContribution || 0) * 100)}%`],
                    ['Employer Contribution', `${Math.round((emp.payroll?.pension?.employerContribution || 0) * 100)}%`],
                    ['Joining Date', formatDate(emp.payroll?.pension?.joiningDate)],
                  ].map(([label, value]) => (
                    <View key={label} style={styles.detailRow}>
                      <Text style={styles.detailLabel}>{label}</Text>
                      <Text style={styles.detailValue}>{value || '—'}</Text>
                    </View>
                  ))}
                </View>
              </>
            )}
            {empDetailTab === 'deductions' && (
              <View style={styles.detailGroup}>
                <Text style={styles.detailGroupTitle}>Voluntary Deductions</Text>
                {(emp.payroll?.deductions?.length || 0) === 0 ? (
                  <Text style={styles.emptyInline}>No deductions</Text>
                ) : (emp.payroll?.deductions || []).map(ded => (
                  <View key={ded.id} style={styles.dedCard}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.dedName}>{ded.name}</Text>
                      <Text style={styles.dedType}>{ded.type} • {ded.frequency}</Text>
                    </View>
                    <Text style={styles.dedAmount}>{formatCurrency(ded.amount)}</Text>
                  </View>
                ))}
              </View>
            )}
            {empDetailTab === 'benefits' && (
              <View style={styles.detailGroup}>
                <Text style={styles.detailGroupTitle}>Benefits in Kind</Text>
                {(emp.payroll?.benefits?.length || 0) === 0 ? (
                  <Text style={styles.emptyInline}>No benefits recorded</Text>
                ) : (emp.payroll?.benefits || []).map(ben => (
                  <View key={ben.id} style={styles.dedCard}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.dedName}>{ben.name}</Text>
                      <Text style={styles.dedType}>{ben.type} • {ben.taxable ? 'Taxable' : 'Non-Taxable'}</Text>
                    </View>
                    <Text style={styles.dedAmount}>{ben.value > 0 ? formatCurrency(ben.value) + '/mo' : 'N/A'}</Text>
                  </View>
                ))}
              </View>
            )}
            {empDetailTab === 'documents' && (
              <View style={styles.detailGroup}>
                <Text style={styles.detailGroupTitle}>Documents</Text>
                {(emp.documents || []).length === 0 ? (
                  <Text style={styles.emptyInline}>No documents uploaded yet</Text>
                ) : (
                  (emp.documents || []).map(doc => (
                    <View key={doc.id} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: C.surface, borderRadius: 10, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: C.border }}>
                      <MaterialCommunityIcons name={docIcon(doc.mimeType)} size={28} color={C.primary} style={{ marginRight: 10 }} />
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: FS_SM, fontWeight: '600', color: C.text }} numberOfLines={1}>{doc.name}</Text>
                        <Text style={{ fontSize: FS_XS, color: C.muted, marginTop: 2 }}>
                          {new Date(doc.uploadedAt).toLocaleDateString('en-GB')}{doc.size ? '  ·  ' + formatDocSize(doc.size) : ''}
                        </Text>
                      </View>
                      <TouchableOpacity onPress={() => openEmployeeDocument(doc)} style={{ padding: 6, marginRight: 4 }}>
                        <MaterialCommunityIcons name="open-in-new" size={20} color={C.info} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => deleteEmployeeDocument(emp, doc.id)} style={{ padding: 6 }}>
                        <MaterialCommunityIcons name="trash-can-outline" size={20} color={C.danger} />
                      </TouchableOpacity>
                    </View>
                  ))
                )}
                <TouchableOpacity style={styles.uploadBtn} onPress={() => uploadEmployeeDocument(emp)}>
                  <MaterialCommunityIcons name="upload" size={20} color={C.primary} />
                  <Text style={{ color: C.primary, marginLeft: 8, fontWeight: '600' }}>Upload Document</Text>
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
          {/* Actions */}
          <View style={{ flexDirection: 'row', padding: HPAD, paddingBottom: 4 }}>
            <TouchableOpacity
              style={[styles.modalActionBtn, { backgroundColor: C.success + '18', flex: 1, marginRight: 8 }]}
              onPress={() => {
                setShowEmployeeDetail(false);
                navigateToEmployeeDetails(emp);
              }}
            >
              <MaterialCommunityIcons name="account-arrow-right" size={18} color={C.success} />
              <Text style={[styles.modalActionText, { color: C.success }]}>Full Profile</Text>
            </TouchableOpacity>
          </View>
          <View style={{ padding: HPAD, paddingBottom: 0, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: C.border }}>
            {/* PDF Payslip row */}
            <TouchableOpacity
              style={[styles.modalActionBtn, { backgroundColor: C.success, marginBottom: 10, justifyContent: 'center' }]}
              onPress={() => { setShowEmployeeDetail(false); setTimeout(() => generatePayslipForEmployee(emp), 300); }}>
              <MaterialCommunityIcons name="file-pdf-box" size={20} color="#fff" />
              <Text style={[styles.modalActionText, { color: '#fff', fontSize: 15, fontWeight: '700' }]}>Download Payslip PDF</Text>
            </TouchableOpacity>
            {/* Action row */}
            <View style={styles.modalActions}>
              <TouchableOpacity style={[styles.modalActionBtn, { backgroundColor: C.danger + '18' }]} onPress={() => deleteEmployee(emp)}>
                <MaterialCommunityIcons name="delete" size={18} color={C.danger} />
                <Text style={[styles.modalActionText, { color: C.danger }]}>Delete</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalActionBtn, { backgroundColor: C.info + '18' }]} onPress={() => {
                const updated = employees.map(e => e.id === emp.id ? { ...e, status: e.status === 'active' ? 'suspended' : 'active' } : e);
                setEmployees(updated);
                setSelectedEmployee({ ...emp, status: emp.status === 'active' ? 'suspended' : 'active' });
                AsyncStorage.setItem('payroll_employees', JSON.stringify(updated));
              }}>
                <MaterialCommunityIcons name={emp.status === 'active' ? 'pause' : 'play'} size={18} color={C.info} />
                <Text style={[styles.modalActionText, { color: C.info }]}>{emp.status === 'active' ? 'Suspend' : 'Reactivate'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalActionBtn, { flex: 2, backgroundColor: C.primary }]} onPress={() => { setShowEmployeeDetail(false); setEmployeeForm(emp); setShowEmployeeForm(true); }}>
                <MaterialCommunityIcons name="pencil" size={18} color="#fff" />
                <Text style={[styles.modalActionText, { color: '#fff' }]}>Edit Employee</Text>
              </TouchableOpacity>
            </View>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };


  // ── Modal: Employee Form ─────────────────────────────────────────────────
  const renderEmployeeFormModal = () => {
    const isEdit = !!selectedEmployee;
    return (
      <Modal visible={showEmployeeForm} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => { setShowEmployeeForm(false); setSelectedEmployee(null); setEmployeeForm({}); }} style={styles.modalCloseBtn}>
              <Ionicons name="close" size={22} color={C.dark} />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>{isEdit ? 'Edit Employee' : 'New Employee'}</Text>
            <TouchableOpacity onPress={isEdit ? updateEmployee : createEmployee} style={styles.modalSaveBtn}>
              <Text style={styles.modalSaveBtnText}>Save</Text>
            </TouchableOpacity>
          </View>
          <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            <ScrollView contentContainerStyle={{ padding: HPAD, paddingBottom: 60 }}>
              <Text style={styles.formSection}>Personal Information</Text>
              {[
                { label: 'Title', key: 'title', placeholder: 'Mr/Mrs/Ms/Dr' },
                { label: 'First Name *', key: 'firstName', placeholder: 'First name' },
                { label: 'Last Name *', key: 'lastName', placeholder: 'Last name' },
                { label: 'Preferred Name', key: 'preferredName', placeholder: 'Preferred name' },
                { label: 'Date of Birth', key: 'dateOfBirth', placeholder: 'YYYY-MM-DD' },
                { label: 'Gender', key: 'gender', placeholder: 'Male/Female/Other' },
                { label: 'Nationality', key: 'nationality', placeholder: 'Nationality' },
                { label: 'NI Number *', key: 'niNumber', placeholder: 'AB123456C' },
                { label: 'Tax Code', key: 'taxCode', placeholder: '1257L' },
                { label: 'NI Category', key: 'niCategory', placeholder: 'A/B/C/J' },
              ].map(field => (
                <View key={field.key} style={styles.formField}>
                  <Text style={styles.formLabel}>{field.label}</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder={field.placeholder}
                    placeholderTextColor={C.light}
                    value={String(employeeForm[field.key] || '')}
                    onChangeText={v => setEmployeeForm(prev => ({ ...prev, [field.key]: v }))}
                  />
                </View>
              ))}
              <Text style={styles.formSection}>Contact Details</Text>
              {[
                { label: 'Email *', key: 'email', placeholder: 'work@company.co.uk', sub: 'contact.email' },
                { label: 'Phone', key: 'phone', placeholder: '+44 20 1234 5678', sub: 'contact.phone' },
                { label: 'Mobile', key: 'mobile', placeholder: '+44 7700 123456', sub: 'contact.mobile' },
                { label: 'Address Line 1', key: 'address1', placeholder: '123 High Street', sub: 'contact.address.line1' },
                { label: 'City', key: 'city', placeholder: 'London', sub: 'contact.address.city' },
                { label: 'Postcode', key: 'postcode', placeholder: 'SW1A 1AA', sub: 'contact.address.postcode' },
              ].map(field => (
                <View key={field.key} style={styles.formField}>
                  <Text style={styles.formLabel}>{field.label}</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder={field.placeholder}
                    placeholderTextColor={C.light}
                    value={String(employeeForm[field.key] || '')}
                    onChangeText={v => setEmployeeForm(prev => ({ ...prev, [field.key]: v }))}
                    keyboardType={field.key === 'email' ? 'email-address' : field.key.includes('phone') || field.key.includes('mobile') ? 'phone-pad' : 'default'}
                  />
                </View>
              ))}
              <Text style={styles.formSection}>Employment</Text>
              {[
                { label: 'Department *', key: 'department', placeholder: 'Engineering' },
                { label: 'Job Title *', key: 'jobTitle', placeholder: 'Senior Engineer' },
                { label: 'Job Grade', key: 'jobGrade', placeholder: 'E3' },
                { label: 'Start Date *', key: 'startDate', placeholder: 'YYYY-MM-DD' },
                { label: 'Contract Type', key: 'contractType', placeholder: 'permanent/fixed-term/temporary' },
                { label: 'Working Hours/Week', key: 'workingHours', placeholder: '37.5' },
                { label: 'Notice Period (days)', key: 'noticePeriod', placeholder: '30' },
                { label: 'Cost Centre', key: 'costCenter', placeholder: 'CC-ENG-001' },
                { label: 'Location', key: 'location', placeholder: 'London Office' },
              ].map(field => (
                <View key={field.key} style={styles.formField}>
                  <Text style={styles.formLabel}>{field.label}</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder={field.placeholder}
                    placeholderTextColor={C.light}
                    value={String(employeeForm[field.key] || '')}
                    onChangeText={v => setEmployeeForm(prev => ({ ...prev, [field.key]: v }))}
                    keyboardType={['workingHours', 'noticePeriod'].includes(field.key) ? 'numeric' : 'default'}
                  />
                </View>
              ))}
              <Text style={styles.formSection}>Payroll</Text>
              {[
                { label: 'Annual Salary (£) *', key: 'salary', placeholder: '50000' },
                { label: 'Pay Frequency', key: 'payFrequency', placeholder: 'monthly/weekly/bi-weekly' },
                { label: 'Payment Method', key: 'payMethod', placeholder: 'BACS/cheque/cash' },
                { label: 'Bank Account Name', key: 'bankName', placeholder: 'Full name' },
                { label: 'Sort Code', key: 'sortCode', placeholder: '12-34-56' },
                { label: 'Account Number', key: 'accountNumber', placeholder: '12345678' },
                { label: 'Student Loan Plan', key: 'studentLoan', placeholder: 'plan1/plan2/plan4/none' },
              ].map(field => (
                <View key={field.key} style={styles.formField}>
                  <Text style={styles.formLabel}>{field.label}</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder={field.placeholder}
                    placeholderTextColor={C.light}
                    value={String(employeeForm[field.key] || '')}
                    onChangeText={v => setEmployeeForm(prev => ({ ...prev, [field.key]: v }))}
                    keyboardType={field.key === 'salary' ? 'numeric' : 'default'}
                  />
                </View>
              ))}
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ── Modal: Pay Run Detail ────────────────────────────────────────────────
  const renderPayRunDetailModal = () => {
    if (!selectedPayRun) return null;
    const pr = selectedPayRun;
    const statusColors = { draft: C.mid, processing: C.info, completed: C.success, approved: C.success, locked: C.dark };
    const color = statusColors[pr.status] || C.mid;
    return (
      <Modal visible={showPayRunDetail} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowPayRunDetail(false)} style={styles.modalCloseBtn}>
              <Ionicons name="close" size={22} color={C.dark} />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>{pr.period}</Text>
            <View style={[styles.payRunStatus, { backgroundColor: color + '20' }]}>
              <Text style={[styles.payRunStatusText, { color }]}>{String(pr.status ?? '').toUpperCase()}</Text>
            </View>
          </View>
          <ScrollView contentContainerStyle={{ padding: HPAD, paddingBottom: 100 }}>
            <LinearGradient colors={C.primaryGradient} style={[styles.analyticsCard, { marginBottom: 16 }]}>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: FS_SM, marginBottom: 4 }}>Total Net Pay</Text>
              <Text style={{ color: '#fff', fontSize: 28, fontWeight: '700' }}>{formatCurrency(pr.totalNet)}</Text>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: FS_XS, marginTop: 4 }}>{pr.employeeCount} employees • {pr.reference}</Text>
            </LinearGradient>
            <View style={styles.payRunGridStats}>
              {[
                { label: 'Gross Pay', value: formatCurrency(pr.totalGross), color: C.primary },
                { label: 'Income Tax', value: formatCurrency(pr.totalTax), color: C.warning },
                { label: 'National Insurance', value: formatCurrency(pr.totalNI), color: C.danger },
                { label: 'Pension', value: formatCurrency(pr.totalPension), color: C.purple },
                { label: 'Other Deductions', value: formatCurrency(pr.totalDeductions), color: C.mid },
                { label: 'Net Pay', value: formatCurrency(pr.totalNet), color: C.success },
              ].map((stat, i) => (
                <View key={i} style={[styles.prGridItem, { borderLeftColor: stat.color }]}>
                  <Text style={styles.prGridLabel}>{stat.label}</Text>
                  <Text style={[styles.prGridValue, { color: stat.color }]}>{stat.value}</Text>
                </View>
              ))}
            </View>
            <View style={styles.detailGroup}>
              <Text style={styles.detailGroupTitle}>Pay Period</Text>
              {[
                ['Period', pr.period],
                ['Reference', pr.reference],
                ['Start Date', formatDate(pr.startDate)],
                ['End Date', formatDate(pr.endDate)],
                ['Payment Date', formatDate(pr.paymentDate)],
                ['Frequency', pr.frequency],
                ['Created', formatDate(pr.createdDate)],
                ['Processed', pr.processedDate ? formatDate(pr.processedDate) : '—'],
                ['Approved By', pr.approvedBy || '—'],
                ['Approved Date', pr.approvedDate ? formatDate(pr.approvedDate) : '—'],
                ['Locked By', pr.lockedBy || '—'],
              ].map(([label, value]) => (
                <View key={label} style={styles.detailRow}>
                  <Text style={styles.detailLabel}>{label}</Text>
                  <Text style={styles.detailValue}>{value}</Text>
                </View>
              ))}
            </View>
            {isProcessing && (
              <View style={{ marginVertical: 16 }}>
                <Text style={{ textAlign: 'center', color: C.primary, marginBottom: 8 }}>Processing... {progress}%</Text>
                <View style={{ height: 8, backgroundColor: C.border, borderRadius: 4, overflow: 'hidden' }}>
                  <View style={{ height: '100%', width: `${progress}%`, backgroundColor: C.primary, borderRadius: 4 }} />
                </View>
              </View>
            )}
          </ScrollView>
          <View style={styles.modalActions}>
            {pr.status === 'draft' && (
              <TouchableOpacity style={[styles.modalActionBtn, { flex: 1, backgroundColor: C.info }]} onPress={() => processPayRun(pr)}>
                <MaterialCommunityIcons name="cog-play" size={18} color="#fff" />
                <Text style={[styles.modalActionText, { color: '#fff' }]}>Process</Text>
              </TouchableOpacity>
            )}
            {pr.status === 'completed' && (
              <TouchableOpacity style={[styles.modalActionBtn, { flex: 1, backgroundColor: C.success }]} onPress={() => approvePayRun(pr)}>
                <MaterialCommunityIcons name="check-circle" size={18} color="#fff" />
                <Text style={[styles.modalActionText, { color: '#fff' }]}>Approve</Text>
              </TouchableOpacity>
            )}
            {pr.status === 'approved' && (
              <TouchableOpacity style={[styles.modalActionBtn, { flex: 1, backgroundColor: C.dark }]} onPress={() => lockPayRun(pr)}>
                <MaterialCommunityIcons name="lock" size={18} color="#fff" />
                <Text style={[styles.modalActionText, { color: '#fff' }]}>Lock</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={[styles.modalActionBtn, { backgroundColor: C.primary + '18' }]} onPress={async () => {
              const html = `<html><body><h1>Pay Run ${pr.period}</h1><p>Gross: £${pr.totalGross.toFixed(2)}</p><p>Tax: £${pr.totalTax.toFixed(2)}</p><p>NI: £${pr.totalNI.toFixed(2)}</p><p>Pension: £${pr.totalPension.toFixed(2)}</p><p>Net: £${pr.totalNet.toFixed(2)}</p></body></html>`;
              const { uri } = await Print.printToFileAsync({ html });
              await Sharing.shareAsync(uri, { mimeType: 'application/pdf' });
            }}>
              <MaterialCommunityIcons name="file-pdf-box" size={18} color={C.primary} />
              <Text style={[styles.modalActionText, { color: C.primary }]}>Export</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };


  // ── Modal: Pay Run Form ──────────────────────────────────────────────────
  const renderPayRunFormModal = () => (
    <Modal visible={showPayRunForm} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowPayRunForm(false)} style={styles.modalCloseBtn}>
            <Ionicons name="close" size={22} color={C.dark} />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>New Pay Run</Text>
          <TouchableOpacity onPress={createPayRun} style={styles.modalSaveBtn}>
            <Text style={styles.modalSaveBtnText}>Create</Text>
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: HPAD, paddingBottom: 60 }}>
          {[
            { label: 'Period Name *', key: 'period', placeholder: 'January 2025' },
            { label: 'Start Date *', key: 'startDate', placeholder: 'YYYY-MM-DD' },
            { label: 'End Date *', key: 'endDate', placeholder: 'YYYY-MM-DD' },
            { label: 'Payment Date *', key: 'paymentDate', placeholder: 'YYYY-MM-DD' },
            { label: 'Frequency', key: 'frequency', placeholder: 'monthly/weekly/bi-weekly' },
          ].map(field => (
            <View key={field.key} style={styles.formField}>
              <Text style={styles.formLabel}>{field.label}</Text>
              <TextInput
                style={styles.formInput}
                placeholder={field.placeholder}
                placeholderTextColor={C.light}
                value={String(payRunForm[field.key] || '')}
                onChangeText={v => setPayRunForm(prev => ({ ...prev, [field.key]: v }))}
              />
            </View>
          ))}
          <View style={styles.infoCard}>
            <MaterialCommunityIcons name="information" size={20} color={C.info} />
            <Text style={styles.infoText}>Pay run will include all {employees.filter(e => e.status === 'active').length} active employees. Calculations will be performed when you process the pay run.</Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Modal: Payslip Detail ────────────────────────────────────────────────
  const renderPayslipDetailModal = () => {
    if (!selectedPayslip) return null;
    const ps = selectedPayslip;
    const emp = employees.find(e => e.id === ps.employeeId);
    return (
      <Modal visible={showPayslipDetail} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowPayslipDetail(false)} style={styles.modalCloseBtn}>
              <Ionicons name="close" size={22} color={C.dark} />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Payslip</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity onPress={() => emailPayslip(ps)} style={styles.modalIconBtn}>
                <MaterialCommunityIcons name="send" size={20} color={C.primary} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => downloadPayslip(ps)} style={styles.modalIconBtn}>
                <MaterialCommunityIcons name="download" size={20} color={C.primary} />
              </TouchableOpacity>
            </View>
          </View>
          <ScrollView contentContainerStyle={{ padding: HPAD, paddingBottom: 60 }}>
            {/* Header */}
            <LinearGradient colors={C.primaryGradient} style={[styles.analyticsCard, { alignItems: 'center', marginBottom: 16 }]}>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: FS_SM }}>PAYSLIP</Text>
              <Text style={{ color: '#fff', fontSize: 22, fontWeight: '700', marginVertical: 4 }}>{ps.period}</Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)' }}>{ps.employeeName} • {ps.employeeNumber}</Text>
              {emp && <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: FS_XS, marginTop: 4 }}>{emp.employment?.jobTitle} • {emp.employment?.department}</Text>}
            </LinearGradient>
            {/* Employee Info */}
            {emp && (
              <View style={styles.detailGroup}>
                <Text style={styles.detailGroupTitle}>Employee Details</Text>
                {[
                  ['NI Number', emp.niNumber],
                  ['Tax Code', emp.taxCode],
                  ['NI Category', emp.niCategory],
                  ['Payment Method', ps.paymentMethod],
                  ['Payment Date', formatDate(ps.paymentDate)],
                ].map(([label, value]) => (
                  <View key={label} style={styles.detailRow}>
                    <Text style={styles.detailLabel}>{label}</Text>
                    <Text style={styles.detailValue}>{value || '—'}</Text>
                  </View>
                ))}
              </View>
            )}
            {/* Earnings */}
            <View style={styles.detailGroup}>
              <Text style={styles.detailGroupTitle}>Earnings</Text>
              {[
                ['Basic Pay', ps.earnings?.basic],
                ps.earnings?.overtime > 0 && ['Overtime', ps.earnings.overtime],
                ps.earnings?.bonus > 0 && ['Bonus', ps.earnings.bonus],
                ps.earnings?.commission > 0 && ['Commission', ps.earnings.commission],
                ps.earnings?.holiday > 0 && ['Holiday Pay', ps.earnings.holiday],
                ps.earnings?.sick > 0 && ['Sick Pay', ps.earnings.sick],
              ].filter(Boolean).map(([label, value]) => (
                <View key={label} style={styles.detailRow}>
                  <Text style={styles.detailLabel}>{label}</Text>
                  <Text style={styles.detailValue}>{formatCurrency(value)}</Text>
                </View>
              ))}
              <View style={[styles.detailRow, { borderTopWidth: 1, borderTopColor: C.border, paddingTop: 8 }]}>
                <Text style={[styles.detailLabel, { fontWeight: '700', color: C.dark }]}>Total Earnings</Text>
                <Text style={[styles.detailValue, { fontWeight: '700', color: C.dark }]}>{formatCurrency(ps.earnings?.total)}</Text>
              </View>
            </View>
            {/* Deductions */}
            <View style={styles.detailGroup}>
              <Text style={styles.detailGroupTitle}>Deductions</Text>
              {[
                ['Income Tax', ps.deductions?.tax],
                ['National Insurance', ps.deductions?.ni],
                ['Pension', ps.deductions?.pension],
                ps.deductions?.studentLoan > 0 && ['Student Loan', ps.deductions.studentLoan],
                ps.deductions?.union > 0 && ['Union Fees', ps.deductions.union],
                ps.deductions?.charity > 0 && ['Give as You Earn', ps.deductions.charity],
                ps.deductions?.childcare > 0 && ['Childcare Vouchers', ps.deductions.childcare],
              ].filter(Boolean).map(([label, value]) => (
                <View key={label} style={styles.detailRow}>
                  <Text style={styles.detailLabel}>{label}</Text>
                  <Text style={[styles.detailValue, { color: C.danger }]}>-{formatCurrency(value)}</Text>
                </View>
              ))}
              <View style={[styles.detailRow, { borderTopWidth: 1, borderTopColor: C.border, paddingTop: 8 }]}>
                <Text style={[styles.detailLabel, { fontWeight: '700', color: C.dark }]}>Total Deductions</Text>
                <Text style={[styles.detailValue, { fontWeight: '700', color: C.danger }]}>-{formatCurrency(ps.deductions?.total)}</Text>
              </View>
            </View>
            {/* Net Pay */}
            <View style={[styles.analyticsCard, { backgroundColor: C.success + '15', borderColor: C.success + '40', borderWidth: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
              <Text style={{ fontSize: FS_LG, fontWeight: '700', color: C.dark }}>NET PAY</Text>
              <Text style={{ fontSize: 24, fontWeight: '800', color: C.success }}>{formatCurrency(ps.netPay)}</Text>
            </View>
            {/* Year to Date */}
            <View style={styles.detailGroup}>
              <Text style={styles.detailGroupTitle}>Year to Date</Text>
              {[
                ['Gross Pay YTD', ps.yearToDate?.gross],
                ['Tax YTD', ps.yearToDate?.tax],
                ['NI YTD', ps.yearToDate?.ni],
                ['Pension YTD', ps.yearToDate?.pension],
                ['Net Pay YTD', ps.yearToDate?.net],
              ].map(([label, value]) => (
                <View key={label} style={styles.detailRow}>
                  <Text style={styles.detailLabel}>{label}</Text>
                  <Text style={styles.detailValue}>{formatCurrency(value)}</Text>
                </View>
              ))}
            </View>
          </ScrollView>
          <View style={styles.modalActions}>
            <TouchableOpacity style={[styles.modalActionBtn, { flex: 1, backgroundColor: C.accent }]} onPress={() => emailPayslip(ps)}>
              <MaterialCommunityIcons name="send" size={18} color="#fff" />
              <Text style={[styles.modalActionText, { color: '#fff' }]}>Email to Employee</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.modalActionBtn, { backgroundColor: C.primary }]} onPress={() => downloadPayslip(ps)}>
              <MaterialCommunityIcons name="download" size={18} color="#fff" />
              <Text style={[styles.modalActionText, { color: '#fff' }]}>Download PDF</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };


  // ── Modal: Tax Calculator ────────────────────────────────────────────────
  const renderTaxCalculatorModal = () => (
    <Modal visible={showTaxCalculator} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowTaxCalculator(false)} style={styles.modalCloseBtn}>
            <Ionicons name="close" size={22} color={C.dark} />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Tax Calculator 2024/25</Text>
          <View style={{ width: 40 }} />
        </View>
        <ScrollView contentContainerStyle={{ padding: HPAD, paddingBottom: 60 }}>
          <View style={styles.calculatorCard}>
            <Text style={styles.calcLabel}>Annual Salary (£)</Text>
            <TextInput
              style={styles.calcInput}
              keyboardType="numeric"
              value={String(taxCalculator.annualSalary)}
              onChangeText={v => setTaxCalculator(prev => ({ ...prev, annualSalary: parseFloat(v) || 0, results: null }))}
            />
            <Text style={styles.calcLabel}>Tax Code</Text>
            <TextInput
              style={styles.calcInput}
              value={taxCalculator.taxCode}
              onChangeText={v => setTaxCalculator(prev => ({ ...prev, taxCode: v, results: null }))}
              placeholder="1257L"
              placeholderTextColor={C.light}
            />
            <Text style={styles.calcLabel}>Pension Contribution (%)</Text>
            <TextInput
              style={styles.calcInput}
              keyboardType="numeric"
              value={String(taxCalculator.pensionContribution)}
              onChangeText={v => setTaxCalculator(prev => ({ ...prev, pensionContribution: parseFloat(v) || 0, results: null }))}
            />
            <Text style={styles.calcLabel}>Student Loan Plan</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
              {['none', 'plan1', 'plan2', 'plan4'].map(plan => (
                <TouchableOpacity key={plan} onPress={() => setTaxCalculator(prev => ({ ...prev, studentLoanPlan: plan, results: null }))} style={[styles.planChip, taxCalculator.studentLoanPlan === plan && styles.planChipActive]}>
                  <Text style={[styles.planChipText, taxCalculator.studentLoanPlan === plan && { color: '#fff' }]}>{plan === 'none' ? 'None' : plan.toUpperCase()}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity onPress={calculateTaxResults} style={styles.calcBtn}>
              <LinearGradient colors={C.primaryGradient} style={styles.calcBtnGrad}>
                <MaterialCommunityIcons name="calculator" size={18} color="#fff" />
                <Text style={styles.calcBtnText}>Calculate</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
          {taxCalculator.results && (
            <View style={styles.calcResults}>
              <Text style={styles.calcResultsTitle}>Results</Text>
              <View style={[styles.analyticsCard, { backgroundColor: C.success + '12', marginBottom: 12 }]}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ color: C.success, fontSize: 20, fontWeight: '700' }}>{formatCurrency(taxCalculator.results.netMonthly)}</Text>
                    <Text style={{ color: C.mid, fontSize: FS_XS }}>Monthly Take-Home</Text>
                  </View>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ color: C.success, fontSize: 20, fontWeight: '700' }}>{formatCurrency(taxCalculator.results.netAnnual)}</Text>
                    <Text style={{ color: C.mid, fontSize: FS_XS }}>Annual Take-Home</Text>
                  </View>
                </View>
              </View>
              {[
                { label: 'Gross Annual', value: formatCurrency(taxCalculator.results.annualSalary), color: C.primary },
                { label: `Income Tax (${formatNumber(taxCalculator.results.taxRate, 1)}%)`, value: formatCurrency(taxCalculator.results.tax), color: C.warning },
                { label: `National Insurance (${formatNumber(taxCalculator.results.niRate, 1)}%)`, value: formatCurrency(taxCalculator.results.ni), color: C.danger },
                { label: `Pension (${taxCalculator.results.pensionRate}%)`, value: formatCurrency(taxCalculator.results.pensionAmount), color: C.purple },
                taxCalculator.results.studentLoan > 0 && { label: 'Student Loan', value: formatCurrency(taxCalculator.results.studentLoan), color: C.info },
                { label: `Effective Tax Rate`, value: `${formatNumber(taxCalculator.results.effectiveRate, 1)}%`, color: C.mid },
              ].filter(Boolean).map((row, i) => (
                <View key={i} style={styles.detailRow}>
                  <Text style={styles.detailLabel}>{row.label}</Text>
                  <Text style={[styles.detailValue, { color: row.color, fontWeight: '600' }]}>{row.value}</Text>
                </View>
              ))}
              <Text style={styles.calcNote}>* Calculations based on 2024/25 UK tax bands. For guidance only.</Text>
            </View>
          )}
          <Text style={styles.sectionLabel}>2024/25 Tax Bands</Text>
          <View style={styles.detailGroup}>
            {TAX_BANDS_2024.map((band, i) => (
              <View key={i} style={styles.detailRow}>
                <Text style={styles.detailLabel}>{band.name}</Text>
                <Text style={styles.detailValue}>{band.rate === 0 ? '0%' : `${band.rate * 100}%`}</Text>
              </View>
            ))}
          </View>
          <Text style={styles.sectionLabel}>Statutory Rates 2024/25</Text>
          <View style={styles.detailGroup}>
            {[
              ['SSP (weekly)', formatCurrency(STATUTORY_RATES_2024.ssp)],
              ['SMP (weekly)', formatCurrency(STATUTORY_RATES_2024.smp)],
              ['SPP (weekly)', formatCurrency(STATUTORY_RATES_2024.spp)],
              ['SAP (weekly)', formatCurrency(STATUTORY_RATES_2024.sap)],
              ['NMW (23+)', '£11.44/hr'],
              ['NMW (21-22)', '£10.18/hr'],
              ['NMW (18-20)', '£7.49/hr'],
            ].map(([label, value]) => (
              <View key={label} style={styles.detailRow}>
                <Text style={styles.detailLabel}>{label}</Text>
                <Text style={styles.detailValue}>{value}</Text>
              </View>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Modal: Report Generator ──────────────────────────────────────────────
  const renderReportGeneratorModal = () => (
    <Modal visible={showReportGenerator} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowReportGenerator(false)} style={styles.modalCloseBtn}>
            <Ionicons name="close" size={22} color={C.dark} />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Generate Report</Text>
          <TouchableOpacity onPress={generateReport} style={styles.modalSaveBtn}>
            <Text style={styles.modalSaveBtnText}>Generate</Text>
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: HPAD, paddingBottom: 60 }}>
          <Text style={styles.formSection}>Report Type</Text>
          {[
            { id: 'payroll-summary', label: 'Payroll Summary' },
            { id: 'employee-summary', label: 'Employee Summary' },
            { id: 'tax-summary', label: 'Tax Report' },
            { id: 'pension-report', label: 'Pension Report' },
            { id: 'cost-centre', label: 'Cost Centre Report' },
          ].map(rt => (
            <TouchableOpacity key={rt.id} onPress={() => setReportForm(prev => ({ ...prev, type: rt.id }))} style={[styles.reportTypeItem, reportForm.type === rt.id && styles.reportTypeItemActive]}>
              <View style={[styles.reportTypeRadio, reportForm.type === rt.id && { backgroundColor: C.primary, borderColor: C.primary }]}>
                {reportForm.type === rt.id && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fff' }} />}
              </View>
              <Text style={[styles.reportTypeLabel, reportForm.type === rt.id && { color: C.primary }]}>{rt.label}</Text>
            </TouchableOpacity>
          ))}
          <Text style={styles.formSection}>Date Range</Text>
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Start Date</Text>
            <TextInput style={styles.formInput} placeholder="YYYY-MM-DD" placeholderTextColor={C.light} value={reportForm.startDate} onChangeText={v => setReportForm(prev => ({ ...prev, startDate: v }))} />
          </View>
          <View style={styles.formField}>
            <Text style={styles.formLabel}>End Date</Text>
            <TextInput style={styles.formInput} placeholder="YYYY-MM-DD" placeholderTextColor={C.light} value={reportForm.endDate} onChangeText={v => setReportForm(prev => ({ ...prev, endDate: v }))} />
          </View>
          <Text style={styles.formSection}>Export Format</Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            {['pdf', 'excel', 'csv'].map(fmt => (
              <TouchableOpacity key={fmt} onPress={() => setReportForm(prev => ({ ...prev, format: fmt }))} style={[styles.formatBtn, reportForm.format === fmt && styles.formatBtnActive]}>
                <MaterialCommunityIcons name={fmt === 'pdf' ? 'file-pdf-box' : fmt === 'excel' ? 'file-excel' : 'file-delimited'} size={20} color={reportForm.format === fmt ? '#fff' : C.mid} />
                <Text style={[styles.formatBtnText, reportForm.format === fmt && { color: '#fff' }]}>{fmt.toUpperCase()}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.formField}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={styles.formLabel}>Include Detailed Breakdown</Text>
              <Switch value={reportForm.includeDetails} onValueChange={v => setReportForm(prev => ({ ...prev, includeDetails: v }))} trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
          </View>
          {isProcessing && (
            <View style={{ marginTop: 16 }}>
              <Text style={{ textAlign: 'center', color: C.primary, marginBottom: 8 }}>Generating report... {progress}%</Text>
              <View style={{ height: 6, backgroundColor: C.border, borderRadius: 3, overflow: 'hidden' }}>
                <View style={{ height: '100%', width: `${progress}%`, backgroundColor: C.primary, borderRadius: 3 }} />
              </View>
            </View>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Modal: Settings ──────────────────────────────────────────────────────
  const savePayrollSettings = async (updated) => {
    setPayrollSettings(updated);
    await AsyncStorage.setItem('payroll_settings', JSON.stringify(updated));
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
  };

  const renderSettingsModal = () => (
    <Modal visible={showSettings} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowSettings(false)} style={styles.modalCloseBtn}>
            <Ionicons name="close" size={22} color={C.dark} />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Payroll Settings</Text>
          <View style={{ width: 40 }} />
        </View>
        <ScrollView contentContainerStyle={{ padding: HPAD, paddingBottom: 60 }}>
          
          {/* ── Sync from Employee Management ── */}
          <TouchableOpacity
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: C.success + '18',
              borderRadius: CARD_R,
              padding: 16,
              marginBottom: 20,
              borderWidth: 1,
              borderColor: C.success + '40',
            }}
            onPress={syncFromEmployeeManagement}
          >
            <MaterialCommunityIcons name="account-sync" size={26} color={C.success} />
            <View style={{ flex: 1, marginLeft: 14 }}>
              <Text style={{ fontSize: FS_MD, fontWeight: '700', color: C.dark }}>
                Sync from Employee Management
              </Text>
              <Text style={{ fontSize: FS_SM, color: C.muted, marginTop: 3 }}>
                Import & update employees, salaries, and roles from the HR management screen
              </Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color={C.muted} />
          </TouchableOpacity>

          {/* ── Company Info (live from CompanyProfile) ── */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <Text style={styles.formSection}>Company Information</Text>
            <TouchableOpacity onPress={() => navigation.navigate('CompanyProfile')}>
              <Text style={{ fontSize: FS_XS, color: C.primary, fontWeight: '600' }}>Edit in Profile →</Text>
            </TouchableOpacity>
          </View>

          {/* Company logo — resolved via useCompanyLogo hook */}
          <View style={{ marginBottom: 12 }}>
            {logoLoading ? (
              <View style={{ width: 60, height: 60, borderRadius: 8, backgroundColor: C.primary + '18', alignItems: 'center', justifyContent: 'center' }}>
                <ActivityIndicator size="small" color={C.primary} />
              </View>
            ) : companyInfo.logo ? (
              <Image
                source={{ uri: (companyInfo.logo && companyInfo.logo.startsWith('http')) ? companyInfo.logo : undefined }}
                style={{ width: 60, height: 60, borderRadius: 8 }}
                defaultSource={null}
                onError={() => {}}
              />
            ) : (
              <View style={{ width: 60, height: 60, borderRadius: 8, backgroundColor: C.primary + '18', alignItems: 'center', justifyContent: 'center' }}>
                <MaterialCommunityIcons name="office-building" size={28} color={C.primary} />
              </View>
            )}
          </View>

          {[
            { label: 'Company Name',            value: companyInfo.name || '—' },
            { label: 'Company Email',            value: companyInfo.email || '—' },
            { label: 'Phone',                    value: companyInfo.phone || '—' },
            { label: 'Headquarters',             value: companyInfo.headquarters || '—' },
            { label: 'Company Registration No.', value: companyInfo.registrationNumber || '—' },
            { label: 'VAT Number',               value: companyInfo.vatNumber || '—' },
            { label: 'Company Code',             value: companyInfo.companyCode || '—' },
            { label: 'Industry',                 value: companyInfo.industry || '—' },
            { label: 'Tax Year',                 value: '2024/25' },
          ].map((item, i) => (
            <View key={i} style={styles.settingRow}>
              <Text style={styles.settingLabel}>{item.label}</Text>
              <Text style={styles.settingValue}>{item.value}</Text>
            </View>
          ))}

          {/* ── PAYE / HMRC Fields (editable, stored locally) ── */}
          <Text style={[styles.formSection, { marginTop: 16 }]}>PAYE & HMRC</Text>
          <View style={styles.formField}>
            <Text style={styles.formLabel}>PAYE Reference</Text>
            <TextInput
              style={styles.formInput}
              value={payrollSettings.payeReference}
              onChangeText={v => savePayrollSettings({ ...payrollSettings, payeReference: v })}
              placeholder="e.g. 123/AB45678"
              placeholderTextColor={C.light}
              autoCapitalize="characters"
            />
          </View>
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Accounts Office Reference</Text>
            <TextInput
              style={styles.formInput}
              value={payrollSettings.accountsOfficeRef}
              onChangeText={v => savePayrollSettings({ ...payrollSettings, accountsOfficeRef: v })}
              placeholder="e.g. 123PA00045678"
              placeholderTextColor={C.light}
              autoCapitalize="characters"
            />
          </View>

          {/* ── Bank Details (live from CompanyProfile) ── */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, marginTop: 8 }}>
            <Text style={styles.formSection}>Bank Details (BACS)</Text>
            <TouchableOpacity onPress={() => navigation.navigate('CompanyProfile')}>
              <Text style={{ fontSize: FS_XS, color: C.primary, fontWeight: '600' }}>Edit →</Text>
            </TouchableOpacity>
          </View>
          {[
            { label: 'Bank Name',       value: companyInfo.bankName     || '—' },
            { label: 'Account Number',  value: companyInfo.bankAccount  || '—' },
            { label: 'Sort Code',       value: companyInfo.bankSortCode || '—' },
            { label: 'IBAN',            value: companyInfo.bankIBAN     || '—' },
            { label: 'SWIFT/BIC',       value: companyInfo.bankSWIFT    || '—' },
          ].map((item, i) => (
            <View key={i} style={styles.settingRow}>
              <Text style={styles.settingLabel}>{item.label}</Text>
              <Text style={styles.settingValue}>{item.value}</Text>
            </View>
          ))}

          {/* ── Invoice / Payment Defaults (live from CompanyProfile) ── */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, marginTop: 8 }}>
            <Text style={styles.formSection}>Invoice & Payment Defaults</Text>
            <TouchableOpacity onPress={() => navigation.navigate('CompanyProfile')}>
              <Text style={{ fontSize: FS_XS, color: C.primary, fontWeight: '600' }}>Edit →</Text>
            </TouchableOpacity>
          </View>
          {[
            { label: 'Invoice Prefix',    value: companyInfo.invoicePrefix  || 'INV' },
            { label: 'Default Tax Rate',  value: (companyInfo.defaultTaxRate || '20') + '%' },
            { label: 'Payment Terms',     value: (companyInfo.paymentTerms  || '30') + ' days' },
          ].map((item, i) => (
            <View key={i} style={styles.settingRow}>
              <Text style={styles.settingLabel}>{item.label}</Text>
              <Text style={styles.settingValue}>{item.value}</Text>
            </View>
          ))}

          {/* ── Payroll Defaults (editable, stored locally) ── */}
          <Text style={[styles.formSection, { marginTop: 8 }]}>Payroll Defaults</Text>

          {/* Pay Frequency */}
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Default Pay Frequency</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              {['Weekly', 'Bi-Weekly', 'Monthly'].map(opt => (
                <TouchableOpacity
                  key={opt}
                  onPress={() => savePayrollSettings({ ...payrollSettings, payFrequency: opt })}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 9,
                    borderRadius: 8,
                    borderWidth: 1.5,
                    borderColor: payrollSettings.payFrequency?.toLowerCase() === opt.toLowerCase() ? C.primary : C.border,
                    backgroundColor: payrollSettings.payFrequency?.toLowerCase() === opt.toLowerCase() ? C.primary + '18' : C.card,
                  }}
                >
                  <Text style={{
                    fontSize: FS_SM,
                    fontWeight: '600',
                    color: payrollSettings.payFrequency?.toLowerCase() === opt.toLowerCase() ? C.primary : C.mid,
                  }}>{opt}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Pay Method */}
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Default Pay Method</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              {['BACS', 'Cheque', 'Cash'].map(opt => (
                <TouchableOpacity
                  key={opt}
                  onPress={() => savePayrollSettings({ ...payrollSettings, payMethod: opt })}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 9,
                    borderRadius: 8,
                    borderWidth: 1.5,
                    borderColor: payrollSettings.payMethod === opt ? C.primary : C.border,
                    backgroundColor: payrollSettings.payMethod === opt ? C.primary + '18' : C.card,
                  }}
                >
                  <Text style={{
                    fontSize: FS_SM,
                    fontWeight: '600',
                    color: payrollSettings.payMethod === opt ? C.primary : C.mid,
                  }}>{opt}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Payment Day */}
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Default Payment Day (day of month)</Text>
            <TextInput
              style={styles.formInput}
              value={String(payrollSettings.paymentDay || '')}
              onChangeText={v => {
                const day = parseInt(v);
                if (!v || (day >= 1 && day <= 31)) {
                  savePayrollSettings({ ...payrollSettings, paymentDay: v });
                }
              }}
              placeholder="e.g. 28"
              placeholderTextColor={C.light}
              keyboardType="number-pad"
              maxLength={2}
            />
            <Text style={{ fontSize: FS_XS, color: C.muted, marginTop: 4 }}>
              Employees will be paid on this day each month (1–31)
            </Text>
          </View>

          {/* Pension Scheme */}
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Auto-Enrolment Pension Scheme</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              {['NEST', 'PEOPLES', 'NOW', 'AVIVA', 'LEGAL_GENERAL', 'OTHER'].map(opt => (
                <TouchableOpacity
                  key={opt}
                  onPress={() => savePayrollSettings({ ...payrollSettings, pensionScheme: opt })}
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 8,
                    borderWidth: 1.5,
                    borderColor: payrollSettings.pensionScheme === opt ? C.purple : C.border,
                    backgroundColor: payrollSettings.pensionScheme === opt ? C.purple + '18' : C.card,
                    marginBottom: 6,
                  }}
                >
                  <Text style={{
                    fontSize: FS_XS,
                    fontWeight: '600',
                    color: payrollSettings.pensionScheme === opt ? C.purple : C.mid,
                  }}>{opt}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Min Employer Pension */}
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Minimum Employer Pension Contribution (%)</Text>
            <TextInput
              style={styles.formInput}
              value={String(payrollSettings.minEmployerPension || '')}
              onChangeText={v => savePayrollSettings({ ...payrollSettings, minEmployerPension: v })}
              placeholder="e.g. 3"
              placeholderTextColor={C.light}
              keyboardType="decimal-pad"
              maxLength={4}
            />
            <Text style={{ fontSize: FS_XS, color: C.muted, marginTop: 4 }}>
              Statutory minimum is 3% (total minimum 8% including employee contribution)
            </Text>
          </View>

          {/* Currency */}
          <View style={styles.formField}>
            <Text style={styles.formLabel}>Currency</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {['GBP', 'USD', 'EUR'].map(opt => (
                <TouchableOpacity
                  key={opt}
                  onPress={() => savePayrollSettings({ ...payrollSettings, currency: opt })}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 9,
                    borderRadius: 8,
                    borderWidth: 1.5,
                    borderColor: payrollSettings.currency === opt ? C.gold : C.border,
                    backgroundColor: payrollSettings.currency === opt ? C.gold + '18' : C.card,
                  }}
                >
                  <Text style={{
                    fontSize: FS_SM,
                    fontWeight: '600',
                    color: payrollSettings.currency === opt ? C.goldDark : C.mid,
                  }}>{opt} {opt === 'GBP' ? '(£)' : opt === 'USD' ? '($)' : '(€)'}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <Text style={styles.formSection}>HMRC & RTI</Text>
          {[
            { label: 'RTI Auto-Submit',    key: 'rtiAutoSubmit' },
            { label: 'P45 Auto-Generate',  key: 'p45AutoGenerate' },
            { label: 'P60 Auto-Generate',  key: 'p60AutoGenerate' },
            { label: 'BACS File Generation', key: 'bacsFileGen' },
          ].map((item, i) => (
            <View key={i} style={styles.settingToggleRow}>
              <Text style={styles.settingLabel}>{item.label}</Text>
              <Switch
                value={payrollSettings[item.key]}
                onValueChange={v => { savePayrollSettings({ ...payrollSettings, [item.key]: v }); Haptics.selectionAsync(); }}
                trackColor={{ false: C.border, true: C.primary }}
                thumbColor="#fff"
              />
            </View>
          ))}
          <Text style={styles.formSection}>Notifications</Text>
          {[
            { label: 'Pay run reminders',        key: 'reminderPayRun' },
            { label: 'HMRC deadlines',           key: 'reminderHMRC' },
            { label: 'Pension re-enrolment',     key: 'reminderPension' },
            { label: 'Employee birthday alerts', key: 'reminderBirthday' },
            { label: 'Contract expiry alerts',   key: 'reminderContract' },
          ].map((item, i) => (
            <View key={i} style={styles.settingToggleRow}>
              <Text style={styles.settingLabel}>{item.label}</Text>
              <Switch
                value={payrollSettings[item.key]}
                onValueChange={v => { savePayrollSettings({ ...payrollSettings, [item.key]: v }); Haptics.selectionAsync(); }}
                trackColor={{ false: C.border, true: C.primary }}
                thumbColor="#fff"
              />
            </View>
          ))}
          <Text style={styles.formSection}>Data & Security</Text>
          <TouchableOpacity style={styles.settingActionRow} onPress={async () => {
            try {
              const backup = {
                exportedAt: nowISO(),
                version: '2.4.1',
                company: companyInfo,
                payrollSettings,
                employees,
                payRuns,
                payslips,
              };
              const json = JSON.stringify(backup, null, 2);
              const filename = 'payroll_backup_' + todayStr() + '.json';
              const uri = FileSystem.documentDirectory + filename;
              await FileSystem.writeAsStringAsync(uri, json, { encoding: 'utf8' });
              await Sharing.shareAsync(uri, {
                mimeType: 'application/json',
                dialogTitle: 'Save Payroll Backup',
              });
              Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            } catch (err) {
              Alert.alert('Backup Failed', err.message || 'Could not create backup file.');
            }
          }}>
            <MaterialCommunityIcons name="cloud-upload" size={20} color={C.primary} />
            <Text style={styles.settingActionText}>Backup Payroll Data</Text>
            <Ionicons name="chevron-forward" size={16} color={C.light} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.settingActionRow} onPress={() =>
            Alert.alert(
              'Export All Data',
              `Export all payroll data (${employees.length} employees, ${payRuns.length} pay runs, ${payslips.length} payslips) as Excel workbook?`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Export Excel', onPress: () => exportEmployees('xlsx') },
                { text: 'Export CSV', onPress: () => exportEmployees('csv') },
              ]
            )
          }>
            <MaterialCommunityIcons name="database-export" size={20} color={C.info} />
            <Text style={styles.settingActionText}>Export All Data</Text>
            <Ionicons name="chevron-forward" size={16} color={C.light} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.settingActionRow} onPress={() => Alert.alert('Clear Cache', 'This will clear all cached data and reload from server. Continue?', [{ text: 'Cancel', style: 'cancel' }, { text: 'Clear', style: 'destructive', onPress: async () => { await AsyncStorage.clear(); setEmployees(SAMPLE_EMPLOYEES); setPayRuns(SAMPLE_PAY_RUNS); setPayslips(SAMPLE_PAYSLIPS); Alert.alert('Done', 'Cache cleared'); } }])}>
            <MaterialCommunityIcons name="delete-sweep" size={20} color={C.danger} />
            <Text style={[styles.settingActionText, { color: C.danger }]}>Clear Cache & Reset</Text>
            <Ionicons name="chevron-forward" size={16} color={C.light} />
          </TouchableOpacity>
          <View style={{ marginTop: 24, alignItems: 'center' }}>
            <Text style={{ color: C.light, fontSize: FS_XS }}>JobEng Payroll v2.4.1 • HMRC Recognised Software</Text>
            <Text style={{ color: C.light, fontSize: FS_XS, marginTop: 4 }}>2024/25 Tax Year • RTI Compliant</Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );



  // ── Modal: Import Preview ────────────────────────────────────────────────
  const renderImportModal = () => (
    <Modal visible={showImportModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }} edges={['top', 'bottom']}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => { setShowImportModal(false); setImportData([]); setSelectedImportRows([]); }} style={styles.modalCloseBtn}>
            <Ionicons name="close" size={22} color={C.dark} />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={styles.modalTitle}>Import Preview</Text>
            <Text style={{ fontSize: FS_XS, color: C.mid, marginTop: 2 }}>{importFileName}</Text>
          </View>
          <TouchableOpacity onPress={confirmImport} style={styles.modalSaveBtn}>
            <Text style={styles.modalSaveBtnText}>Import {selectedImportRows.length}</Text>
          </TouchableOpacity>
        </View>

        {/* Summary bar */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: HPAD, paddingVertical: 10, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border }}>
          <Text style={{ fontSize: FS_SM, color: C.mid }}>{importData.length} row{importData.length !== 1 ? 's' : ''} detected</Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity onPress={() => setSelectedImportRows(importData.map(r => r._rowIndex))}>
              <Text style={{ fontSize: FS_XS, color: C.primary, fontWeight: '600' }}>Select All</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setSelectedImportRows([])}>
              <Text style={{ fontSize: FS_XS, color: C.danger, fontWeight: '600' }}>Deselect All</Text>
            </TouchableOpacity>
          </View>
        </View>

        {importError ? (
          <View style={{ margin: HPAD, padding: 14, backgroundColor: C.danger + '15', borderRadius: CARD_R, borderWidth: 1, borderColor: C.danger + '40' }}>
            <Text style={{ color: C.danger, fontSize: FS_SM }}>{importError}</Text>
          </View>
        ) : null}

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: HPAD, paddingBottom: 80 }}>
          {importData.map((row, i) => {
            const isSelected = selectedImportRows.includes(row._rowIndex);
            const hasIssue = !row.firstName && !row.lastName;
            return (
              <TouchableOpacity
                key={i}
                onPress={() => {
                  setSelectedImportRows(prev =>
                    prev.includes(row._rowIndex)
                      ? prev.filter(r => r !== row._rowIndex)
                      : [...prev, row._rowIndex]
                  );
                  Haptics.selectionAsync();
                }}
                activeOpacity={0.7}
                style={[
                  styles.importPreviewRow,
                  isSelected && styles.importPreviewRowSelected,
                  hasIssue && styles.importPreviewRowWarning,
                ]}
              >
                {/* Checkbox */}
                <View style={[styles.importCheckbox, isSelected && styles.importCheckboxSelected]}>
                  {isSelected && <MaterialCommunityIcons name="check" size={14} color="#fff" />}
                </View>

                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Text style={{ fontSize: FS_SM, fontWeight: '700', color: C.dark }}>
                      {row.firstName || row.lastName ? `${row.firstName} ${row.lastName}`.trim() : '⚠ Missing name'}
                    </Text>
                    {row.salary ? (
                      <Text style={{ fontSize: FS_XS, fontWeight: '600', color: C.primary }}>
                        {formatCurrency(parseFloat(String(row.salary).replace(/[^0-9.]/g, '')) || 0)}/yr
                      </Text>
                    ) : null}
                  </View>

                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {row.email ? (
                      <View style={styles.importChip}>
                        <MaterialCommunityIcons name="email-outline" size={10} color={C.mid} />
                        <Text style={styles.importChipText}>{row.email}</Text>
                      </View>
                    ) : null}
                    {row.department ? (
                      <View style={styles.importChip}>
                        <MaterialCommunityIcons name="office-building-outline" size={10} color={C.mid} />
                        <Text style={styles.importChipText}>{row.department}</Text>
                      </View>
                    ) : null}
                    {row.jobTitle ? (
                      <View style={styles.importChip}>
                        <MaterialCommunityIcons name="briefcase-outline" size={10} color={C.mid} />
                        <Text style={styles.importChipText}>{row.jobTitle}</Text>
                      </View>
                    ) : null}
                    {row.niNumber ? (
                      <View style={styles.importChip}>
                        <MaterialCommunityIcons name="shield-account-outline" size={10} color={C.mid} />
                        <Text style={styles.importChipText}>{row.niNumber}</Text>
                      </View>
                    ) : null}
                    {row.startDate ? (
                      <View style={styles.importChip}>
                        <MaterialCommunityIcons name="calendar-start" size={10} color={C.mid} />
                        <Text style={styles.importChipText}>{row.startDate}</Text>
                      </View>
                    ) : null}
                  </View>

                  {hasIssue && (
                    <Text style={{ fontSize: 10, color: C.warning, marginTop: 4 }}>⚠ Row may be missing name fields — review before importing</Text>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {/* Bottom action */}
        <View style={styles.modalActions}>
          <TouchableOpacity style={[styles.modalActionBtn, { backgroundColor: C.bg, borderWidth: 1, borderColor: C.border }]} onPress={() => { setShowImportModal(false); setImportData([]); setSelectedImportRows([]); }}>
            <Text style={[styles.modalActionText, { color: C.mid }]}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.modalActionBtn, { flex: 2, backgroundColor: selectedImportRows.length === 0 ? C.light : C.primary }]}
            onPress={confirmImport}
            disabled={selectedImportRows.length === 0}
          >
            <MaterialCommunityIcons name="account-multiple-plus" size={18} color="#fff" />
            <Text style={[styles.modalActionText, { color: '#fff' }]}>
              Import {selectedImportRows.length} Employee{selectedImportRows.length !== 1 ? 's' : ''}
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  // ── Main Render ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={C.primary} />
        <Text style={styles.loaderText}>Loading payroll...</Text>
      </View>
    );
  }


  // ── Password Gate ──
  if (screenPasswordChecked && screenHasPassword && !screenPasswordVerified) {
    const isLocked = screenLockoutUntil && Date.now() < screenLockoutUntil;
    const lockMins = isLocked ? Math.ceil((screenLockoutUntil - Date.now()) / 60000) : 0;
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }} edges={['top']}>
        <StatusBar barStyle="dark-content" />
        <LinearGradient colors={['#0e4e96', '#1e6bc2']} style={{ paddingVertical: 24, paddingHorizontal: 20, alignItems: 'center' }}>
          <TouchableOpacity onPress={() => navigation?.goBack?.()} style={{ position: 'absolute', left: 16, top: 24 }}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
            <Ionicons name="lock-closed" size={30} color="#fff" />
          </View>
          <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#fff' }}>Password Required</Text>
          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>Payroll Management is protected by admin password</Text>
        </LinearGradient>
        <View style={{ padding: 24 }}>
          {isLocked ? (
            <View style={{ alignItems: 'center', paddingVertical: 30 }}>
              <Ionicons name="time-outline" size={48} color="#EF4444" />
              <Text style={{ fontSize: 17, fontWeight: 'bold', color: '#EF4444', marginTop: 12 }}>Locked</Text>
              <Text style={{ fontSize: 14, color: '#64748B', marginTop: 6 }}>Try again in {lockMins} min{lockMins > 1 ? 's' : ''}.</Text>
              <TouchableOpacity onPress={() => navigation?.goBack?.()}
                style={{ marginTop: 20, backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 13, paddingHorizontal: 28 }}>
                <Text style={{ color: '#fff', fontWeight: '600' }}>Go Back</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: screenPasswordError ? '#EF4444' : '#E2E8F0', paddingHorizontal: 14, marginBottom: 12 }}>
                <Ionicons name="lock-closed" size={18} color={screenPasswordError ? '#EF4444' : '#94A3B8'} />
                <TextInput
                  style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 10, fontSize: 16, color: '#222' }}
                  placeholder="Enter admin password"
                  placeholderTextColor="#94A3B8"
                  value={screenPassword}
                  onChangeText={(t) => { setScreenPassword(t); setScreenPasswordError(''); }}
                  secureTextEntry={!showScreenPassword}
                  autoFocus
                  onSubmitEditing={verifyScreenPassword}
                  returnKeyType="go"
                />
                <TouchableOpacity onPress={() => setShowScreenPassword(v => !v)}>
                  <Ionicons name={showScreenPassword ? 'eye-off' : 'eye'} size={20} color="#94A3B8" />
                </TouchableOpacity>
              </View>
              {screenPasswordError ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12, backgroundColor: '#FEF2F2', padding: 10, borderRadius: 10 }}>
                  <Ionicons name="alert-circle-outline" size={16} color="#EF4444" />
                  <Text style={{ fontSize: 12, color: '#EF4444', flex: 1 }}>{screenPasswordError}</Text>
                </View>
              ) : null}
              {screenPasswordAttempts > 0 && (
                <Text style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginBottom: 10 }}>
                  {SCREEN_MAX_ATTEMPTS - screenPasswordAttempts} attempt{SCREEN_MAX_ATTEMPTS - screenPasswordAttempts > 1 ? 's' : ''} remaining
                </Text>
              )}
              <TouchableOpacity onPress={verifyScreenPassword}
                style={{ backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 15, alignItems: 'center', marginBottom: 10 }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>Unlock</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => navigation?.goBack?.()}
                style={{ borderRadius: 12, paddingVertical: 13, alignItems: 'center', borderWidth: 1, borderColor: '#E2E8F0' }}>
                <Text style={{ color: '#64748B', fontWeight: '600' }}>Go Back</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </SafeAreaView>
    );
  }

  // ── Auth Gate ──
  if (authChecked && !isAdminUser) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }} edges={['top']}>
        <StatusBar barStyle="dark-content" />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 30 }}>
          <View style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: '#EF444420', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <Ionicons name="lock-closed" size={40} color="#EF4444" />
          </View>
          <Text style={{ fontSize: 22, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 8 }}>Access Restricted</Text>
          <Text style={{ fontSize: 15, color: '#64748B', textAlign: 'center', lineHeight: 22, marginBottom: 20 }}>
            Payroll Management is restricted to administrators, HR managers, and business owners.
          </Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity onPress={() => navigation?.goBack?.()}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8 }}>
              <Ionicons name="arrow-back" size={18} color="#fff" />
              <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Go Back</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { setAuthChecked(false); checkAdminStatus(); }}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0F4FF', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}>
              <Ionicons name="refresh" size={18} color="#0e4e96" />
              <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 15 }}>Retry</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <Animated.View style={[{ flex: 1 }, { opacity: fadeAnim }]}>
      <SafeAreaView style={styles.container} edges={['top']}>
        {renderHeader()}
        {renderTabs()}

        {activeTab === 'employees' && renderEmployeesTab()}
        {activeTab === 'payruns' && renderPayRunsTab()}
        {activeTab === 'payslips' && renderPayslipsTab()}
        {activeTab === 'reports' && renderReportsTab()}
        {activeTab === 'analytics' && renderAnalyticsTab()}
        {activeTab === 'pension' && renderPensionTab()}
        {activeTab === 'compliance' && renderComplianceTab()}
        {activeTab === 'audit' && renderAuditTab()}

        {/* FAB */}
        <TouchableOpacity style={styles.fab} onPress={() => {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
          if (activeTab === 'employees') { AsyncStorage.getItem('company_id').catch(() => null).then(cid => {
                  const cbKey = `payroll_save_${Date.now()}`;
                  registerCallback(cbKey, (newEmp) => {
                    const payrollEmp = {
                      ...newEmp,
                      payroll: {
                        salary: newEmp.salary || 0,
                        payFrequency: 'monthly',
                        payMethod: 'BACS',
                        paymentDate: 28,
                        pension: { enrolled: true, employeeContribution: 5, employerContribution: 3 },
                      },
                      taxCode: newEmp.taxCode || '1257L',
                      niCategory: 'A',
                    };
                    setEmployees(prev => [payrollEmp, ...prev]);
                  });
                  navigation.navigate('AddEmployeeScreen', {
                    companyId: cid,
                    existingCount: employees.length,
                    callbackKey: cbKey,
                  });
                }); }
          else if (activeTab === 'payruns') { setPayRunForm({ startDate: todayStr(), endDate: todayStr(), paymentDate: todayStr(), frequency: 'monthly' }); setShowPayRunForm(true); }
          else if (activeTab === 'reports') setShowReportGenerator(true);
          else if (activeTab === 'analytics') setShowTaxCalculator(true);
        }}>
          <LinearGradient colors={C.primaryGradient} style={styles.fabGrad}>
            <Ionicons name="add" size={26} color="#fff" />
          </LinearGradient>
        </TouchableOpacity>

        {/* All Modals */}
        {renderEmployeeDetailModal()}
        {renderEmployeeFormModal()}
        {renderPayRunDetailModal()}
        {renderPayRunFormModal()}
        {renderPayslipDetailModal()}
        {renderTaxCalculatorModal()}
        {renderReportGeneratorModal()}
        {renderSettingsModal()}
        {renderImportModal()}
      </SafeAreaView>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: C.bg },
  loaderText: { marginTop: 16, fontSize: FS_LG, fontWeight: '600', color: C.dark },

  // Header
  header: { paddingTop: 12, paddingBottom: 16, paddingHorizontal: HPAD },
  headerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  headerBtn: { width: ICON_BTN, height: ICON_BTN, borderRadius: ICON_BTN / 2, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerTitleContainer: { flex: 1, marginHorizontal: 12 },
  headerTitle: { fontSize: FS_XL, fontWeight: '700', color: '#fff', letterSpacing: -0.5 },
  headerSubRow: { flexDirection: 'row', alignItems: 'center', marginTop: 2 },
  headerSub: { fontSize: FS_SM, color: 'rgba(255,255,255,0.8)', marginRight: 8 },
  syncBadge: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 12 },
  syncBadgeText: { fontSize: 10, fontWeight: '600', marginLeft: 4 },
  headerActions: { flexDirection: 'row', gap: 8 },
  statsBar: { flexDirection: 'row', justifyContent: 'space-around', marginBottom: 8 },
  statItem: { alignItems: 'center', padding: 8 },
  statValue: { fontSize: FS_MD, fontWeight: '700', color: '#fff', marginTop: 2 },
  statLabel: { fontSize: FS_XS, color: 'rgba(255,255,255,0.7)', marginTop: 2 },
  searchBar: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: CARD_R, paddingHorizontal: 14, paddingVertical: Platform.OS === 'ios' ? 12 : 8, marginTop: 8, gap: 10 },
  searchInput: { flex: 1, fontSize: FS_BASE, color: C.dark, padding: 0 },

  // Tabs
  tabsContainer: { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border },
  tabsScroll: { paddingHorizontal: 8, paddingVertical: 4 },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 2, borderBottomColor: 'transparent', marginHorizontal: 2 },
  tabActive: { borderBottomColor: C.primary },
  tabText: { fontSize: FS_SM, fontWeight: '500', color: C.mid },
  tabTextActive: { color: C.primary, fontWeight: '700' },
  tabBadge: { backgroundColor: C.bg, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 12, minWidth: 22, alignItems: 'center' },
  tabBadgeActive: { backgroundColor: `${C.primary}18` },
  tabBadgeText: { fontSize: 10, fontWeight: '600', color: C.mid },

  // Action Bar
  actionBar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: HPAD, paddingVertical: 12, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border, gap: 8 },
  createBtn: { flex: 1, borderRadius: CARD_R, overflow: 'hidden' },
  createBtnGrad: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 10 },
  createBtnText: { fontSize: FS_BASE, fontWeight: '600', color: '#fff' },
  importBtn: { width: ICON_BTN, height: ICON_BTN, borderRadius: ICON_BTN / 2, backgroundColor: `${C.primary}12`, justifyContent: 'center', alignItems: 'center' },
  exportBtn: { width: ICON_BTN, height: ICON_BTN, borderRadius: ICON_BTN / 2, backgroundColor: `${C.primary}12`, justifyContent: 'center', alignItems: 'center' },
  filterChip: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10, backgroundColor: C.bg },
  filterChipActive: { backgroundColor: C.primary + '15' },
  filterChipText: { fontSize: 10, color: C.mid, fontWeight: '500' },

  // Results Bar
  resultsBar: { paddingHorizontal: HPAD, paddingVertical: 10, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  resultsCount: { fontSize: FS_SM, fontWeight: '600', color: C.mid },

  // List
  listContent: { paddingHorizontal: HPAD, paddingTop: 12, paddingBottom: 100 },

  // Employee Card
  employeeCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: C.border, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 },
  employeeAvatar: { width: AVATAR_SM, height: AVATAR_SM, borderRadius: AVATAR_SM / 2, backgroundColor: `${C.primary}20`, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  employeeAvatarText: { fontSize: FS_MD, fontWeight: '700', color: C.primary },
  employeeInfo: { flex: 1 },
  employeeHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  employeeName: { flex: 1, fontSize: FS_MD, fontWeight: '600', color: C.dark },
  employeeStatus: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 12 },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: { fontSize: 9, fontWeight: '600' },
  employeeNumber: { fontSize: FS_XS, color: C.light, marginBottom: 4 },
  employeeDetails: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
  employeeDetail: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  employeeDetailText: { fontSize: FS_XS, color: C.mid },
  employeeFooter: { flexDirection: 'row', gap: 12 },
  employeeFooterItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  employeeFooterText: { fontSize: 9, color: C.light },

  // Pay Run Card
  payRunCard: { backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: C.border, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 },
  payRunHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  payRunPeriod: { fontSize: FS_MD, fontWeight: '600', color: C.dark },
  payRunRef: { fontSize: FS_XS, color: C.light, marginTop: 2 },
  payRunStatus: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 },
  payRunStatusText: { fontSize: 10, fontWeight: '600' },
  payRunStats: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 12 },
  payRunStat: { minWidth: 80 },
  payRunStatLabel: { fontSize: 9, color: C.light, marginBottom: 2 },
  payRunStatValue: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  payRunFooter: { flexDirection: 'row', gap: 12, paddingTop: 8, borderTopWidth: 1, borderTopColor: C.border },
  payRunFooterItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  payRunFooterText: { fontSize: FS_XS, color: C.light },
  payRunGridStats: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  prGridItem: { width: (W - HPAD * 2 - 10) / 2 - 5, backgroundColor: '#fff', borderRadius: CARD_R, padding: 12, borderLeftWidth: 3 },
  prGridLabel: { fontSize: FS_XS, color: C.light, marginBottom: 4 },
  prGridValue: { fontSize: FS_MD, fontWeight: '700' },

  // Payslip Card
  payslipCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: C.border, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 },
  payslipAvatar: { width: AVATAR_SM, height: AVATAR_SM, borderRadius: AVATAR_SM / 2, backgroundColor: C.purple + '20', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  payslipAvatarText: { fontSize: FS_MD, fontWeight: '700', color: C.purple },
  payslipInfo: { flex: 1 },
  payslipName: { fontSize: FS_MD, fontWeight: '600', color: C.dark },
  payslipAmount: { fontSize: FS_MD, fontWeight: '700', color: C.success },
  payslipPeriod: { fontSize: FS_XS, color: C.light, marginTop: 2 },
  payslipBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8 },
  payslipBadgeText: { fontSize: 9, fontWeight: '600' },

  // Empty State
  emptyState: { alignItems: 'center', paddingVertical: 48 },
  emptyText: { fontSize: FS_LG, fontWeight: '600', color: C.light, marginTop: 12 },
  emptySubText: { fontSize: FS_SM, color: C.light, marginTop: 6, textAlign: 'center' },
  emptyBtn: { marginTop: 16, paddingHorizontal: 24, paddingVertical: 10, backgroundColor: C.primary, borderRadius: CARD_R },
  emptyBtnText: { color: '#fff', fontWeight: '600' },

  // Reports
  sectionLabel: { fontSize: FS_SM, fontWeight: '700', color: C.mid, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, marginTop: 8 },
  reportGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  reportCard: { width: (W - HPAD * 2 - 12) / 2, backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, borderWidth: 1, borderColor: C.border },
  reportIcon: { width: 50, height: 50, borderRadius: 14, justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
  reportCardTitle: { fontSize: FS_SM, fontWeight: '700', color: C.dark, marginBottom: 4 },
  reportCardDesc: { fontSize: FS_XS, color: C.mid, marginBottom: 10, lineHeight: 16 },
  reportChip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, alignSelf: 'flex-start' },
  reportChipText: { fontSize: FS_XS, fontWeight: '600' },
  recentReports: { backgroundColor: '#fff', borderRadius: CARD_R, borderWidth: 1, borderColor: C.border, overflow: 'hidden' },
  recentReportItem: { flexDirection: 'row', alignItems: 'center', padding: 14, borderBottomWidth: 1, borderBottomColor: C.border },
  recentReportName: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  recentReportMeta: { fontSize: FS_XS, color: C.light, marginTop: 2 },

  // Analytics
  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 24 },
  kpiCard: { width: (W - HPAD * 2 - 10) / 2 - 5, backgroundColor: '#fff', borderRadius: CARD_R, padding: 12, borderWidth: 1, borderColor: C.border },
  kpiIcon: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 8 },
  kpiValue: { fontSize: FS_MD, fontWeight: '700', color: C.dark },
  kpiLabel: { fontSize: FS_XS, fontWeight: '600', color: C.mid, marginTop: 2 },
  kpiSub: { fontSize: 9, color: C.light, marginTop: 2 },
  analyticsCard: { backgroundColor: '#fff', borderRadius: CARD_R, padding: 16, borderWidth: 1, borderColor: C.border, marginBottom: 16 },
  headcountRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  headcountDot: { width: 10, height: 10, borderRadius: 5 },
  headcountLabel: { width: 70, fontSize: FS_SM, color: C.mid },
  headcountBar: { flex: 1, height: 8, backgroundColor: C.bg, borderRadius: 4, overflow: 'hidden' },
  headcountFill: { height: '100%', borderRadius: 4 },
  headcountCount: { width: 24, textAlign: 'right', fontSize: FS_SM, fontWeight: '600', color: C.dark },
  deptRow: { marginBottom: 14 },
  deptName: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  deptSalary: { fontSize: FS_SM, fontWeight: '600', color: C.primary },
  deptBarBg: { height: 6, backgroundColor: C.bg, borderRadius: 3, overflow: 'hidden', marginVertical: 4 },
  deptBarFill: { height: '100%', borderRadius: 3 },
  deptCount: { fontSize: FS_XS, color: C.light },

  // Pension
  pensionRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: C.border },
  pensionAvatar: { width: 36, height: 36, borderRadius: 18, backgroundColor: C.purple + '20', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  pensionAvatarText: { fontSize: FS_SM, fontWeight: '700', color: C.purple },
  pensionName: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  pensionScheme: { fontSize: FS_XS, color: C.light },
  pensionRate: { fontSize: FS_XS, fontWeight: '600', color: C.primary },
  pensionStatus: { fontSize: FS_XS, fontWeight: '600', marginTop: 2 },

  // Compliance
  complianceStatus: { borderRadius: CARD_R, padding: 24, alignItems: 'center', marginBottom: 16 },
  complianceTitle: { fontSize: FS_LG, fontWeight: '700', marginTop: 8 },
  complianceSub: { fontSize: FS_SM, color: C.mid, marginTop: 4 },
  issueCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 8, borderLeftWidth: 4, borderWidth: 1, borderColor: C.border },
  issueText: { flex: 1, fontSize: FS_SM, color: C.dark, lineHeight: 20 },
  rtiRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 8, borderWidth: 1, borderColor: C.border },
  rtiPeriod: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  rtiRef: { fontSize: FS_XS, color: C.light },
  rtiBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10 },
  rtiBadgeText: { fontSize: FS_XS, fontWeight: '600' },

  // Audit
  auditRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 8, borderWidth: 1, borderColor: C.border },
  auditIcon: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  auditAction: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  auditTarget: { fontSize: FS_XS, color: C.mid, marginTop: 2 },
  auditMeta: { fontSize: 9, color: C.light, marginTop: 4 },

  // Modals
  modalHeader: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: HPAD, paddingVertical: 16, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border },
  modalCloseBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: C.bg, justifyContent: 'center', alignItems: 'center' },
  modalTitle: { flex: 1, fontSize: FS_LG, fontWeight: '700', color: C.dark, textAlign: 'center' },
  modalEditBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: C.primary + '15', justifyContent: 'center', alignItems: 'center' },
  modalSaveBtn: { paddingHorizontal: 16, paddingVertical: 8, backgroundColor: C.primary, borderRadius: 12 },
  modalSaveBtnText: { color: '#fff', fontWeight: '700', fontSize: FS_SM },
  modalIconBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: C.primary + '15', justifyContent: 'center', alignItems: 'center' },
  modalActions: { flexDirection: 'row', gap: 10, padding: HPAD, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: C.border },
  modalActionBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12, borderRadius: CARD_R },
  modalActionText: { fontWeight: '700', fontSize: FS_SM },

  // Employee Detail Hero
  empDetailHero: { padding: 24, alignItems: 'center', marginHorizontal: HPAD, marginVertical: 12, borderRadius: CARD_R },
  empDetailAvatar: { width: 70, height: 70, borderRadius: 35, backgroundColor: 'rgba(255,255,255,0.25)', justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
  empDetailAvatarText: { fontSize: 24, fontWeight: '700', color: '#fff' },
  empDetailName: { fontSize: FS_XL, fontWeight: '700', color: '#fff' },
  empDetailRole: { fontSize: FS_SM, color: 'rgba(255,255,255,0.8)', marginTop: 4 },
  empDetailBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },

  // Sub tabs
  subTab: { paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  subTabActive: { borderBottomColor: C.primary },
  subTabText: { fontSize: FS_XS, fontWeight: '500', color: C.mid },
  subTabTextActive: { color: C.primary, fontWeight: '700' },

  // Detail Groups
  detailGroup: { backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: C.border },
  detailGroupTitle: { fontSize: FS_SM, fontWeight: '700', color: C.primary, marginBottom: 12, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: C.border },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: C.border + '80' },
  detailLabel: { fontSize: FS_SM, color: C.mid, flex: 1 },
  detailValue: { fontSize: FS_SM, fontWeight: '500', color: C.dark, flex: 1.2, textAlign: 'right' },
  emergencyCard: { backgroundColor: C.bg, borderRadius: CARD_R, padding: 12, marginBottom: 8 },
  emergencyName: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  emergencyPhone: { fontSize: FS_XS, color: C.mid },
  emergencyEmail: { fontSize: FS_XS, color: C.mid },
  emptyInline: { fontSize: FS_SM, color: C.light, textAlign: 'center', paddingVertical: 16 },
  uploadBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 14, borderWidth: 1.5, borderColor: C.primary, borderRadius: CARD_R, borderStyle: 'dashed', marginTop: 12 },
  dedCard: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: C.border + '80' },
  dedName: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  dedType: { fontSize: FS_XS, color: C.light, marginTop: 2 },
  dedAmount: { fontSize: FS_SM, fontWeight: '700', color: C.primary },

  // Forms
  formSection: { fontSize: FS_SM, fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 20, marginBottom: 12, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: C.border },
  formField: { marginBottom: 14 },
  formLabel: { fontSize: FS_XS, fontWeight: '600', color: C.mid, marginBottom: 6 },
  formInput: { backgroundColor: '#fff', borderRadius: CARD_R, borderWidth: 1.5, borderColor: C.border, paddingHorizontal: 14, paddingVertical: Platform.OS === 'ios' ? 12 : 10, fontSize: FS_BASE, color: C.dark },
  infoCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, backgroundColor: C.info + '12', borderRadius: CARD_R, padding: 14, borderWidth: 1, borderColor: C.info + '30' },
  infoText: { flex: 1, fontSize: FS_SM, color: C.infoDark, lineHeight: 20 },

  // Calculator
  calculatorCard: { backgroundColor: '#fff', borderRadius: CARD_R, padding: 16, borderWidth: 1, borderColor: C.border, marginBottom: 16 },
  calcLabel: { fontSize: FS_SM, fontWeight: '600', color: C.mid, marginBottom: 6, marginTop: 12 },
  calcInput: { backgroundColor: C.bg, borderRadius: CARD_R, borderWidth: 1.5, borderColor: C.border, paddingHorizontal: 14, paddingVertical: Platform.OS === 'ios' ? 12 : 10, fontSize: FS_MD, color: C.dark, fontWeight: '600' },
  planChip: { flex: 1, paddingVertical: 8, borderRadius: 10, backgroundColor: C.bg, alignItems: 'center', borderWidth: 1, borderColor: C.border },
  planChipActive: { backgroundColor: C.primary, borderColor: C.primary },
  planChipText: { fontSize: FS_XS, fontWeight: '600', color: C.mid },
  calcBtn: { borderRadius: CARD_R, overflow: 'hidden', marginTop: 16 },
  calcBtnGrad: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14 },
  calcBtnText: { fontSize: FS_MD, fontWeight: '700', color: '#fff' },
  calcResults: { backgroundColor: '#fff', borderRadius: CARD_R, padding: 16, borderWidth: 1, borderColor: C.border, marginBottom: 16 },
  calcResultsTitle: { fontSize: FS_LG, fontWeight: '700', color: C.dark, marginBottom: 12 },
  calcNote: { fontSize: 10, color: C.light, marginTop: 12, textAlign: 'center' },

  // Report Generator
  reportTypeItem: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, paddingHorizontal: 14, backgroundColor: '#fff', borderRadius: CARD_R, marginBottom: 8, borderWidth: 1.5, borderColor: C.border },
  reportTypeItemActive: { borderColor: C.primary, backgroundColor: C.primary + '08' },
  reportTypeRadio: { width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: C.border, alignItems: 'center', justifyContent: 'center' },
  reportTypeLabel: { fontSize: FS_SM, fontWeight: '600', color: C.dark },
  formatBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12, borderRadius: CARD_R, backgroundColor: C.bg, borderWidth: 1.5, borderColor: C.border, marginBottom: 12 },
  formatBtnActive: { backgroundColor: C.primary, borderColor: C.primary },
  formatBtnText: { fontSize: FS_XS, fontWeight: '700', color: C.mid },

  // Settings
  settingRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: C.border, backgroundColor: '#fff', paddingHorizontal: 14, marginBottom: 1 },
  settingLabel: { fontSize: FS_SM, color: C.dark, flex: 1 },
  settingValue: { fontSize: FS_SM, fontWeight: '600', color: C.mid },
  settingToggleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 14, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: C.border, marginBottom: 1 },
  settingActionRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 14, paddingHorizontal: 14, backgroundColor: '#fff', borderRadius: CARD_R, marginBottom: 8, borderWidth: 1, borderColor: C.border },
  settingActionText: { flex: 1, fontSize: FS_SM, fontWeight: '600', color: C.dark },

  // FAB
  // Import Preview
  importPreviewRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: '#fff', borderRadius: CARD_R, padding: 14, marginBottom: 8, borderWidth: 1.5, borderColor: C.border },
  importPreviewRowSelected: { borderColor: C.primary, backgroundColor: C.primary + '06' },
  importPreviewRowWarning: { borderColor: C.warning },
  importCheckbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: C.border, alignItems: 'center', justifyContent: 'center', marginTop: 2 },
  importCheckboxSelected: { backgroundColor: C.primary, borderColor: C.primary },
  importChip: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: C.bg, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6 },
  importChipText: { fontSize: 9, color: C.mid },

    fab: { position: 'absolute', right: HPAD, bottom: FAB_BOTTOM, borderRadius: FAB_SIZE / 2, shadowColor: C.primary, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 8 },
  fabGrad: { width: FAB_SIZE, height: FAB_SIZE, borderRadius: FAB_SIZE / 2, justifyContent: 'center', alignItems: 'center' },
});